import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { GetRequest } from 'components/common';
import styles from 'components/layout_and_quote_builder/_style.module.scss';
import ManufacturingLineCard from 'components/layout_and_quote_builder/ManufacturingLineCard.jsx';
import LayoutNavSectionCombinationListList from 'components/layout_and_quote_builder/LayoutNavSectionCombinationListList.jsx';
import { setInCache } from 'components/layout_and_quote_builder/utils/SolutionScoreCache.js';
import {
  fetchJsonCached, hasCachedResponse, getCachedResponse, setCachedResponse,
} from 'components/frank/utils/ManufacturingLineResponseCache.js';
import { pollSectionBestScores } from 'components/frank/utils/sectionBestScorePolling.js';
import { minimumOf, maximumOf } from 'components/utils/minMax';
import { appendManufacturingLinesSource, manufacturingLinesSource, OWN } from 'components/frank/utils/manufacturingLinesSource.js';
import ManageOwnMachinesButton from 'components/frank/ManageOwnMachinesButton.jsx';
import ManageOwnMaterialsButton from 'components/frank/ManageOwnMaterialsButton.jsx';
import MaterialForm from 'components/frank/MaterialForm.jsx';
import AddMachineFlow from 'components/frank/AddMachineFlow.jsx';
import UserUnitPriceCreateForm from 'components/quote_builder/UserUnitPriceCreateForm.jsx';
import { machineTabConfig, machineCount, hasMachineOfType, refreshCountsAndResolve } from 'components/frank/utils/machineSettings.js';
import { ProgressProvider, useProgress } from 'components/layout_and_quote_builder/utils/ProgressContext.jsx';
import { DebugPanel } from 'components/layout_and_quote_builder/DebugPopup.jsx';
// The listing's loading state is a modal, blocking popup -- a full-screen card with a short "Loading…" title and
// a determinate percentage bar while the manufacturing lines are scored. It reads more clearly than an inline
// bar. ListingProgressOverlay is Frank's own (it reuses the legacy overlay's styles but with the shorter title),
// so the legacy page's ProgressBarOverlay stays exactly as it is. The solve keeps Frank's own in-place skeleton
// (SolutionCardsSkeleton), whose single request has no percentage to show.
import ListingProgressOverlay from 'components/frank/ListingProgressOverlay.jsx';
import SolutionCardsSkeleton from 'components/frank/SolutionCardsSkeleton.jsx';
import SelectedSolutionSections from 'components/frank/SelectedSolutionSections.jsx';
// The shared de-select: it posts to the reset endpoint, clears the section server-side and tells the linked
// sections. A JS helper, not a Ruby service -- the same reuse the legacy cards get.
import resetSelectedListHandler from 'components/layout_and_quote_builder/quote_builder/resetSelectedListHandler.js';
import skeletonStyles from 'components/frank/_style.module.scss';
import sectionAnswerQueue from 'components/frank/utils/sectionAnswerQueue.js';
import {
  readSelectedManufacturingLineList,
  writeSelectedManufacturingLineList,
  clearSelectedManufacturingLineList,
  readSectionMachineTypes,
  readSectionDisabledFilters,
  readSectionAreaRatioPercent,
  readSectionMaterialUserPriceId,
  readSectionCartonGsm,
  writeSectionFilters,
} from 'components/frank/utils/manufacturingLineListUrlState.js';
import MaterialSelect from 'components/frank/MaterialSelect.jsx';
import { materialFitsSection } from 'components/frank/utils/materialCompatibility.js';
import { combineSolutionGroups } from 'components/frank/utils/solutionGroups.js';
import {
  manufacturingLineListsFromListing, manufacturingLineListOf, manufacturingLinesToScore, planOfferedByStructure,
  allManufacturingLinesOf, lineKey,
} from 'components/frank/utils/manufacturingLineLists.js';
import {
  starRatingForScore, Stars, columnVisibilityFor, partLabelsByMachineId, maxMachineDimensionOf,
} from 'components/frank/utils/manufacturingLineListRendering.jsx';
import StandardManufacturingLinesFallback from 'components/frank/StandardManufacturingLinesFallback.jsx';
import { isLinkedSection, linkedParentOf } from 'components/frank/utils/linkedNavSection.js';

// starRatingForScore and Stars are shared with the standard-lines fallback, so a card scores and stars the
// same way on both -- see components/frank/utils/manufacturingLineListRendering.

// How many manufacturing line lists the listing shows and how many solution groups the solutions grid shows,
// cheapest first -- SystemParam.frank_maximum_manufacturing_line_lists / frank_maximum_solution_groups, rendered by
// the page. No fallback here: a page that does not render them is broken, and cheapestCombinations says so.
const MAXIMUM_MANUFACTURING_LINE_LISTS = window.frank_maximum_manufacturing_line_lists;
const MAXIMUM_SOLUTION_GROUPS = window.frank_maximum_solution_groups;

// The area-ratio cap (a PERCENT: "110" == a printing bed up to 110% larger in area than the sheet is kept) that
// the /mcc Debug input starts on. Seeded from the server so it matches the applied default: the server reads
// the frank_machine_area_ratio_percent SystemParam (admin-tunable from the database, no deploy), which itself
// falls back to the filter's shipped default -- no second hardcoded value to drift. The literal fallback here
// is only for surfaces that do not set the global, e.g. JS tests.
const DEFAULT_AREA_RATIO_PERCENT = window.frank_machine_area_ratio_percent ?? 110;

// BEST FIRST, AND HOW MANY STARS -- the SORT (cheapest first) is the legacy listing's own sortDivs
// (LayoutNavSectionCombinationList), so a solution ranks identically on both pages. The STARS are Frank's own:
// the cheapest card is always 5 and the rest are PROPORTIONAL to it (best/score * 5), where the legacy stretches
// them across the on-screen range (best = 5, worst = 1). So the ordering matches the legacy page but the star
// counts no longer do -- deliberately, Frank being the calculator we carry forward.
//
// The stars are still NOT a property of one solution: a card needs the cheapest score on screen to know its own
// rating, so they can only be worked out once the scores have landed. This is done in the DOM rather than in
// React state for exactly that reason -- each card fetches its own score, and data-score appears on the grid
// item as each lands.
//
// LOWER IS BETTER: the score is a cost.
const sortSolutionsByScoreAndDrawStars = (grid) => {
  if (!grid) { return; }

  const items = Array.from(grid.children);
  if (items.length === 0) { return; }

  const scoreOf = (item) => parseInt(item.getAttribute('data-score'), 10);

  const sorted = [...items].sort((a, b) => scoreOf(a) - scoreOf(b));
  const alreadySorted = sorted.every((item, index) => item === items[index]);
  if (!alreadySorted) {
    sorted.forEach((item) => grid.appendChild(item));
  }

  // A score of 0 is "not answered yet", not "free" -- the legacy filters those out of the range, or an
  // unanswered card would clamp to a spurious 5.0 and sort to the front.
  const scores = items.map(scoreOf).filter((score) => !Number.isNaN(score) && score > 0);
  if (scores.length === 0) { return; }

  const bestScore = minimumOf(scores);
  const worstScore = maximumOf(scores);
  // Nothing to rank until the scores spread -- while only one has landed (or they are all equal) leave the
  // cards be, exactly as before, rather than flash a lone 5.0.
  if (worstScore - bestScore <= 0) { return; }

  items.forEach((item) => {
    const score = scoreOf(item);
    if (Number.isNaN(score) || score <= 0) { return; }

    // The cheapest card is 5 stars and every other is proportional to it -- a card that costs twice the best
    // earns half the stars. score <= 0 is skipped above, so this never divides by zero.
    const starRating = (bestScore / score) * 5;
    const rounded = Math.round(Math.max(1, Math.min(5, starRating)) * 10) / 10;

    let container = item.querySelector('.stars-rating-container');
    if (!container) {
      container = document.createElement('span');
      container.className = 'stars-rating-container';
      item.prepend(container);
    }

    const fullStars = Math.floor(rounded);
    const remainder = rounded - fullStars;
    const hasHalfStar = remainder >= 0.25 && remainder < 0.75;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    let starsHtml = '<span style="color: #ffc107; font-size: 14px; margin-left: 8px;">';
    starsHtml += '★'.repeat(fullStars);
    if (hasHalfStar) {
      starsHtml += '<span style="position: relative; display: inline-block;">'
        + '<span style="color: #ddd;">★</span>'
        + '<span style="position: absolute; left: 0; width: 50%; overflow: hidden; color: #ffc107;">★</span>'
        + '</span>';
    }
    starsHtml += '☆'.repeat(Math.max(0, emptyStars));
    starsHtml += ` <span style="margin-left: 4px; color: #666; font-size: 13px;">${rounded.toFixed(1)}</span></span>`;

    container.innerHTML = starsHtml;
  });
};

// The label each filter's toggle shows, by the key the server sends. Reuses the legacy listing's own label
// keys, so a filter reads the same on both pages.
const FILTER_LABEL_KEYS = {
  smallest_die_cutting_machine_of_each_type: 'smallest_die_cutting_filter',
};

// Each machine-type filter's row heading, and how to label one of its ticks. The printing and die-cutting rows
// reuse the legacy listing's own keys, so they read identically on both pages; the combined print-and-die-cut
// row is Frank's, because only Frank lists corrugated cardboard in flexo.
//
// WHICH rows arrive, and which types each offers, is the SERVER's answer (the section's part_type decides it --
// Frank::SubSectionManufacturingLines::MachineTypeFilters); this map only says how to LABEL one. A row whose key
// is not named here is skipped rather than drawn unlabelled.
const MACHINE_TYPE_FILTER_LABELS = {
  printing_and_die_cutting_machine_types: {
    headingKey: 'quote_builder.printing_and_die_cutting_machines',
    optionKey: (type) => `quote_builder.${type}`,
  },
  printing_machine_types: {
    headingKey: 'quote_builder.printing_machines',
    optionKey: (type) => `quote_builder.${type}`,
  },
  die_cutting_machine_types: {
    headingKey: 'quote_builder.die_cutting_machines',
    optionKey: (type) => `quote_builder.die_cutting_type_${type}`,
  },
};

// Everything the owner-machines flow needs to know about ONE machine-type row, keyed by the row's filter key
// (from listing.machine_type_filters, set server-side by Frank::SubSectionManufacturingLines::MachineTypeFilters):
// which own-machine key it manages, the add/manage button labels, and how to derive the picker's allowed types
// (see LibraryAttributePicker's allowedTypes / library_filter_property). Both the filter island's add/manage
// button AND the empty-state adder read this, so the two paths to add a machine offer the SAME constrained set.
//   - printing_machine_types  -> printing_machine, constrained to the row's own printing TYPES (offset/digital/..)
//   - die_cutting_machine_types -> die_cutting_machine, constrained to the section's MATERIAL GROUP (the server's
//     own die-cutting suitability, carried in the payload as die_cutting_material_group) -- NOT the row's machine
//     types, which are a different property the picker does not filter by.
// A row not named here imposes NO setup requirement (ownMachineMissing) and gets NO empty-state adder. The combined
// printing_and_die_cutting_machine_types row is deliberately one of them -- a corrugated-cardboard flexo section can
// be made on ONE combined machine, so it must not be gated on a separate printing + die-cutting machine (mirroring
// selectEmptyStateAdder and Frank::SectionSetupRequirements, which both skip it). It DOES get a "Manage" button,
// wired through MANAGE_BUTTON_ONLY_MACHINE_ROWS below so the button appears without the row entering the gate/adder.
const SECTION_MACHINE_ROWS = {
  printing_machine_types: {
    machineKey: 'printing_machine',
    addLabelKey: 'quote_builder.add_printing_machine',
    manageLabelKey: 'quote_builder.manage_printing_machines',
    fullLabelKey: 'quote_builder.manage_own_printing_machines',
    allowedTypesFor: (filter) => filter.options.map((option) => option.value),
  },
  die_cutting_machine_types: {
    machineKey: 'die_cutting_machine',
    addLabelKey: 'quote_builder.add_die_cutting_machine',
    manageLabelKey: 'quote_builder.manage_die_cutting_machines',
    fullLabelKey: 'quote_builder.manage_own_die_cutting_machines',
    allowedTypesFor: (_filter, listing) => (
      listing.die_cutting_material_group ? [listing.die_cutting_material_group] : null
    ),
  },
};

// Rows that get a "Manage" button but are kept OUT of SECTION_MACHINE_ROWS on purpose, so they impose no setup-gate
// requirement (ownMachineMissing) and no empty-state adder -- only the button reads this map (see renderSectionFilters).
// The combined print-and-die-cut row is the only one: a flexo section is made on ONE combined machine, so gating it on
// a separate printing + die-cutting machine would be wrong, yet the owner still needs to add and manage their own
// combined machines. Its picker filters on machine_type (settings.yml library_filter_property for
// printing_and_die_cutting_machine), whose values ARE the row's own options (rotary_and_flexo / flatbed_and_flexo).
const MANAGE_BUTTON_ONLY_MACHINE_ROWS = {
  printing_and_die_cutting_machine_types: {
    machineKey: 'printing_and_die_cutting_machine',
    addLabelKey: 'quote_builder.add_printing_and_die_cutting_machine',
    manageLabelKey: 'quote_builder.manage_printing_and_die_cutting_machines',
    fullLabelKey: 'quote_builder.manage_own_printing_and_die_cutting_machines',
    allowedTypesFor: (filter) => filter.options.map((option) => option.value),
  },
};

// A machine a FOUND own line uses that the owner has NOT PRICED for the type this section needs -- or null. A
// manufacturing line is FOUND from a machine RECORD (so ownMachineMissing is already false once a line exists), but
// it cannot be QUOTED until that machine has a UserUnitPrice -- without one the section shows a found-but-unquotable
// line ("Makine birim fiyat bulunamadi") instead of leading the owner to add the price, exactly as a missing
// material leads to EmptyStateMaterialAdder. Gated on the SPECIFIC found machine (issue #5887): the server stamps
// each found line with <machineKey>_priced (Frank::ManufacturingLine#printing_machine_priced? / #die_cutting_
// machine_priced?), so a found line naming an UNPRICED machine leads the owner to price THAT machine even when the
// owner has ANOTHER priced machine of the same type (whose priceable line would otherwise just sort first and hide
// it). The found line names the machine (its <machineKey>_id), so the price adder pre-selects it rather than auto-
// selecting the owner's first (possibly already-priced) machine of the key. Printing before die-cutting, matching
// the wizard's step order. The id is read from the FIRST found line that actually names an unpriced machine of that
// key (across every list), not just list[0]'s first line -- a die-cutting-only line carries no printing_machine_id,
// so scanning finds the one that does.
//
// A module-level pure function of the listing (null-safe) so both the render (ownUnpricedMachine) and the wizard-
// notification effect derive it from the SAME rule -- the effect must be an unconditional top-level hook, above this
// component's `!listing` / `!canSee` early returns, so it cannot read values computed after them.
const findOwnUnpricedMachine = (listing) => {
  if (!listing || manufacturingLinesSource() !== OWN) { return null; }
  // The flat candidate set (from the structure, or a legacy payload's pre-built lists) -- read below to name an
  // unpriced machine the found lines carry.
  const foundManufacturingLines = allManufacturingLinesOf(listing);
  if (foundManufacturingLines.length === 0) { return null; }
  return (listing.machine_type_filters || [])
    .map((filter) => ({ row: SECTION_MACHINE_ROWS[filter.key], filter }))
    .filter(({ row }) => row)
    .map(({ row, filter }) => ({ machineKey: row.machineKey, allowedTypes: row.allowedTypesFor(filter, listing) }))
    .filter(({ allowedTypes }) => allowedTypes !== null)
    .filter(({ machineKey, allowedTypes }) => hasMachineOfType(machineKey, allowedTypes))
    .map(({ machineKey }) => {
      const namingLine = foundManufacturingLines.find((line) => (
        line[`${machineKey}_id`] != null && line[`${machineKey}_priced`] === false
      ));
      return { machineKey, attributeId: namingLine ? namingLine[`${machineKey}_id`] : null };
    })
    .find(({ attributeId }) => attributeId != null) || null;
};

// Frank lists a section's SubSectionManufacturingLines on /mcc, below the "Chosen material dimensions": per
// sub_section, the manufacturing lines that can make it, rendered through the SAME cards the legacy page uses
// (ManufacturingLineCard) -- so a manufacturing line reads identically on both pages.
//
// It lists SUB_SECTION manufacturing lines, not ManufacturingLineLists: a ManufacturingLineList is one
// complete way to manufacture the WHOLE section, which for overlapping sub_sections (Cover, Tray, and the
// combined "Tray & Cover" all describing the same parts) is an exact-cover across them. That comes later.
//
// What Frank does not have yet, and so does not render:
//   - "Material dimensions:" chips -- a Frank ManufacturingLine carries its members, not the material sheets
//     (the sheets are the finder's fit input). The card drops that block on its own when the key is absent.
//   - "Priced solutions" -- no Solution Finder yet, so the cards are told there is no count to wait for
//     (hidePricedSolutionCount) rather than being left dimmed behind a spinner forever.
//   - selection -- this is a read-only verify view while Frank grows, so the cards are not clickable.
//
// Admin-gated like the material-sheets line above it.
// The solution this section is already priced from, if any -- read back from the sections the page was
// rendered with (user_quote_nav_sections[].selected_lists), so a refresh or a shared link reopens on the
// priced section rather than on the solution list. It is stored per section, which is why it is looked up by
// sort_order rather than by position.
const restoreSelectedSolutions = (navSectionIndex) => {
  const sections = window.user_quote_nav_sections || [];
  const section = sections.find((candidate) => candidate.sort_order === navSectionIndex);
  const stored = section && section.selected_lists;

  return stored && stored.length > 0 ? stored : null;
};

// IS A SOLUTION CHOSEN? Asked of the LENGTH, never of truthiness, because the reset path sets the selection to
// an EMPTY ARRAY rather than to null -- `resetSelectedListHandler` does `setSelectedList(() => [])`, and the
// legacy section's own Back button (rendered inside QuoteBuilderNavSection, which is handed this setter) goes
// through it. `[]` is truthy in JavaScript, so a truthiness check kept the priced view on screen with nothing
// in it: the reset had already reached the server, and the page showed one orphaned button.
const hasSelection = (selectedSolutions) => Array.isArray(selectedSolutions) && selectedSolutions.length > 0;

// What a LINKED section's listing is: nothing, and nothing pending. Distinct from null, which the page reads as
// "still loading" and waits on forever for a section that never fetches.
const NO_LISTING_OF_ITS_OWN = { manufacturing_line_lists: [], machine_type_filters: [], filters: [] };

// HOW LONG THE SECTION GOES ON ASKING FOR A SCORE THAT HAS NOT ARRIVED -- and it asks WITHOUT SAYING ANYTHING,
// because a score that is late is not something the reader has to know about. The section shows the progress it
// shows for any scoring pass, the card keeps its placeholder, and when the answer lands it simply lands. Nobody
// needs to be told that a worker was restarted.
//
// Ten minutes because of what it has to outlast. One poll loop is patient for POLL_DEADLINE_MILLISECONDS -- five
// minutes, the SAME as the server's claim on a piece of work (Frank::SectionAnswer::PROCESSING_TTL) -- so a loop
// waits a claim's whole life before reporting what it did not get, and never gives up on work still being computed.
// A job whose worker was killed still cannot be recovered inside that one loop: every ask in the window is answered
// 'processing' by a claim nobody is working on. So the round AFTER it drops the dead claim and queues the work
// again (refresh in askAboutLines), and ten minutes spans the claim with rounds to spare -- which is how scores
// lost to a killed worker come back with nothing on screen ever having said a word about it.
//
// Not unbounded, though. Work that fails the same way every time would be re-queued for ever on the very queue
// the rest of the page is waiting on, and a placeholder that never resolves is its own kind of lie. When the
// patience is spent the card says it could not be scored and offers to ask again -- a person spending that work,
// rather than the page spending it in a loop nobody is watching.
//
// Overridable per page (window.frank_scoring_patience_milliseconds) so the exhausted state can be reached in a
// test and looked at in development without waiting ten minutes for it.
const SCORING_PATIENCE_MILLISECONDS = 600000;
// The pause between rounds, and how it grows. It starts short -- a restarting dyno is back in seconds and the
// scores should follow it straight away -- and grows towards a minute, so a section nothing is answering settles
// into asking once a minute rather than hammering the queue for the whole ten.
const FIRST_BETWEEN_ROUNDS_MILLISECONDS = 2000;
const MAXIMUM_BETWEEN_ROUNDS_MILLISECONDS = 60000;
const BETWEEN_ROUNDS_GROWTH = 3;

// The longest the Solution grid is held hidden while it ranks (see #solutionGridRanked / renderSolutions) before
// it is revealed regardless. Ranking normally settles within a second of the solve landing, but a card whose
// score never arrives would otherwise trap the grid off screen forever -- this floor reveals it either way, so
// the worst case is only what the grid did before this change (shown mid-rank), never a grid that never shows.
const SOLUTION_GRID_REVEAL_FLOOR_MILLISECONDS = 4000;

// HOW LONG THE SECTION KEEPS ASKING FOR ITS OWN LISTING before it reports that it could not be worked out. The
// scores have always recovered this way (the rounds above); the listing was the one ask that did not, so a
// single dropped or refused request -- a restarting dyno, a Wi-Fi blip, a worker that was not up yet -- was
// reported as a finding about the section, and the reader had to press Try again to do what the page could have
// done itself.
//
// A MINUTE, not the scores' ten, because the listing is what the whole stage waits on: the scores fill in behind
// cards that can already be read, while a section with no listing has nothing to show at all. Four asks fit in
// that minute (the rounds pause 2s, 6s, 18s, 54s), which spans a dyno coming back; past it the reader is better
// served by being told, with Try again to spend another minute on it.
//
// It bounds when the section stops STARTING asks, not how long one takes: a single ask polls a computing answer
// for up to POLL_DEADLINE_MILLISECONDS (five minutes) on its own, and the round after that one is the ask that
// can queue the work afresh (see the same note on SCORING_PATIENCE_MILLISECONDS).
//
// Overridable per page, like the scoring patience and for the same reason: so a test can reach the reported
// state without waiting a minute for it.
const LISTING_PATIENCE_MILLISECONDS = 60000;

// THE BLINK the results give as a search lands: half strength, held, then straight back. No transition on
// either edge -- an eased fade over a large block of cards is easy to miss entirely, and what has to register
// is that something changed, not a graceful change.
const RESULTS_BLINK_MILLISECONDS = 250;
const RESULTS_BLINK_OPACITY = 0.5;

// The card flex-basis the wizard uses -- 3 across (33.333%) instead of the page's 4 (25%), because the modal
// is narrower than the full page and four line/solution cards (each two sub-cards wide) come out cramped.
const WIZARD_CARD_BASIS = 'calc(33.333% - 16px)';
const PAGE_CARD_BASIS = 'calc(25% - 16px)';

// The width the own-setup empty-state adders (the add-material list / form, the add-machine picker) are capped at
// and centred within. The filter island is hidden while these show (see the main render), so otherwise they take
// the full modal / page width -- far too wide for a form or a short list of materials.
const SETUP_ADDER_MAX_WIDTH = '680px';

// How the scroll-to-the-selection below decides a card has finished growing: three frames at the same height.
// SETTLE_FRAME_LIMIT bounds the wait in frames rather than by a clock, so a card that never settles (an image
// that never loads) still lands instead of waiting for ever.
const SETTLED_FRAMES = 3;
const SETTLE_FRAME_LIMIT = 90;
// How long that watch will wait for the list to finish arriving and ordering itself before giving up on the
// glide -- about ten seconds, generous for a slow solve and short of waiting for ever.
const LIST_READY_FRAME_LIMIT = 600;
// The beat between the card settling and the page moving -- one full tick of the 200ms re-ordering pass with a
// little margin, so the glide goes to where the card ends up rather than where it was. Kept as short as that
// allows: a longer pause reads as the page hesitating.
const GLIDE_DELAY_MS = 250;

// What counts as the reader moving the page themselves, while that watch is waiting. Their INPUT, never a
// scroll position -- see #glideWhenSettled.
const READER_INPUT_EVENTS = ['wheel', 'touchmove', 'keydown'];
const READER_SCROLL_KEYS = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '];

// How many cards a full row holds: the wizard's modal is narrower and gives a card a third of the row, the page
// a quarter. The pair decides when a row is full, and so when there is spare width for the cards to be centred
// in -- change a width without its capacity and one or two cards start centring in a row they actually fill.
//
// Kept next to the bases above, but note the page's SOLUTION card width is NOT one of them: it comes from
// .combinations_grid_item_cont in the shared layout_and_quote_builder stylesheet (also 25%), because
// columnBasis is only passed in the wizard. A change there has to be reflected here too.
const WIZARD_CARDS_PER_ROW = 3;
const PAGE_CARDS_PER_ROW = 4;

// The progress of the section's SEARCH, shown INLINE in the wizard's line stage -- so the wizard shows a bar
// rather than a page of blank-scored cards, and the cards appear only once every manufacturing line has
// answered. On the normal page the full-screen ListingProgressOverlay does this instead; inside the wizard that
// overlay would sit behind the modal, so this reads the SAME useProgress() data and renders in-flow. Rendered
// inside ManufacturingLineLists' ProgressProvider, so useProgress() is available.
//
// `title` IS WHICH HALF OF THE SEARCH IS RUNNING, and the caller says which, because the two are not the same
// wait. The section's manufacturing lines are FOUND first -- one request, nothing to count, so the bar sweeps --
// and only then are they SCORED, one answer per manufacturing line, which is what the percentage counts. A
// single title for both told the viewer their manufacturing lines were being scored while the section had not
// even been listed yet.
// onCancel stops the very pass this bar is reporting, so the button belongs UNDER the bar rather than up by the
// filters: what is being cancelled is what you are watching.
const WizardSearchProgress = ({ title, onCancel }) => {
  const { progress } = useProgress();
  // Until the first manufacturing line's best score lands, `completed` is 0 -- a "0%" bar frozen for the whole
  // (cold) first best-score request reads as broken, so sweep indeterminately and hide the percentage until
  // results land. The finding half has no units at all (total 0), so it sweeps for its whole length.
  const started = progress.total > 0 && progress.completed > 0;
  const percentage = started ? progress.percentage : 0;

  return (
    <div style={{ padding: '48px 20px', textAlign: 'center' }}>
      <div style={{ fontSize: '14px', color: 'var(--ink-soft, #4e5865)', marginBottom: '14px' }}>
        {title}
      </div>
      <div style={{ maxWidth: '360px', margin: '0 auto', height: '8px', background: 'var(--surface-muted, #f1f3f5)', borderRadius: '4px', overflow: 'hidden', position: 'relative' }}>
        {started
          ? <div style={{ height: '100%', width: `${percentage}%`, background: 'var(--brand, #eb5424)', transition: 'width 200ms ease' }} />
          : <div className={skeletonStyles.indeterminateFill} />}
      </div>
      {started && <div style={{ fontSize: '12px', color: 'var(--ink-faint, #8a94a3)', marginTop: '8px' }}>{percentage}%</div>}
      {onCancel && (
        <div style={{ marginTop: '16px' }}>
          <button
            type="button"
            data-cancel-search
            onClick={onCancel}
            className="dct-button dct-button--secondary dct-button--small"
          >
            {t('quote_builder.cancel')}
          </button>
        </div>
      )}
    </div>
  );
};

// Shown IN the empty manufacturing-line listing when the owner is on their OWN lines and is MISSING a machine of
// some type (see renderListing / selectEmptyStateAdder): a manufacturing line needs a printing AND a die-cutting
// machine, so without one the quote can find no manufacturing line, and the add-machine flow fills the empty
// area -- in place of the "no lines match" finding (the filter island is hidden while the owner is setting up,
// see the main render). machineKey names the type ('printing_machine' / 'die_cutting_machine'), constrained to
// allowedLibraryTypes (the section's usable types for this machine).
//
// TWO STEPS, INLINE, mirroring EmptyStateMaterialAdder above: pick a machine from the library, then its price
// form REPLACES the picker in place (AddMachineFlow) -- no popup. RE-SOLVE ON PRICE SAVE, not on the copy: the
// copy alone leaves the machine UNPRICEABLE, and re-solving then would remount the section and tear this flow
// down before the price is entered (the bug this fixed). So the copy is silent, and the one re-solve fires when
// the PRICE is saved (onPriced) -- exactly as adding a material does -- remounting the section to list with the
// now-priceable machine, or to offer the next missing machine row. A safety net re-solves on unmount if a
// machine was copied but the flow ended before its price was saved.
const EmptyStateMachineAdder = ({ machineKey, allowedLibraryTypes }) => {
  const copiedRef = useRef(false);

  // A price was saved -- re-solve now (like a material save); the flow is done. refreshCountsAndResolve pulls the
  // owner's own counts fresh (OWN-only, so the gate agrees with the page seed even for an admin) and announces
  // frank:own-machines-changed, exactly as the wizard's setup steps do -- one refresh path, no drift.
  const onPriced = () => { copiedRef.current = false; refreshCountsAndResolve(); };

  // A machine was copied (it now exists in the account) -- NOTE it so that if the flow ends WITHOUT its price
  // being saved, the section is still re-solved for it on unmount rather than keeping the pre-copy empty result.
  const onCopied = () => { copiedRef.current = true; };
  useEffect(() => () => {
    if (copiedRef.current) { window.dispatchEvent(new CustomEvent('frank:own-machines-changed')); }
  }, []);

  // Framed as its OWN island -- the same card (surface + 1px rule + rounded + slight shadow), capped at a readable
  // width and centred. The filter island is hidden while this shows (see the main render), so there is nothing to
  // match its width to any more; a fixed cap keeps a picker from stretching the full modal / page width.
  return (
    <div
      style={{
        width: '100%', maxWidth: SETUP_ADDER_MAX_WIDTH, margin: '0 auto',
        textAlign: 'left', padding: '16px 20px',
        background: 'var(--surface, #ffffff)', border: '1px solid var(--rule, #e3e7ec)',
        borderRadius: 'var(--dct-radius, 8px)', boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)'
      }}
    >
      <AddMachineFlow
        machineKey={machineKey}
        tabConfig={machineTabConfig(machineKey)}
        allowedTypes={allowedLibraryTypes}
        onCopied={onCopied}
        onPriced={onPriced}
        onClose={() => {}}
      />
    </div>
  );
};

// The FIRST thing a section's own manufacturing line needs: a material price. A line is found on standard boards
// but cannot be PRICED without the owner's material unit price, so when the owner is on their own lines with none
// USABLE for this section, the empty area shows a NEW-material adder (MaterialForm with hideOwnPricesList) -- ahead
// of the machine adders. This is strictly "add a new material": the owner's OWN price list is dropped (hidden),
// because it is not the point here and can mislead -- the reason the line is unpriced is often that their only
// material is a bobbin the line cannot use, so listing it (to edit/copy/delete) invites the wrong action. What
// stays is the two ways to ADD one: the "Sık kullanılanlar" (frequently-used) system materials, each offering
// "Seç ve fiyat ekle" (copy-from-system -> the owner just enters their price), and the "Yeni birim fiyat ekle"
// button for a manual entry of anything not in that list. Unlike a machine (a library picker), a
// material is a UserUnitPrice, so this has no autoStartNew/allowedTypes; and its count lives under `counts` (not
// `attribute_counts`). Saving a material re-solves the section (fresh fingerprint + remount) so the now-priceable
// listing / the next missing step appears -- one add is enough, so it re-solves on save rather than deferring to
// a close.
const EmptyStateMaterialAdder = ({ materialKey, defaultBobbinEnabled, boardThicknessMm, corrugatedCardboardFlute }) => {
  // Saving a material re-solves the section via the same OWN-scoped refresh the wizard and the machine adder use
  // (refreshCountsAndResolve): it pulls the owner's own counts fresh -- so the gate agrees with the page seed even
  // for an admin -- and announces frank:own-machines-changed to remount the now-priceable listing / next step.
  const afterSave = () => { refreshCountsAndResolve(); };

  // Capped at a readable width and centred (the same card frame as the machine adder). The filter island is
  // hidden while this shows, so there is nothing to match a width to; a fixed cap keeps the list / form from
  // stretching the full modal / page width.
  return (
    <div
      style={{
        width: '100%', maxWidth: SETUP_ADDER_MAX_WIDTH, margin: '0 auto',
        textAlign: 'left', padding: '16px 20px',
        background: 'var(--surface, #ffffff)', border: '1px solid var(--rule, #e3e7ec)',
        borderRadius: 'var(--dct-radius, 8px)', boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)'
      }}
    >
      <MaterialForm materialKey={materialKey} systemItemsFirst hideOwnPricesList afterSave={afterSave} defaultBobbinEnabled={defaultBobbinEnabled} boardThicknessMm={boardThicknessMm} corrugatedCardboardFlute={corrugatedCardboardFlute} />
    </div>
  );
};

// Shown when the owner is on their OWN lines and a manufacturing line WAS found, but the machine that line uses has
// no unit price for the type this section needs (see ownUnpricedMachine / renderListing) -- so the line is found
// yet cannot be quoted ("Makine birim fiyat bulunamadi"). Unlike EmptyStateMachineAdder, which copies a NEW machine
// from the library and then prices it, this prices the machine the owner ALREADY has: UserUnitPriceCreateForm opens
// a new price pre-pointed at attributeId -- the exact machine the found line uses (its <machineKey>_id) -- so the
// owner just fills in the rate, and the price is never attached to some other, already-priced machine of the same
// key. SAVE re-solves (refreshCountsAndResolve) so the now-quotable listing comes back. CANCEL, by contrast, does
// NOT re-solve: the section is still unpriced, so it would only rebuild to this same adder at the cost of a full
// section remount -- and QuoteBuilderForm's own cancel drops to its price LIST first, which would flash the owner's
// (wrong-group) records inside the box. Instead cancel just REMOUNTS this form fresh via a bumped key, throwing away
// what was typed and landing straight back on the empty "new" price form -- no section re-solve, no list flash.
// In the WIZARD (hideButtons + submitRef given) this is the SAME footer-integrated price form the wizard's own
// machine_price step uses (SetupStepForms MachinePriceStep) -- one Save, the wizard's Next, not a second pair of
// buttons over the wizard footer. On the section tab (no wizard props) the form keeps its OWN Save (re-solves) and
// Cancel (resets in place). Either way it pre-points at attributeId, the exact machine the found line uses.
const EmptyStateMachinePriceAdder = ({ machineKey, attributeId, hideButtons, submitRef, afterSave, onSaveError }) => {
  const [formInstance, setFormInstance] = useState(0);
  // Section tab default: re-solve so the now-quotable listing comes back. In the wizard the caller passes its own
  // resolve (which also clears the footer's Next spinner and recomputes the steps), so use that.
  const onSave = afterSave || (() => { refreshCountsAndResolve(); });
  const resetForm = () => { setFormInstance((instance) => instance + 1); };

  return (
    <div
      style={{
        width: '100%', maxWidth: SETUP_ADDER_MAX_WIDTH, margin: '0 auto',
        textAlign: 'left', padding: '16px 20px',
        background: 'var(--surface, #ffffff)', border: '1px solid var(--rule, #e3e7ec)',
        borderRadius: 'var(--dct-radius, 8px)', boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)'
      }}
    >
      <UserUnitPriceCreateForm
        key={formInstance}
        priceKey={machineKey}
        contextKey={machineKey}
        attributeId={attributeId}
        afterSave={onSave}
        afterCancel={hideButtons ? undefined : resetForm}
        hideButtons={hideButtons}
        submitRef={submitRef}
        onSaveError={onSaveError}
      />
    </div>
  );
};

// The adder to show for an empty listing on the owner's own lines, or null to keep the plain "no lines" finding.
//   1. A section that lists a COMBINED print-and-die-cut machine (corrugated flexo) is deferred -- a printing-only
//      or die-cutting-only picker cannot by itself yield its line -- so keep the finding.
//   2. Otherwise the FIRST supported row the owner has none of, in listing order (printing before die-cutting for
//      a carton; die-cutting alone for hard cardboard), whose allowed types are known (a die-cutting row with no
//      material group is not eligible -- never offer a picker that cannot help).
//   3. Legacy fallback: a payload with no supported machine-type rows at all, but the owner has no printing
//      machine, still offers the printing picker unconstrained (the shape older callers/tests send).
const selectEmptyStateAdder = (machineTypeFilters, listing) => {
  if (machineTypeFilters.some((filter) => filter.key === 'printing_and_die_cutting_machine_types')) { return null; }

  const unmet = machineTypeFilters
    .filter((filter) => SECTION_MACHINE_ROWS[filter.key])
    // Type-aware: the owner is missing this row when they have no machine of a type it ALLOWS -- a wrong-group
    // die-cutter (or wrong printing type) does not count, so the section is not left at a found-but-unquotable
    // listing. allowedTypesFor null (no eligible types) falls back to the plain "any machine of this key" check.
    .find((filter) => {
      const row = SECTION_MACHINE_ROWS[filter.key];
      return !hasMachineOfType(row.machineKey, row.allowedTypesFor(filter, listing));
    });
  if (unmet) {
    const row = SECTION_MACHINE_ROWS[unmet.key];
    const allowedTypes = row.allowedTypesFor(unmet, listing);
    return allowedTypes === null ? null : { machineKey: row.machineKey, allowedTypes };
  }

  const noSupportedRows = !machineTypeFilters.some((filter) => SECTION_MACHINE_ROWS[filter.key]);
  if (noSupportedRows && machineCount('printing_machine') === 0) {
    return { machineKey: 'printing_machine', allowedTypes: undefined };
  }
  return null;
};

// The uuids of a solution group, joined -- the stable identity of "this card", used to tell whether two
// groups are the same solution (e.g. a restored selection vs a freshly fetched grid). score_data is dropped
// on persistence, but the uuids survive, so this matches across a remount.
const groupUuids = (group) => (group || []).map((solution) => solution.diecutting_result.uuid).join('|');

// Prime the score cache from the embedded score_data, so the legacy card renders every solution with no XHR.
const primeSolutionScores = (data) => {
  ((data && data.solutions) || []).forEach((group) => group.solutions.forEach((solution) => {
    if (solution.score_data) { setInCache(solution.diecutting_result.uuid, solution.score_data); }
  }));
};

// The cheapest MAXIMUM_SOLUTION_GROUPS complete ways a solve makes the section, cheapest first (see
// combineSolutionGroups). Every part must be makeable, or the manufacturing line list makes nothing.
const solutionGroupsOf = (data) => {
  const groups = (data && data.solutions) || [];
  const perPart = groups.map((group) => group.solutions).filter((list) => list.length > 0);
  if (perPart.length === 0 || perPart.length !== groups.length) { return []; }

  return combineSolutionGroups(perPart, MAXIMUM_SOLUTION_GROUPS);
};

// What a solve answers with when it failed: no solutions, which the grid reports as such.
const NO_SOLUTIONS_SOLVED = { solutions: [] };

// Append the quantity (count) to a fetch's query. The client sends count on all three /mcc fetches so the
// server scores / solves against the count the page is SHOWING -- not whatever user_quote.count happens to be,
// which the count field persists ASYNCHRONOUSLY (a server that read the quote could score a not-yet-saved
// quantity). It also puts count in the URL, so it is part of the response-cache key. Absent count (the legacy
// stacked view, which never passes it) is simply not appended, and the server falls back to the quote's count.
const setCountParam = (query, count) => {
  if (count !== undefined && count !== null) { query.set('count', count); }
};

// The /mcc /solutions/<index> URL for a manufacturing line list, built from the section's current inputs. ONE
// source so the solve effect and the Pricing-tab machine-change handler solve a line with byte-identical params:
// the URL is the response-cache key, so any divergence would re-solve a line already solved and could price it on
// different sheets/filters than it was listed with. The parameter ORDER is deliberate, for that cache-key stability.
const solutionsUrlFor = (manufacturingLineListIdentifier, {
  userQuoteId, navSectionIndex, bobbinEnabled, selectedMaterialUserPriceId, selectedCartonGsm,
  disabledFilters, selectedTypes, areaRatioPercent, count
}) => {
  const query = new URLSearchParams();
  query.set('manufacturing_line_list', manufacturingLineListIdentifier);
  if (bobbinEnabled !== undefined) { query.set('bobbin_enabled', bobbinEnabled); }
  if (selectedMaterialUserPriceId) { query.set('material_user_price_id', selectedMaterialUserPriceId); }
  // The carton GSM the section is PRICED at (the same one its listing was found with), so a carton_lamination
  // solution is priced at the picked carton, not the default 300.
  if (selectedCartonGsm !== null) { query.set('carton_gsm', selectedCartonGsm); }
  disabledFilters.forEach((key) => query.append('disabled_filters[]', key));
  Object.entries(selectedTypes).forEach(([key, types]) => {
    if (types.length === 0) {
      query.append(`${key}[]`, '');
      return;
    }
    types.forEach((type) => query.append(`${key}[]`, type));
  });
  // The same cap the listing used -- a plan is only meaningful against the listing it came from.
  query.set('area_ratio_percent', areaRatioPercent);
  setCountParam(query, count);
  appendManufacturingLinesSource(query);
  return `/mcc/${userQuoteId}/solutions/${navSectionIndex}?${query.toString()}`;
};

// stageView: when set, this component renders ONLY ONE sub-view -- 'line' (the filter panel + the
// manufacturing-line listing), 'solution' (the picked plan's solution cards) or 'pricing' (the priced view). It
// is set by BOTH the guided setup wizard (FrankSetupWizard, which splits the flow into modal stages) and the
// section's three page tabs (FrankQuoteBuilderPage: "<Section> - Manufacturing Line / Solution / Pricing"), so
// both surfaces reuse this one component's fetching, scoring, solving and pricing. Absent (the legacy stacked
// admin view) leaves every behaviour unchanged.
//   inWizard distinguishes the two: in the wizard a solution click just MARKS the card (the wizard commits on
//   its own "Done") and cards are drawn 3-across for the narrow modal; on the page tabs a solution is marked
//   and then committed by the Pricing tab, and cards keep the page's 4-across layout.
//   onWizardPickedChange / onWizardSelectionChange report the marked plan / solution up to the wizard (for its
//   Next / Done). onLinePicked / onSolutionPicked fire on a USER click (not on restore) so the page tabs can
//   auto-advance to the next tab.
//   onGoToNextStage advances the page's section tabs one stage on (Manufacturing Line -> Solution -> Pricing).
//   The parent binds it to the stage AFTER the one on screen, so this component just calls it. It is the page's
//   explicit "Next" button under a SELECTED card; the wizard has its own Next in the modal footer, so it does
//   NOT pass this (its absence is what keeps the "Next" button off the wizard's cards).
//   onGoToManufacturingLine jumps the page's section tabs back to the Manufacturing Line stage. It backs the
//   "select a Manufacturing Line" prompt the Solution tab shows when the user arrives with nothing picked. The
//   wizard does not pass it (its own Next is disabled until a line is picked, so it never reaches that state).
const ManufacturingLineLists = ({
  navSectionIndex, bobbin, config, stageView, inWizard, count,
  onWizardPickedChange, onWizardSelectionChange, onLinePicked, onSolutionPicked,
  onGoToNextStage, onGoToManufacturingLine, onGoToLinkedParent,
  // The page's "switch this quote to Standard lines and open THIS line's solutions" handler, given to the
  // standard-lines fallback shown when the owner's OWN lines come up empty (see renderListing / the page's
  // useStandardLineForSection). Passed on the page tabs AND in the guided setup wizard, but not the legacy stacked
  // view -- so the fallback shows in the first two and not the last.
  onUseStandardLine,
  // Wizard-only: the price form for a found line's unpriced machine runs through the wizard footer (submitRef +
  // its resolve), and onWizardMachinePriceChange reports WHICH machine is being priced up so the footer shows
  // the machine-price title and its Next saves the form -- the same footer-integrated path the machine_price step uses.
  onWizardMachinePriceChange, wizardSetupSubmitRef, onWizardSetupResolved, onWizardSetupSaveError
}) => {
  // The RAW bobbin value (undefined until touched -> the server's part_type default) that the listing is found
  // over -- read off the one `bobbin` descriptor rather than as a second prop the parent has to keep in step
  // with bobbin.checked. Absent (no bobbin row on this page) reads as undefined, exactly as before.
  const bobbinEnabled = bobbin ? bobbin.enabled : undefined;

  // A LINKED SECTION HAS NOTHING TO CHOOSE. Its manufacturing line and its solution come from another section --
  // a corrugated section is produced together with the carton_lamination one that leads it, on one line, in one
  // pass -- so it lists nothing, solves nothing and scores nothing, and says so instead (renderLinkedNote).
  // ONLY those two are shared: it still prices itself, and its priced view renders like any other section's.
  //
  // "Nothing to choose" is not "nothing to ask". It still fetches the inherited PLAN, so its Manufacturing Line
  // tab has a card to draw -- against the LEADING section's index, since it has no manufacturing lines of its
  // own (see planSectionIndex) -- and SelectedSolutionSections still posts select-solution, which is what
  // prices it. Three of the four fetch effects below bail on isLinked; the priced-plan one deliberately does
  // not, it redirects. Read them as "does not choose", not "does not talk to the server".
  //
  // Read from window.user_quote_nav_sections rather than re-derived from part types: the pairing is decided
  // server-side (linkedNavSection.js), and every select-solution response refreshes that global, so a section
  // that becomes linked mid-session is seen on the next render.
  //
  // Each effect bails individually rather than the component early-returning: hooks must run unconditionally,
  // so "does not fetch" has to be said at every fetch. The legacy listing does the same
  // (LayoutNavSectionCombinationList's isLinkedSection).
  // The row capacity of the surface this is rendered on -- the wizard's modal fits three cards across, the page
  // four. Below it the cards are centred in the spare width.
  const cardsPerRow = inWizard ? WIZARD_CARDS_PER_ROW : PAGE_CARDS_PER_ROW;

  const navSectionsForLink = window.user_quote_nav_sections;
  const isLinked = isLinkedSection(navSectionsForLink, navSectionIndex);
  const linkedParent = linkedParentOf(navSectionsForLink, navSectionIndex);

  // WHOSE MANUFACTURING LINES TO ASK ABOUT. A linked section's plan belongs to the section that LEADS it, so the
  // plan is looked up against the PARENT's index: asking for this section's own would find nothing, because it
  // has no manufacturing lines of its own -- which is the whole reason it inherits.
  const planSectionIndex = isLinked && linkedParent ? linkedParent.sort_order : navSectionIndex;

  const [listing, setListing] = useState(null);
  // WHY THERE IS NO LISTING, when there is none. The section's manufacturing lines are worked out on the
  // manufacturing_cost_calculator queue now, so an ask can END in a failure -- the work raised, or (in
  // development) nothing is listening on that queue at all -- and a failure arrives here as no listing, which
  // is EXACTLY what "still loading" looks like. Drawn as one, it is a stage that never fills: the guided setup
  // wizard sat on an empty "Select a manufacturing line for Carton" with a disabled Next, no cards, no message
  // and no end, while the reason went to a console nobody had open. So the failure is held, said, and can be
  // asked again -- see #renderListingUnavailable.
  const [listingUnavailable, setListingUnavailable] = useState(null);
  // Once a search actually STARTS, every filter locks -- disabled and dimmed -- until the answer lands, so a
  // viewer cannot fire a fresh find + score while one is already running. Ticking before Search leaves it
  // unlocked (the ticks are cheap, local state).
  const [isRefetchingListing, setIsRefetchingListing] = useState(false);
  // THE VIEWER GAVE UP ON THIS SEARCH. The find + score can take tens of seconds, and while it runs the filters
  // are locked -- so without a way out, wanting a different bobbin or machine type means waiting for an answer
  // nobody wants. Cancelling stops the requests, unlocks the block and leaves whatever was already found on
  // screen; the next committed tick starts a fresh search and clears this.
  const [searchCancelled, setSearchCancelled] = useState(false);
  // How the Cancel button reaches the two passes it has to stop. Each pass writes its own stopper here while it
  // is in flight -- the listing fetch marks itself superseded, the score batch aborts its requests and empties
  // the queue -- because both live inside effects the button cannot see into.
  const cancelListingRef = useRef(null);
  const cancelScoringRef = useRef(null);
  // A DELIBERATE FRESH SEARCH: the viewer pressed "Ara" (not a filter select searching on its own), so the first
  // ask of both passes drops any stuck 'processing' claim (refresh) instead of joining it -- the recovery from a
  // worker killed mid-job, which otherwise holds the claim for PROCESSING_TTL. Set by the button / "Try again".
  //
  // CONSUMED BY THE LISTING EFFECT, which runs on every re-list (even a cache hit, where the fetch short-circuits),
  // so the flag can never survive the search that set it and leak into the next one. That effect carries the intent
  // to the scoring pass by stamping the listing object it produces into freshlyFetchedListingRef; the scoring pass
  // asks afresh only for that exact object. The passes' own timed-out rounds ask afresh regardless (askAboutLines /
  // askAgainOrReport), so this is only about the very first ask a press makes.
  const freshSearchRef = useRef(false);
  // The listing object a pressed "Ara" produced, so the scoring pass that keys off it (and only that one) asks its
  // first round afresh too. Keyed by object identity rather than a boolean, so a later search -- which mints a
  // different listing object -- is never mistaken for the pressed one.
  const freshlyFetchedListingRef = useRef(null);
  // A SEARCH THAT ANSWERS THE SAME AS THE LAST ONE STILL HAS TO LOOK LIKE AN ANSWER. Two searches can list the
  // very same manufacturing lines -- bobbin on and bobbin off are found over different material sheets, but the
  // same presses and cutters run both -- so the results settle back to a screen identical to the one before the
  // press, and the honest conclusion from watching that is that the filter does nothing. The results dim for
  // half a second as they land instead: something arrived, and it happens to be this. See the flash effect.
  const [resultsJustRefreshed, setResultsJustRefreshed] = useState(false);
  // Set by Search and read when the results settle, so ONLY an asked-for search flashes -- not the section's own
  // first listing, and not a cancelled one (nothing arrived to show).
  // WHAT THIS SEARCH IS WAITING FOR: the counters as they stood when Search was pressed, or null when nothing
  // was asked for. The blink fires when BOTH have moved past them -- a listing has landed for this press and its
  // scoring pass has finished -- because either alone is a half-answer: the moment the listing lands, the scores
  // on screen are still the previous search's and read as complete, and blinking there says "here is your
  // answer" over numbers about to be thrown away.
  const awaitingSearchResultsRef = useRef(null);
  // How many listings have LANDED for this section (see the listing fetch). The blink watches this rather than
  // the listing itself, because a Search that answers with the listing already on screen changes no state at
  // all -- same object from the response cache, no scoring pass, no request.
  const [listingLandedCount, setListingLandedCount] = useState(0);
  // How many scoring passes have finished for this section. Counted for the same reason as the landing above:
  // re-scoring the same lines at the same quantity ends with the very numbers already on screen, so nothing
  // about the state itself says a pass completed.
  const [scoringCompletedCount, setScoringCompletedCount] = useState(0);
  // The reductions switched OFF for the SECTION: ['smallest_die_cutting_machine_of_each_type'].
  // Every one is ON by default -- switching one off is an administrator asking to SEE what it hides, so the
  // reduction is never a silent one.
  //
  // Restored from the address bar (like the picked plan below), so the section comes back on the filters it was
  // left on rather than on the server's defaults -- see manufacturingLineListUrlState.
  const [disabledFilters, setDisabledFilters] = useState(() => readSectionDisabledFilters(navSectionIndex));
  // The machine types ticked for the SECTION, by filter key: { printing_machine_types: ['offset'] }.
  // Absent means "not spoken yet" -- the server applies the filter's own default (Offset for printing;
  // everything but Digital for die-cutting), so the defaults live in ONE place rather than being duplicated here.
  const [selectedTypes, setSelectedTypes] = useState(() => readSectionMachineTypes(navSectionIndex));
  // The owner's chosen material row (a UserUnitPrice id) for the Malzeme dropdown, or null for the server's
  // lowest-id auto-pick. Restored from the address bar like the other filters; sent as material_user_price_id.
  const [selectedMaterialUserPriceId, setSelectedMaterialUserPriceId] = useState(() => readSectionMaterialUserPriceId(navSectionIndex));
  // The carton GSM the owner picked for a carton_lamination section (the Malzeme-row dropdown), or null for the
  // server's default (300). It sets the printing material's thickness, so it changes WHICH printing machines the
  // finder returns AND the carton the section is priced at. Restored from the address bar. It is on the LISTING
  // query like the machine-type ticks, but a CHANGE searches at once (the effect below) rather than waiting for
  // "Ara" -- a select reacting to its own change is the nicer UX.
  const [selectedCartonGsm, setSelectedCartonGsm] = useState(() => readSectionCartonGsm(navSectionIndex));
  // Search when the GSM changes. runSearch is defined far below (it depends on much of what follows), so it is
  // reached through a ref that carries its latest closure. The ref-guard skips the initial value, so a mount /
  // URL restore -- not a change the viewer made -- does not search on its own.
  const runSearchRef = useRef(null);
  const committedCartonGsmRef = useRef(selectedCartonGsm);
  useEffect(() => {
    if (committedCartonGsmRef.current === selectedCartonGsm) { return; }
    committedCartonGsmRef.current = selectedCartonGsm;
    runSearchRef.current?.();
  }, [selectedCartonGsm]);
  // Search when the material choice changes, like the carton GSM select above: it changes WHICH lines are found
  // and how they score, so a change re-finds and re-scores at once rather than waiting for "Ara". The ref-guard
  // skips the mount / URL restore, so a restored material does not search on its own. For a cover this is the ONLY
  // way its coating grammage changes -- the found/priced paper is the chosen coating material row's own grammage.
  const committedMaterialRef = useRef(selectedMaterialUserPriceId);
  useEffect(() => {
    if (committedMaterialRef.current === selectedMaterialUserPriceId) { return; }
    committedMaterialRef.current = selectedMaterialUserPriceId;
    runSearchRef.current?.();
  }, [selectedMaterialUserPriceId]);
  // The owner's OWN material rows for THIS section (each carrying its bobbin flag / thickness), so the add/manage
  // button label and the "add a material first" gate can tell whether the owner has a material that FITS the
  // current bobbin state -- not merely ANY material of this key (a non-bobbin carton row does not price a bobbin
  // solution). The SAME list and the SAME fit test the Malzeme dropdown reads (materialCompatibility). Fetched on
  // mount only when on the owner's own lines with a material key, and refreshed by the remount a material
  // add/remove triggers (frank:own-machines-changed bumps the section key). null while UNKNOWN -- not loaded, or
  // the response could not be read -- so the gate falls back to the synchronous total count rather than wrongly
  // reading "no material" while the list is still in flight. See #compatibleOwnMaterialCount.
  const [ownMaterials, setOwnMaterials] = useState(null);

  // THE FILTERS ARE ASKED FOR, NOT SUBMITTED. The checkboxes above change the edited values at once so the ticks
  // respond instantly, but nothing is found or scored until the viewer presses Search: a find + score is tens of
  // seconds of server work, and running one per tick (or one per burst, on a timer that fires while the viewer
  // is still deciding) spent it on filter sets nobody asked about. So the LISTING works over the COMMITTED
  // filters, which catch up to the edited ones only on Search -- and a section switch, whose listing cannot wait
  // to be asked for. `pendingFilterKey` is a stable string of the edited filters; the listing fetch and the
  // score/selection reset both key off `committedFilterKey`.
  //   NOTE: the bobbin is the ONE exception. Its material-sheet recompute (the "Chosen material dimensions"
  //   line) fires straight away from the parent's onChange -- only the LISTING half of a bobbin change waits for
  //   Search. See LayoutAndQuoteBuilderNavSection#fetchDefaultMaterialValues.
  //   The area-ratio input (below) is not on it either -- it commits on blur / Enter, its own coarse commit, and
  //   drives the fetches directly as areaRatioPercent.
  // selectedMaterialUserPriceId IS a committed listing filter, exactly like the carton GSM: the chosen material
  // feeds a different material sheet into the finder AND prices the solutions, so it changes WHICH manufacturing
  // lines are found and what they score -- not just their cost (BRISTOL and KROMO score differently for the same
  // section). It rides the LISTING query, and its own commit-on-change effect (beside the carton GSM one above)
  // searches at once rather than waiting for "Ara" -- so a change re-finds and re-scores immediately.
  const pendingFilterKey = JSON.stringify({ bobbinEnabled, disabledFilters, selectedTypes, selectedCartonGsm, selectedMaterialUserPriceId });
  const [committedFilterKey, setCommittedFilterKey] = useState(pendingFilterKey);
  // Bumped by every Search press, and a trigger of the listing fetch in its own right -- so pressing Search
  // AFTER a cancel runs the same filters again, which committedFilterKey alone (unchanged) could not ask for.
  const [searchRunNumber, setSearchRunNumber] = useState(0);
  const committedSectionRef = useRef(navSectionIndex);
  // The area-ratio cap the admin is experimenting with, as a percent. areaRatioPercent is the COMMITTED value
  // that drives the fetches (the listing, the solve and the scoring all reduce against it, so a plan is always
  // listed and solved against the same cap); areaRatioInput is the text shown while typing, committed on blur /
  // Enter -- not per keystroke, because each change re-runs a full listing and its heavy per-line scoring.
  // Restored from the address bar too, so the listing, the solve and the scoring come back against the very cap
  // they were left on -- a plan picked under a tuned cap is only meaningful against that cap.
  const [areaRatioPercent, setAreaRatioPercent] = useState(
    () => readSectionAreaRatioPercent(navSectionIndex) ?? DEFAULT_AREA_RATIO_PERCENT
  );
  const [areaRatioInput, setAreaRatioInput] = useState(() => String(areaRatioPercent));
  // The ManufacturingLineList the viewer picked, by identifier -- the plan whose solutions are shown. null = the
  // listing, nothing picked.
  // Restored from the address bar up front (manufacturing_line_<index>) so a refresh / shared link reopens the
  // picked plan straight away, before the listing has even loaded. A stale value (a plan the listing that lands
  // does not hold) is dropped once the listing is known -- see the stale-restore guard below.
  const [pickedList, setPickedList] = useState(() => readSelectedManufacturingLineList(navSectionIndex));
  const [solutionsLoading, setSolutionsLoading] = useState(false);
  // The picked manufacturing line list's solve response, null while nothing is solved. Its SOLUTION GROUPS -- each
  // an ARRAY holding ONE solution per part, drawn as ONE card -- are built from it here, during render, so a page
  // missing its maximum (MAXIMUM_SOLUTION_GROUPS) fails loudly rather than in a promise that would report "no
  // solutions".
  const [solveData, setSolveData] = useState(null);
  const solutionGroups = useMemo(() => (solveData ? solutionGroupsOf(solveData) : null), [solveData]);
  // The solutions grid, so the ranking pass can reach the cards' data-score as each lands.
  const solutionsGridRef = useRef(null);
  // MAY THE SOLUTION GRID BE REVEALED? #solutionsSettled (which explains WHY the grid is held) reads the DOM and
  // cannot drive a render, so its answer is held here as state and renderSolutions KEEPS the grid hidden until it
  // is true. Driven by #settleSolutionGridReveal (below), both before paint and on a timer.
  const [solutionGridRanked, setSolutionGridRanked] = useState(false);
  // A score that never lands (getScore's fetch can fail and is not retried, leaving a card at 0) must not hide the
  // grid forever, so a floor timer forces the reveal. It is a flag, not an elapsed-time read: the reveal decision
  // runs in a layout effect on every render, and a Date.now() comparison there would change value between renders
  // and loop. Reset when a new solve starts and when the grid unmounts, so a stale force never carries across a
  // tab switch (the three stage tabs share one mounted section, so this component's state outlives the grid).
  const solutionRevealForcedRef = useRef(false);
  // This section's own subtree, which is what the scroll-to-the-selection below looks for its marked card in.
  // The page mounts one of these at a time (FrankQuoteBuilderPage renders the active section, and hides the tab
  // body outright while the wizard is open), so this is not fixing a collision -- it keeps the search inside
  // what this component drew rather than over the whole document.
  const sectionRootRef = useRef(null);
  // Which arrival (stage + section) the page has already scrolled for, so opening a tab scrolls ONCE -- picking
  // a different card while already there must not yank the page.
  const scrolledForStageRef = useRef(null);
  // The arrival the last render was on, which is how the one above is re-armed: noticing the CHANGE here keeps
  // it in the same pass. A separate effect for it also fired on mount, wiping the mark the first pass had just
  // made -- and a second watch then started as soon as the cards landed.
  const lastArrivalRef = useRef(null);
  // The animation frame the settle-watch is waiting on, so it can be called off on unmount.
  const watchFrameRef = useRef(null);
  // How that watch lets go of its listeners, kept so unmount can do it too.
  const watchTeardownRef = useRef(null);
  // The beat between settling and gliding, kept so unmount can call it off.
  const glideTimerRef = useRef(null);
  // Whether the list this stage draws has finished arriving -- written during the render that knows (below,
  // where solutionsLoading and bestScoresReady are both in hand) and read by the scroll effect above.
  const listSettledRef = useRef(false);
  // The BEST (lowest) score of each DISTINCT manufacturing line, keyed by lineKey -- what the cards sort and
  // label by. Empty until all the per-line best-score requests have landed (the progress bar covers the wait).
  // null for a line that produces no solution (its list sorts last).
  const [bestScoreByLine, setBestScoreByLine] = useState({});
  // THE MANUFACTURING LINES WITH NO SCORE OF THEIR OWN: keyed by lineKey, each `{ reason, asking }` -- why it
  // has none, and whether anything is still trying to get one.
  //
  // WHY IT IS NOT ENOUGH TO RECORD null. A manufacturing line the server answers about with no solution is
  // recorded null too, and that null is what the card draws the red em dash for: "this plan cannot be
  // manufactured". A lost answer recorded the same way therefore said that about a plan nobody had established
  // anything of the kind for -- and, because bestScoreByLine having the key is what "answered" means, it also
  // told the SECTION it was finished: the progress went away, the listing sorted itself and drew its stars, and
  // the given-up cards sat in the finished listing with no value.
  //
  // These lines are still recorded null in bestScoreByLine, so one stuck manufacturing line cannot hold every
  // other card hostage -- but this map is what everything asking "are we done" reads, and while a key is in here
  // with `asking` the section is still working.
  const [unscoredLines, setUnscoredLines] = useState(new Map());

  // Bumped by the retry control to run the scoring pass again -- see #retryUnscoredScores for why a retry is a
  // re-run rather than a poke at the pass that gave up.
  const [scoringRetryNumber, setScoringRetryNumber] = useState(0);
  // THE CHOSEN SOLUTION -- an ARRAY holding one solution per part (a solution GROUP, which is what one card
  // is), or null while none is chosen. Choosing one is what moves the section from "how would this be made" to
  // "what does it cost": it is stored on the section and the cost sections are priced from it.
  //
  // Held WITH THE SECTION IT BELONGS TO, and read back only for that section. The page used to switch section
  // WITHOUT remounting this component (today every section has its own instance -- see
  // selectionResetSkipRef), and a child's effect runs before its parent's -- so a bare array would
  // be visible to children for one render under the NEW index while still holding the PREVIOUS section's
  // choice, and SelectedSolutionSections would post it there. That is not a wasted request: the endpoint
  // writes `lists` straight over the section's selected_lists, so section 0's solution would be saved onto
  // section 1 while the page, one effect later, correctly shows section 1 as unpriced.
  //
  // Restored from what the page was rendered with, so a refresh reopens on the priced section.
  const [selection, setSelection] = useState(() => ({
    index: navSectionIndex,
    solutions: restoreSelectedSolutions(navSectionIndex)
  }));

  // THE PRICED PLAN'S CARD, recovered for the refresh path. On a refresh the listing is re-fetched with the
  // DEFAULT machine-type filters, which drop a plan whose machines are off by default (a Flatbed UV press + a
  // Digital cutter) -- so the priced view can no longer find its own plan in `listing`. It fetches that plan by
  // identifier from the UNFILTERED priced endpoint into here. null while nothing is priced, or when the listing
  // already holds the plan (a fresh in-session select, where the filters the viewer ticked to surface it are
  // still set).
  const [pricedPlanData, setPricedPlanData] = useState(null);
  // WHETHER THAT ASK IS STILL OUT, and why it ended with nothing when it did. The plan is worked out on the
  // manufacturing_cost_calculator queue like every other section answer, so there is a real wait before it lands
  // and a real way for it to fail -- and a LINKED section has nothing else on its Manufacturing Line tab, so
  // both of those were an empty tab: no card, no bar, no message, nothing to press. Held here so the wait can
  // show as a wait and the failure can be said (see the plan slot in renderSelectedSolution).
  const [pricedPlanPending, setPricedPlanPending] = useState(false);
  const [pricedPlanUnavailable, setPricedPlanUnavailable] = useState(null);
  // Try again for that failure: bumped by the button, and a dependency of the ask, so pressing it asks again.
  const [pricedPlanAskNumber, setPricedPlanAskNumber] = useState(0);

  // The solution card the user has MARKED in a 'solution' view (wizard stage or the page's Solution tab). Held
  // separately from `selection` on purpose: setting `selection` would switch this component to the priced view
  // (and its solutions-fetch guard would clear the grid), whereas we want the solution grid to stay on screen
  // with the marked card highlighted. The wizard commits it on its own "Done"; the page's Pricing tab commits
  // it (see the pricing commit effect). Restored from the already-priced section on mount so revisiting a
  // completed section re-marks its card.
  const [markedSolution, setMarkedSolution] = useState(
    () => (stageView === 'solution' ? restoreSelectedSolutions(navSectionIndex) : null)
  );

  const selectedSolutions = selection.index === navSectionIndex ? selection.solutions : null;

  // The setter the children are handed. They pass an array or an updater, exactly as they would to a plain
  // useState setter (the shared resetSelectedListHandler passes `() => []`); filing it against the section on
  // screen is this component's business, not theirs.
  const setSelectedSolutions = useCallback((value) => {
    setSelection((previous) => {
      const current = previous.index === navSectionIndex ? previous.solutions : null;
      return {
        index: navSectionIndex,
        solutions: typeof value === 'function' ? value(current) : value
      };
    });
  }, [navSectionIndex]);

  // Mark a solution card (does NOT set `selection`, see #markedSolution). Clicking the marked card again
  // unmarks it. Used by both the wizard solution stage and the page's Solution tab.
  const markSolution = useCallback((group) => {
    setMarkedSolution((previous) => (groupUuids(previous) === groupUuids(group) ? null : group));
  }, []);

  // /mcc PRICING TAB: changing the printing or die-cutting machine picker is a manufacturing-line change (the
  // machine sets the sheet, which sets the layout), so the line is re-selected and re-solved (below). The priced
  // view is dimmed while that runs; a machine the current line cannot be paired with raises the notice instead.
  const [reselectingForMachineChange, setReselectingForMachineChange] = useState(false);
  const [machineChangeUnavailable, setMachineChangeUnavailable] = useState(false);
  // Bumped on every machine change, so a slower earlier solve cannot commit over a newer pick.
  const machineChangeTokenRef = useRef(0);
  // True between committing a machine-change re-select and the re-price it triggers landing, so the "recalculating"
  // overlay is lifted by the select-solution completion (navSectionsUpdated) rather than the moment the commit is
  // fired -- otherwise the old price shows as final while the re-price is still in flight.
  const awaitingMachineChangeRepriceRef = useRef(false);

  // The /solutions URL for the machine-change handler to solve a line the Pricing tab never solves (the solve
  // effect returns early on 'pricing'). Delegates to solutionsUrlFor, the SAME builder the solve effect uses.
  const buildSolveUrl = useCallback((manufacturingLineListIdentifier) => solutionsUrlFor(manufacturingLineListIdentifier, {
    userQuoteId: window.user_quote_id, navSectionIndex, bobbinEnabled, selectedMaterialUserPriceId,
    selectedCartonGsm, disabledFilters, selectedTypes, areaRatioPercent, count
  }), [bobbinEnabled, selectedMaterialUserPriceId, selectedCartonGsm, disabledFilters, selectedTypes,
    areaRatioPercent, count, navSectionIndex]);

  // Report the marked plan / marked solution up to the wizard (FrankSetupWizard) as they change, so its
  // Next / Done buttons enable and disable in step with the selection on screen. No-ops on the page tabs (the
  // callbacks are absent there).
  //
  // A plan is reported ONLY once the listing has loaded and still OFFERS it. `pickedList` is restored from the
  // manufacturing_line_<index> URL param before the listing loads (so a shared link reopens the picked plan),
  // and reporting that unconfirmed value let a STALE param enable the wizard's Next on an empty, still-loading
  // line stage -- advancing the guided flow with nothing really chosen and no valid plan for the solution stage.
  // Gating on the loaded listing makes the wizard's Next track the card the listing actually shows selected:
  // disabled while the listing loads, disabled for a stale plan the listing dropped, enabled once a real plan
  // (restored or clicked) is on screen. The stale-restore guard below clears such a pick from `pickedList` too,
  // but only after the listing lands; this keeps the wizard's Next honest in the meantime.
  useEffect(() => {
    if (!onWizardPickedChange) { return; }
    const offeredByListing = Boolean(pickedList) && planOfferedByStructure(listing, pickedList);
    onWizardPickedChange(offeredByListing ? pickedList : null);
  }, [pickedList, listing, onWizardPickedChange]);

  // The machine a found own line names but has no price for (findOwnUnpricedMachine), computed HERE -- with the
  // other hooks, above the component's early returns -- so the notification effect below is a top-level hook. It is
  // also what renderListing shows the price adder for (ownMachinePriceMissing, further down).
  const ownUnpricedMachine = findOwnUnpricedMachine(listing);
  // Report that machine (or null once priced) up to the wizard, so its footer becomes the machine-price step -- the
  // title and the Next-saves-the-form behaviour -- rather than a second Save button colliding with the footer. Keyed
  // on the primitive machineKey/attributeId so it fires only when the answer changes, not on every listing render.
  const unpricedMachineKey = ownUnpricedMachine ? ownUnpricedMachine.machineKey : null;
  const unpricedMachineAttributeId = ownUnpricedMachine ? ownUnpricedMachine.attributeId : null;
  useEffect(() => {
    if (!onWizardMachinePriceChange) { return; }
    onWizardMachinePriceChange(
      inWizard && unpricedMachineKey
        ? { machineKey: unpricedMachineKey, attributeId: unpricedMachineAttributeId }
        : null
    );
  }, [inWizard, unpricedMachineKey, unpricedMachineAttributeId, onWizardMachinePriceChange]);

  // THE SOLUTION CHOSEN ON SCREEN -- ONE value, read by BOTH the card highlighting and the wizard's Next/Done,
  // so the two can never disagree. It is the in-session mark, or -- on a reopened priced section holding no
  // mark -- the section's own priced solution, which is what the card draws as "Selected".
  //
  // Reporting only `markedSolution` here is what left the reopened wizard drawing a card "Selected" with its
  // Next DISABLED: `markedSolution` restores from the priced section only when the component MOUNTS on the
  // solution view, and the wizard's line and solution stages share one instance (same key), so arriving from
  // the line stage never restores it -- while the card, reading the fallback below, showed it as chosen.
  //
  // The priced solution stands in ONLY while it is still on screen: either the solve returned nothing and it is
  // itself the card shown (the priced fallback), or it is one of the solved groups. Pick a DIFFERENT
  // manufacturing line and the section re-solves, and the old priced solution is not among the new plan's
  // solutions -- the card already highlights nothing there, and reporting it anyway would let the wizard's Done
  // persist a solution the user can no longer see. Same discipline as the picked plan above, which is reported
  // only while the listing still OFFERS it.
  // "Still on screen" is read as the CARD reads it, including while the solve is in flight: the grid is a
  // skeleton then, showing no solution at all, so a priced solution must not count as chosen -- otherwise Done
  // pressed during the solve would persist the OLD solution while the new plan's cards were still loading.
  //
  // The grid holds the cheapest MAXIMUM_SOLUTION_GROUPS solution groups, so the group the user priced or marked is
  // added when it is not among them -- only when it belongs to the manufacturing line list this solve is for, and
  // rebuilt from this solve's own solutions PART BY PART: a solution's uuid carries no member, so two parts can
  // share one (Frank::DieCuttingSolution#uuid), and matching across parts could price the wrong part.
  const solvedGroups = useMemo(() => {
    const groups = solutionGroups || [];
    const solvedParts = ((solveData && solveData.solutions) || []).map((part) => part.solutions);
    const kept = [];
    [selectedSolutions, markedSolution].forEach((chosen) => {
      if (!hasSelection(chosen) || chosen.length !== solvedParts.length) { return; }
      const chosenManufacturingLineList = chosen
        .map((solution) => solution.diecutting_result.manufacturing_line_identifier).join('|');
      if (chosenManufacturingLineList !== solveData.manufacturing_line_list) { return; }
      const offered = chosen.map((solution, part) => solvedParts[part].find(
        (candidate) => candidate.diecutting_result.uuid === solution.diecutting_result.uuid,
      ));
      if (offered.some((solution) => !solution)) { return; }
      const uuids = groupUuids(offered);
      if ([...groups, ...kept].some((group) => groupUuids(group) === uuids)) { return; }
      kept.push(offered);
    });
    return kept.length > 0 ? [...groups, ...kept] : groups;
  }, [solutionGroups, solveData, selectedSolutions, markedSolution]);
  const pricedSolutionOnScreen = hasSelection(selectedSolutions) && !solutionsLoading
    && (solvedGroups.length === 0
      || solvedGroups.some((group) => groupUuids(group) === groupUuids(selectedSolutions)));
  // In the guided-setup wizard a section with exactly one solution has nothing to choose, so that sole solution
  // IS the chosen one before any click: the wizard's Done gates on chosenSolution (onWizardSelectionChange) and
  // the card outlines from it, so deriving it here enables Done and selects the card with no click (issue #6145).
  // DERIVED, not marked -- it takes no state, re-derives on every re-pick / re-solve, yields to a real user mark
  // or a priced solution, and never sets markedSolution (which would fire the plan-change discard). Wizard only
  // and solution-stage only: the page's single-solution path (a click enabling its Pricing tab) is left alone,
  // and !solutionsLoading so an in-flight solve -- whose grid is a skeleton -- chooses nothing.
  const soleWizardSolution = inWizard && stageView === 'solution' && !solutionsLoading && solvedGroups.length === 1
    ? solvedGroups[0]
    : null;
  const chosenSolution = hasSelection(markedSolution)
    ? markedSolution
    : (pricedSolutionOnScreen ? selectedSolutions : soleWizardSolution);

  useEffect(() => {
    if (onWizardSelectionChange) { onWizardSelectionChange(chosenSolution); }
  }, [chosenSolution, onWizardSelectionChange]);

  // Fetch the owner's own material rows for this section (see #ownMaterials). Only on the owner's own lines with a
  // material key; nothing to ask on Standard (the example account) or a part_type with no material. NOT useCache
  // (like MaterialSelect): the list must be fresh, and this component remounts on a material add/remove, so a
  // cached copy would keep serving a list that no longer holds a just-added row.
  //
  // RESET TO null FIRST, on every (re)load. When the page moved between sections on one instance, the material key
  // changed without a remount, so leaving the previous section's rows in place filtered them against the new key's
  // criteria for the fetch window -- a wrong count, and a flashed gate. null means UNKNOWN, so the gate falls
  // back to the synchronous total count until the fresh list lands (or, on a failure GetRequest surfaces by never
  // calling back / a non-array body, it simply stays null -- never a stale list).
  useEffect(() => {
    setOwnMaterials(null);
    const materialKey = manufacturingLinesSource() === OWN && listing ? listing.material_section_key : null;
    if (!materialKey) { return; }
    GetRequest({
      url: `/quote-builder/user_unit_prices/list/section/${materialKey}/${window.locale}`,
      callback: (data) => { if (Array.isArray(data)) { setOwnMaterials(data); } },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the material key is what decides WHICH rows to fetch;
    // listing itself is deliberately not a dep (only its key is).
  }, [listing && listing.material_section_key]);

  // THE PAGE'S PRICING TAB COMMITS THE MARKED SOLUTION. The wizard commits on its Done; on the page there is no
  // Done, so arriving at the Pricing tab is what commits the marked solution -- setting `selection` renders the
  // priced view, which posts select-solution and prices the section. It commits whenever the mark DIFFERS from
  // what is priced, so re-picking a different solution on the Solution tab re-prices too.
  useEffect(() => {
    if (stageView !== 'pricing' || inWizard) { return; }
    if (hasSelection(markedSolution) && groupUuids(markedSolution) !== groupUuids(selectedSolutions)) {
      setSelectedSolutions(markedSolution);
    }
  }, [stageView, inWizard, markedSolution, selectedSolutions, setSelectedSolutions]);

  // RE-SELECT THE MANUFACTURING LINE WHEN A MACHINE IS CHANGED on the Pricing tab. The machine pickers belong to
  // the legacy cost form and are reached only through document events; changing one is really a line change, so
  // this pairs the newly-chosen machine with the OTHER machine the current line already has, switches to that
  // line, solves it and auto-picks its cheapest solution -- keeping the machine, the line and the price one
  // consistent choice. `manufacturing_line_<index>` follows setPickedList and `solution_<index>` follows the
  // re-price, so a refresh reopens on the new machine.
  //
  // Scope (guarded): the Pricing tab, the owner's OWN lines, a single-member section and an OFFSET line (printing
  // + die-cutting). Multi-member, combined-flexo and die-cutting-only lines are changed on the Manufacturing Line
  // tab (documented follow-ups).
  useEffect(() => {
    const OFFSET_PREFIX = 'manufacturing_line:offset:';

    const onMachineChanged = (machineKind) => (event) => {
      const detail = event.detail || {};
      if (stageView !== 'pricing' || inWizard || manufacturingLinesSource() !== OWN) { return; }
      if (!detail.nav_section || detail.nav_section.index !== navSectionIndex) { return; }
      // One member only: the line parameter is the section's single line; a shirt box's Kapak + Kutu each have
      // their own line and are out of scope.
      if (!Array.isArray(selectedSolutions) || selectedSolutions.length !== 1) { return; }

      const currentLineIdentifier = selectedSolutions[0]
        && selectedSolutions[0].diecutting_result
        && selectedSolutions[0].diecutting_result.manufacturing_line_identifier;
      if (!currentLineIdentifier || !currentLineIdentifier.startsWith(OFFSET_PREFIX)) { return; }

      const field = machineKind === 'printing' ? 'printing_machine_user_price_id' : 'die_cutting_machine_user_price_id';
      const priceRowId = Number(detail.values && detail.values[field]);
      // A real price row only; 'not-available' / unset is the empty state, not a machine to switch to.
      if (!Number.isInteger(priceRowId) || priceRowId <= 0) { return; }

      const token = machineChangeTokenRef.current + 1;
      machineChangeTokenRef.current = token;

      const attributeKey = machineKind === 'printing'
        ? 'user_unit_price_attribute.printing_machine'
        : 'user_unit_price_attribute.die_cutting_machine';

      // A machine change that cannot become a manufacturing line: the section's picker only offers machines
      // compatible with it, so a swap with no line (or no solution) points at a filtering / pairing bug, not an
      // ordinary user outcome -- show the notice AND record the event to debug it.
      const reportUnavailable = (reason, target) => {
        if (machineChangeTokenRef.current !== token) { return; }
        setReselectingForMachineChange(false);
        setMachineChangeUnavailable(true);
        if (typeof trackEvent === 'function') {
          trackEvent('frank_machine_not_available', {
            reason: reason,
            machine_kind: machineKind,
            price_row_id: priceRowId,
            target_manufacturing_line_identifier: target,
            current_manufacturing_line_identifier: currentLineIdentifier,
            nav_section_index: navSectionIndex,
            user_quote_id: window.user_quote_id
          });
        }
      };

      // Resolve the chosen price row to its machine ATTRIBUTE id -- the id the line identifier is built from,
      // which the picker rows do not carry. Owner-scoped by the endpoint (current_user), so a foreign id 404s.
      GetRequest({
        url: `/quote-builder/user_unit_prices/${priceRowId}/`,
        useCache: true,
        onError: () => {
          if (machineChangeTokenRef.current !== token) { return; }
          // Never leave the "recalculating" dim up on a failed lookup -- the switch cannot proceed, so the priced
          // view must stay usable (the picker reseeds to the line's own machine on reload).
          setReselectingForMachineChange(false);
          if (typeof trackEvent === 'function') {
            trackEvent('js_error_happened', {
              exception: '[ManufacturingLineLists#onMachineChanged] could not resolve the chosen machine attribute',
              nav_section_index: navSectionIndex,
              user_quote_id: window.user_quote_id
            });
          }
        },
        callback: (row) => {
          if (machineChangeTokenRef.current !== token) { return; }
          const chosenAttributeId = row && row[attributeKey];
          // A malformed row with no attribute is nothing to switch to; still drop any overlay a now-superseded
          // earlier change left up (this is the current token -- see the guard above).
          if (!chosenAttributeId) { setReselectingForMachineChange(false); return; }

          const machineAttributes = currentLineIdentifier.slice(OFFSET_PREFIX.length).split(':');
          const target = machineKind === 'printing'
            ? `${OFFSET_PREFIX}${chosenAttributeId}:${machineAttributes[1]}`
            : `${OFFSET_PREFIX}${machineAttributes[0]}:${chosenAttributeId}`;
          // The picker republishes its seeded value on mount, and a machine can be re-picked to the one already in
          // the line -- either way there is nothing to change. Returning the picker to its current machine must also
          // drop the overlay: an earlier change that is now superseded (its solve will no-op on the token) would
          // otherwise leave the "recalculating" dim up for good, since nothing commits to lift it.
          if (target === currentLineIdentifier) { setReselectingForMachineChange(false); return; }

          // A machine the finder never paired with the other current one for this section -- no valid line to
          // switch to.
          if (!planOfferedByStructure(listing, target)) {
            reportUnavailable('not_offered', target);
            return;
          }

          // Solve the target BEFORE switching to it: a structurally-offered line can still solve to NO solution at
          // this quantity, and moving pickedList to a line with no solution would leave the section on the new line
          // but the old solution -- the conflicted state this exists to avoid. So commit only once a solution exists.
          setReselectingForMachineChange(true);
          fetchJsonCached(buildSolveUrl(target))
            .then((data) => buildSolutionGroups(data))
            .then((groups) => {
              if (machineChangeTokenRef.current !== token) { return; }
              const cheapest = (groups || [])[0];
              if (!cheapest) {
                reportUnavailable('no_solution', target);
                return;
              }
              setPickedList(target);
              // Keep the MARK in step with the commit. The pricing-commit effect above re-commits markedSolution
              // whenever it differs from what is priced, so a stale mark left by an in-session solution click would
              // otherwise revert this switch to the old solution the instant selectedSolutions changes.
              setMarkedSolution(cheapest);
              setSelectedSolutions(cheapest);
              // The overlay is lifted by the select-solution completion (navSectionsUpdated), NOT here, so it is not
              // removed while the re-price this commit triggers is still in flight (showing the old price as final).
              awaitingMachineChangeRepriceRef.current = true;
            })
            .catch(() => {
              if (machineChangeTokenRef.current !== token) { return; }
              setReselectingForMachineChange(false);
            });
        }
      });
    };

    const onPrintingChanged = onMachineChanged('printing');
    const onDieCuttingChanged = onMachineChanged('die_cutting');
    document.addEventListener('quote_builder.printing_machine_user_price_id_changed', onPrintingChanged);
    document.addEventListener('quote_builder.die_cutting_machine_user_price_id_changed', onDieCuttingChanged);
    return () => {
      document.removeEventListener('quote_builder.printing_machine_user_price_id_changed', onPrintingChanged);
      document.removeEventListener('quote_builder.die_cutting_machine_user_price_id_changed', onDieCuttingChanged);
    };
  }, [navSectionIndex, stageView, inWizard, listing, selectedSolutions, buildSolveUrl, setSelectedSolutions]);

  // Lift the machine-change "recalculating" overlay only once the re-price the commit triggered has LANDED:
  // SelectedSolutionSections dispatches navSectionsUpdated (on window) after its select-solution completes. Clearing
  // it at commit time instead left the OLD price on screen as final for the frame before the re-price returned.
  useEffect(() => {
    const onNavSectionsUpdated = (event) => {
      const detail = event.detail || {};
      if (detail.sourceIndex !== navSectionIndex || !awaitingMachineChangeRepriceRef.current) { return; }
      awaitingMachineChangeRepriceRef.current = false;
      setReselectingForMachineChange(false);
    };
    window.addEventListener('navSectionsUpdated', onNavSectionsUpdated);
    return () => window.removeEventListener('navSectionsUpdated', onNavSectionsUpdated);
  }, [navSectionIndex]);

  // FAILSAFE for a re-price that never lands. The lift above waits on navSectionsUpdated, which the select-solution
  // completion fires only on SUCCESS; a failed select-solution POST clears the selection (SelectedSolutionSections
  // sets selectedSolutions to null on its catch) and dispatches nothing. Without this, awaiting/reselecting would
  // stay set and the stale "recalculating" dim would later cover a fresh pick. When the selection is lost while a
  // re-price is awaited, drop the overlay state.
  useEffect(() => {
    if (awaitingMachineChangeRepriceRef.current && !hasSelection(selectedSolutions)) {
      awaitingMachineChangeRepriceRef.current = false;
      setReselectingForMachineChange(false);
    }
  }, [selectedSolutions]);

  // OPENING A TAB THAT ALREADY HAS A CHOICE SCROLLS TO IT. A section with a picked manufacturing line or a
  // marked solution can hold rows of cards, and the chosen one sorts wherever its score puts it -- so the tab
  // opened at the top of a list with nothing on screen saying which card is the answer, and the reader had to
  // hunt for the outlined one.
  //
  // ONCE per arrival, tracked in scrolledForStageRef: the mark also changes while the reader is on the tab
  // (that is what picking is), and scrolling then would pull the page out from under the click. Coming back to
  // a stage is a fresh arrival and lands on the choice again -- see #scrollToSelectedCard.
  //
  // NOT while it is already on screen. Entering a tab whose selection is visible and having the page glide
  // anyway reads as the page moving for its own reasons; #cardNeedsScrolling asks first.
  //
  // The effect deliberately has NO dependency list. The cards arrive asynchronously -- the listing, then the
  // solve -- so there is no one value that means "the marked card is in the document now"; running after every
  // render and stopping at the ref costs a querySelector until the card appears, and nothing after that.
  // HAS THE SOLUTION LIST FINISHED RANKING? Its cards are re-ordered by a DOM sorter on a 200ms interval
  // (#sortSolutionsByScoreAndDrawStars), because each card fetches its OWN score and they land one at a time --
  // so the marked card MOVES until the last one is in. Measured on the reported quote, arriving straight at the
  // Solution tab: the marked card sat at the top until the ranking ran and dropped it 1765px, long after its
  // own pixels had stopped moving. No React state says "ranked", but the cards do: data-score is 0 until a
  // card's score lands (LayoutNavSectionCombinationListList).
  //
  // Read here rather than beside the other readiness flags because it reads the DOM, which is only the drawn
  // one inside an effect.
  const solutionsRanked = () => {
    const grid = solutionsGridRef.current;
    if (!grid) { return false; }

    const cards = Array.from(grid.children);
    return cards.length > 0 && cards.every((card) => parseInt(card.getAttribute('data-score'), 10) > 0);
  };

  // AND HAS THE SORTER FINISHED PLACING THEM? "Ranked" only means every score is in -- NOT that the cards are in
  // their final places. A warm return to the tab keeps the cached scores (every data-score already > 0) while the
  // grid re-mounts in listing order, and the 200ms sorter then re-orders it, sliding the marked card. So the grid
  // is revealed (below) only once the scores are in AND the DOM already sits in the sorter's ascending-score order
  // -- nothing left for the next tick to move. Reads the DOM, so, like #solutionsRanked, it is called from effects.
  const solutionsSettled = () => {
    const grid = solutionsGridRef.current;
    if (!grid) { return false; }

    const cards = Array.from(grid.children);
    if (cards.length === 0) { return false; }
    const scoreOf = (card) => parseInt(card.getAttribute('data-score'), 10);
    if (!cards.every((card) => scoreOf(card) > 0)) { return false; }
    return cards.every((card, index) => index === 0 || scoreOf(cards[index - 1]) <= scoreOf(card));
  };

  // KEEP #solutionGridRanked (the reveal flag) IN STEP WITH THE DOM: revealed once the grid has settled, or once
  // the floor has forced it. Deliberately a PURE read -- solutionsSettled() and the forced flag both return the
  // same value between renders unless the DOM actually changes, so the setState below settles in a render or two
  // and never loops (a time comparison here would change every render and blow the update depth). When the grid is
  // gone (another stage, or nothing picked) it also clears the stale force, so a floor that fired earlier cannot
  // reveal an unsettled grid on the next return to the tab.
  const settleSolutionGridReveal = () => {
    // Only the Frank SOLUTION STAGE (the page tab and the wizard's solution step) holds its grid -- the legacy
    // stacked view (no stageView) renders the same grid but is left exactly as it was, and the other stages draw
    // no solutions grid at all. Inert elsewhere, so nothing on those paths sets this state in a loop.
    if (stageView !== 'solution' || !pickedList || !solutionsGridRef.current) {
      solutionRevealForcedRef.current = false;
      setSolutionGridRanked((wasRanked) => (wasRanked ? false : wasRanked));
      return;
    }
    const revealed = solutionRevealForcedRef.current || solutionsSettled();
    setSolutionGridRanked((wasRanked) => (wasRanked === revealed ? wasRanked : revealed));
  };

  // BEFORE PAINT, not after: the three stage tabs share one mounted section, so solutionGridRanked survives a tab
  // switch -- returning to the Solution tab re-mounts the grid (its cards fresh at score 0) while the flag is still
  // true from last time. A plain effect corrects that only AFTER the stale, mid-sorting grid has painted; a layout
  // effect runs before paint and reads the drawn DOM, so the grid is held hidden from the very first frame it
  // appears, never shown mid-sort. Keyed to the stage/pick/solve so it fires exactly when the grid can (re)appear
  // with fresh cards -- NOT on every render (a bare layout effect that setState-s each commit blows the update
  // depth). The async scores landing later are picked up by the sort effect's timer, which drives the reveal.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- settleSolutionGridReveal reads only refs and the live
  // DOM besides these three, so those are the only inputs that change WHAT it should hide.
  useLayoutEffect(settleSolutionGridReveal, [stageView, pickedList, solutionGroups]);

  // IS THE LIST FINISHED -- both arriving and ordering itself? Until it is, the marked card is still liable to
  // move: the Solution tab draws what it has while the solve is out, and the cards re-order themselves as the
  // scores land. Measured on the reported quote, arriving straight at the Solution tab: the marked card sat at
  // the top until the ranking dropped it 1765px, long after its own pixels had stopped moving.
  const listReady = () => {
    if (!listSettledRef.current) { return false; }

    return stageView === 'solution' ? solutionsRanked() : true;
  };

  const cardNeedsScrolling = (card) => {
    const box = card.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    return box.top < 0 || box.bottom > viewportHeight;
  };

  const glideTo = (card) => {
    if (!cardNeedsScrolling(card)) { return; }

    // A reader who has asked for less motion gets the same landing without the glide, the way Frank's own
    // stylesheet answers that preference for its skeletons and progress bars.
    const stillness = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    card.scrollIntoView({ behavior: stillness ? 'auto' : 'smooth', block: 'center' });
  };

  // NOT ON THE CARD'S FIRST FRAME. A solution card ARRIVES SMALL and grows as its score panel and pictures land
  // -- measured on the reported section (work/frank_scroll_card_growth_probe.mjs): 122px tall at 699ms, 864 at
  // 820, settling at 889 by 953. Both questions this asks are answered wrong against that first frame: a
  // 122px card sits inside the viewport, so "is it already on screen?" says yes and nothing scrolls, and
  // centring it would centre a card that is about to be seven times taller.
  //
  // So watch its height until it stops changing, then decide (SETTLED_FRAMES / SETTLE_FRAME_LIMIT).
  const glideWhenSettled = (card) => {
    if (typeof window.requestAnimationFrame !== 'function') { glideTo(card); return; }

    // THE READER'S OWN SCROLLING WINS -- but only the READER's. Settling takes a moment (about a second on the
    // Solution stage, where the cards are fetched and then grow), and someone who starts moving in that window
    // has said where they want to be; gliding then takes the page off them.
    //
    // Read from their INPUT, never from window.scrollY. Comparing scroll positions cannot tell a person from
    // the page itself: arriving at the Solution tab from Manufacturing Line starts this watch while the glide
    // to the manufacturing line card is STILL ANIMATING (measured on the reported quote: scrollY climbing
    // 26 -> 51 -> 67 -> 84 -> 103 ...), so every such arrival read as "the reader scrolled" and stood down --
    // which is exactly the tab that was reported as not scrolling. A page shrinking under a fixed scroll
    // position clamps it the same way.
    let readerMoved = false;
    const readerInput = (event) => {
      // A keystroke only counts if it is one that scrolls -- typing in the machine filters must not stand this
      // down.
      if (event.type === 'keydown' && !READER_SCROLL_KEYS.includes(event.key)) { return; }
      readerMoved = true;
    };
    READER_INPUT_EVENTS.forEach((name) => window.addEventListener(name, readerInput, { passive: true }));
    const stopListening = () => {
      READER_INPUT_EVENTS.forEach((name) => window.removeEventListener(name, readerInput));
    };
    watchTeardownRef.current = stopListening;

    const stopWatching = () => { watchFrameRef.current = null; };
    const release = () => {
      stopListening();
      watchTeardownRef.current = null;
    };
    const finish = () => { stopWatching(); release(); };

    // THE BEAT BEFORE THE PAGE MOVES. Two things want it. The reader has just clicked a tab, and a page that
    // glides in the same instant reads as having jumped rather than travelled. And the ordering is not final
    // the moment the last score lands: the cards are re-ordered by a sorter on a 200ms interval
    // (#sortSolutionsByScoreAndDrawStars), so a card can still be moved one tick AFTER its own box went still.
    // GLIDE_DELAY_MS covers one full tick, and the position is measured when it fires rather than now, so the
    // glide goes to where the card ENDED UP.
    //
    // The reader can still take it back during the beat -- their input is listened for until it fires -- and a
    // card taken out of the document in the meantime is left alone.
    const glideAfterTheBeat = () => {
      stopWatching();
      glideTimerRef.current = window.setTimeout(() => {
        glideTimerRef.current = null;
        release();
        if (!readerMoved && card.isConnected) { glideTo(card); }
      }, GLIDE_DELAY_MS);
    };

    let lastShape = '';
    let stillFrames = 0;
    let framesWatched = 0;
    let framesWaitingForList = 0;

    const watch = () => {
      // THE CARD CAN BE REPLACED UNDER THE WATCH -- a fresh solve re-renders the grid, and the element this was
      // started on is then detached. A detached node measures 0x0, which reads as "settled, and already on
      // screen", so the glide would be skipped for good. Give the arrival back instead, and the next render
      // starts a watch on the card that replaced it.
      if (!card.isConnected) {
        scrolledForStageRef.current = null;
        finish();
        return;
      }

      if (readerMoved) { finish(); return; }

      // Nothing is measured until the list has stopped changing under the card -- see #listReady. Waiting is
      // bounded separately from settling, because a slow solve is not a card that will not settle.
      if (!listReady()) {
        stillFrames = 0;
        framesWaitingForList += 1;
        if (framesWaitingForList >= LIST_READY_FRAME_LIMIT) { finish(); return; }
        watchFrameRef.current = window.requestAnimationFrame(watch);
        return;
      }

      // WHERE IT IS as well as HOW BIG IT IS. A card can reach its final height while still MOVING, because
      // the cards above it are growing -- and gliding then lands where the card used to be. Measured on the
      // reported quote: the direct arrival scrolled 291px to a card that ended up 1737px further down.
      const box = card.getBoundingClientRect();
      const shape = `${Math.round(box.top)}:${Math.round(box.height)}`;
      stillFrames = shape === lastShape ? stillFrames + 1 : 0;
      lastShape = shape;
      framesWatched += 1;

      if (stillFrames >= SETTLED_FRAMES || framesWatched >= SETTLE_FRAME_LIMIT) {
        glideAfterTheBeat();
        return;
      }
      watchFrameRef.current = window.requestAnimationFrame(watch);
    };

    watchFrameRef.current = window.requestAnimationFrame(watch);
  };

  // A watch outlives the render that started it, so it has to be called off when the section goes away -- a
  // page that has moved on must not be scrolled by a card that is no longer on it.
  useEffect(() => () => {
    if (watchFrameRef.current && typeof window.cancelAnimationFrame === 'function') {
      window.cancelAnimationFrame(watchFrameRef.current);
    }
    if (glideTimerRef.current) { window.clearTimeout(glideTimerRef.current); }
    if (watchTeardownRef.current) { watchTeardownRef.current(); }
  }, []);

  const scrollToSelectedCard = () => {
    // The stage AND the section: when the page switched section without remounting this component, the stage
    // alone called an arrival at another section's list "already done".
    const arrival = `${stageView}:${navSectionIndex}`;

    // ARRIVING SOMEWHERE ELSE RE-ARMS THIS -- including coming BACK to a stage that was scrolled for earlier,
    // which is a fresh arrival and should land on the choice again. Noticed here rather than in an effect of
    // its own, before the stage is even looked at, so a trip through Pricing counts as leaving.
    if (lastArrivalRef.current !== arrival) {
      lastArrivalRef.current = arrival;
      scrolledForStageRef.current = null;
    }

    // The PAGE's tabs only. The guided setup wizard walks the same stages inside a modal (it passes stageView
    // too), but there the reader is choosing as they go rather than returning to a choice already made, and the
    // modal has its own scrolling to answer for.
    if (inWizard) { return; }
    if (stageView !== 'line' && stageView !== 'solution') { return; }
    if (scrolledForStageRef.current === arrival) { return; }

    // Whether the LIST is finished is asked inside the watch (#listReady), not here: it can become true without
    // this component re-rendering at all -- a card writes its own score into its OWN state, so the parent is
    // never told and this pass would not run again to notice.
    const card = sectionRootRef.current && sectionRootRef.current.querySelector('[data-selected-card]');
    if (!card) { return; }

    // Marked as done as the watch STARTS, not when it scrolls: every render until the card settles would
    // otherwise start a watch of its own.
    scrolledForStageRef.current = arrival;
    glideWhenSettled(card);
  };

  useEffect(scrollToSelectedCard);

  // THE PLAN THE CHOSEN SOLUTION WAS MADE ON, derived from the chosen CARDS rather than read from `pickedList`.
  // A list's identifier is exactly its manufacturing lines' identifiers in cover order
  // (Frank::ManufacturingLineList#identifier) and every card carries the line it was solved on, so the plan is
  // a property of the selection itself. It has to be: `pickedList` is page state, and the two ways a priced
  // section reaches the screen without one are both normal -- a link straight into a priced section never
  // picked anything, and switching section clears the pick while the selection is restored from the server.
  //
  // ALL OR NOTHING: a card with no identifier (a selection made on the legacy page, which stamps none) makes
  // the whole thing null. Dropping the empty ones instead would turn "I know some of the lines" into a
  // complete-looking identifier -- and on a two-part section that shortened string can MATCH a different,
  // single-line plan in the listing, which would then be drawn as the plan and scoped the scoring.
  const pricedLineIdentifiers = hasSelection(selectedSolutions)
    ? selectedSolutions.map((solution) => solution.diecutting_result.manufacturing_line_identifier)
    : [];
  const pricedListIdentifier = pricedLineIdentifiers.length > 0 && pricedLineIdentifiers.every(Boolean)
    ? pricedLineIdentifiers.join('|')
    : null;

  // On a reopened PRICED page section, solve for the plan the section is ACTUALLY priced on -- not a stale
  // manufacturing_line_<index> left in the URL by an earlier pick. Runs ONCE, so a later in-session pick is left
  // alone. This keeps the Solution tab listing the priced plan's solutions (with the priced one selected) on
  // reload, and the Manufacturing Line tab highlighting the right plan. Not in the wizard, which drives its own
  // pick and deliberately ignores the restored selection.
  const syncedPricedPickRef = useRef(false);
  useEffect(() => {
    // Page tabs only: the legacy stacked view (no stageView) and the wizard (inWizard) keep their own behaviour.
    if (!stageView || inWizard || syncedPricedPickRef.current) { return; }
    if (!hasSelection(selectedSolutions) || !pricedListIdentifier) { return; }
    syncedPricedPickRef.current = true;
    if (pickedList !== pricedListIdentifier) { setPickedList(pricedListIdentifier); }
  }, [stageView, inWizard, selectedSolutions, pricedListIdentifier, pickedList]);

  // THROW AWAY A SELECTION ITS MANUFACTURING LINE NO LONGER MATCHES. Called when the user picks a plan other
  // than the one the current selection is priced from; a no-op when nothing is selected, so browsing the
  // listing before choosing anything costs nothing.
  //
  // The SAME reset the section's own "start again" does (LayoutCombinationsFinder::ResetSelectedLists via
  // resetSelectedListHandler), rather than a second way of unselecting: it clears selected_lists server-side,
  // refreshes window.user_quote_nav_sections, and publishes navSectionsReset -- which is what takes the linked
  // sections (a corrugated section inherits this one's solution) and the solution_<index> address bar
  // parameters with it. Local state is cleared too, so nothing on screen outlives the plan it belonged to.
  // NOTHING IS CLEARED UNTIL THE SERVER HAS ACTUALLY CLEARED IT. A reset that fails and a screen that has
  // already forgotten the selection is the worst of both: the section reads as unpriced while the quote still
  // holds the old solution, and the next reload contradicts it -- restoring that solution AND, with it, the
  // pick the user had just changed, which is the very bug this discard exists to stop. So the local state goes
  // in the success callback, and a failure is reported rather than swallowed (a 500 tracks itself inside
  // PostRequest; a network failure would otherwise leave no trace at all).
  const discardSelectionForChangedPlan = useCallback(() => {
    if (!hasSelection(selectedSolutions) && !hasSelection(markedSolution)) { return; }

    resetSelectedListHandler({
      index: navSectionIndex,
      setSelectedList: setSelectedSolutions,
      onSuccess: () => {
        // The priced pick is not synced back over the new one: the selection it would restore is now gone.
        syncedPricedPickRef.current = true;
        setMarkedSolution(null);
        setPricedPlanData(null);
      }
    }).catch((error) => {
      trackEvent('js_error_happened', {
        exception: `[ManufacturingLineLists#discardSelectionForChangedPlan] ${error && error.message ? error.message : error}`,
        nav_section_index: navSectionIndex,
        user_quote_id: window.user_quote_id
      });
    });
  }, [selectedSolutions, markedSolution, navSectionIndex, setSelectedSolutions]);

  // THE ONE PLAN THE PAGE IS ABOUT, if any: the picked one while a solution is being chosen, the priced one
  // once it has been. Everything scoped to "the plan on screen" -- which lines get scored, which lists the star
  // ratings are a ratio within, which card is drawn -- follows this rather than the pick alone. Scoring did not,
  // which is why a priced section opened from a link scored every plan in the listing for a listing it never
  // shows.
  //
  // Once a solution is CHOSEN it is the derived plan or nothing -- never `pickedList`. The pick is whatever
  // manufacturing_line_<index> happens to say in the address bar, which on a deep link into a section priced
  // from a legacy selection is a plan with nothing to do with the chosen solution.
  // In the WIZARD, the "plan on screen" is always the marked pick -- the wizard drives selection through its own
  // markedSolution and does the pricing itself on Done, so a `selectedSolutions` restored from an already-priced
  // section (a reopened quote) must NOT take over here: doing so scoped scoring to the old plan and, worse,
  // blocked the solutions fetch (see the solve effect), which is why a freshly picked plan showed "no solutions".
  // A LINKED SECTION'S PLAN IS ALWAYS THE INHERITED ONE, on every tab. The `!stageView` condition below exists
  // because a tabbed section is mid-flow and its pick drives the view; a linked section never picks, so
  // pickedList is null for it and reading it would leave every tab with no plan at all -- which is what showed
  // the solution card under "Manufacturing Line" and no manufacturing line anywhere.
  const planOnScreen = isLinked
    ? pricedListIdentifier
    : ((hasSelection(selectedSolutions) && !stageView) ? pricedListIdentifier : pickedList);

  // WHAT THE PAGE LISTS -- built HERE, not handed over expanded. The server sends the section's STRUCTURE (each
  // part's manufacturing lines + the exact covers); this keeps the section's cheapest MAXIMUM_MANUFACTURING_LINE_LISTS
  // manufacturing line lists, so the unbounded product (a four-part cover of 11 manufacturing lines per part is
  // 11**4 = 14,641) never rides the wire and never renders. It re-ranks as the manufacturing line scores land. See
  // utils/manufacturingLineLists and the sibling solutions maximum, combineSolutionGroups.
  //
  // The manufacturing line list the user picked or priced STAYS, even when it is not among the cheapest: the
  // stages, the scoring scope and the priced view all look it up here, and the user must still see what they chose.
  const manufacturingLineLists = useMemo(() => {
    const cheapest = manufacturingLineListsFromListing(listing, bestScoreByLine, MAXIMUM_MANUFACTURING_LINE_LISTS);
    if (!planOnScreen || cheapest.some((list) => list.identifier === planOnScreen)) { return cheapest; }

    const chosen = manufacturingLineListOf(listing, planOnScreen);
    return chosen ? [...cheapest, chosen] : cheapest;
  }, [listing, bestScoreByLine, planOnScreen]);

  // WHAT SCORING IS FOR, and so WHAT COUNTS AS SCORED: every manufacturing line on the Manufacturing Line
  // stage, where the user compares them all; just the plan on screen everywhere else, where the other plans are
  // not drawn and scoring them made a deep link slow for nothing.
  //
  // THE STAGE DECIDES IT, so it is derived out here and taken as the best-score effect's dependency. Read only
  // inside that effect, a stage switch that WIDENS the scope did not re-run it -- and arriving at Manufacturing
  // Line from Pricing or Solution is exactly that switch, the one the guided setup wizard leaves the user to
  // make (it closes onto Pricing). The readiness test follows the stage (see listsOnScreen), so the listing then
  // waited for scores nothing had asked the server for: "Scoring manufacturing lines…" with no request in
  // flight, for as long as the user was willing to look at it.
  //
  // It also stops a pick made ON the listing from cancelling the pass that is scoring it: the scope stays null
  // there, so the batch runs to the end instead of being aborted and started again.
  const planScope = stageView === 'line' ? null : planOnScreen;

  // The one manufacturing line list scoring is narrowed to, off the Manufacturing Line stage (see planScope); null
  // when it scores the whole section, or when the structure does not offer the scoped list.
  const scopedManufacturingLineList = planScope
    ? (manufacturingLineLists.find((list) => list.identifier === planScope) || null)
    : null;

  // THE SOLUTION GROUPS -- what a card actually is.
  //
  // A ManufacturingLineList makes the WHOLE section, and it can do that with more than one manufacturing line:
  // a shirt box's plan is "Kapak on the KBA + BOBST, Kutu on the 70x100 Pedal". Those two are not two answers,
  // they are two HALVES of one answer -- so one card must show a solution for EACH part, and the card is the
  // pair. Listing each part's solutions on their own says "here are some covers and some trays" and leaves the
  // reader to do the pairing.
  //
  // So the groups are drawn from the CARTESIAN PRODUCT across its parts, copied from the legacy listing's
  // handlePartsMode:
  //     one part  -> [[s1], [s2], ...]                 (a group of one; every solution is already whole)
  //     two parts -> [[kapak1, kutu1], [kapak1, kutu2], ...]
  //
  // Only the cheapest MAXIMUM_SOLUTION_GROUPS groups are kept, because the full product is unreadable
  // (35 Covers x 30 Boxes is 1,050 cards). Cheapest needs the score -- which now arrives WITH the solution
  // (score_data, computed server-side by Frank::SolutionScore), so this is a plain SYNCHRONOUS sort, no
  // per-solution round trip. See combineSolutionGroups for what a cost cutoff systematically hides.
  //
  // WARMING THE CACHE (not an XHR): the reused legacy solution card reads its score from the shared score cache
  // (LayoutNavSectionCombination#getScore checks the cache before fetching). Priming that cache from the
  // embedded score_data is what lets Frank reuse the legacy card WITHOUT its per-card fetch to
  // /layouts-nav-section-combination-score -- the card finds every score already answered.
  //
  // A part with NO solutions collapses the whole product to nothing, correctly: if the Kutu cannot be made on
  // its assigned machine, the PLAN cannot make the section, however many ways the Kapak has.
  const buildSolutionGroups = (data) => {
    primeSolutionScores(data);
    return solutionGroupsOf(data);
  };

  const toggleFilter = (key) => setDisabledFilters((previous) => (
    previous.includes(key) ? previous.filter((each) => each !== key) : [...previous, key]
  ));

  // Tick or untick one machine type. The CURRENT selection comes from the server (it applied the defaults), so
  // the first tick starts from what is actually on screen rather than from an empty set.
  const toggleMachineType = (key, type, currentlySelected) => setSelectedTypes((previous) => {
    const selected = previous[key] || currentlySelected;
    const next = selected.includes(type) ? selected.filter((each) => each !== type) : [...selected, type];
    return { ...previous, [key]: next };
  });

  // A SECTION SWITCH commits at once: its listing is not something the viewer asked for with Search, it is what
  // the section IS, so it must not sit behind a button. Every other commit comes from Search (#runSearch).
  useEffect(() => {
    if (committedSectionRef.current === navSectionIndex) { return; }

    committedSectionRef.current = navSectionIndex;
    setCommittedFilterKey(pendingFilterKey);
  }, [navSectionIndex, pendingFilterKey]);

  // Commit the typed area-ratio percent -- on blur or Enter, so the expensive re-list / re-score runs once the
  // admin has finished a value, not on every keystroke. A blank or non-numeric entry (or a negative, which is
  // not a "percent larger") is rejected: the field snaps back to the committed value and nothing re-fetches. A
  // valid value that has not changed does not re-fetch either -- React bails on the same state.
  const commitAreaRatio = () => {
    const parsed = Number(areaRatioInput);
    if (areaRatioInput.trim() === '' || Number.isNaN(parsed) || parsed < 0) {
      setAreaRatioInput(String(areaRatioPercent));
      return;
    }
    setAreaRatioPercent(parsed);
    setAreaRatioInput(String(parsed));
  };

  // The bobbin checkbox changes the section's material sheets, and the manufacturing lines are found over
  // those sheets -- so re-fetch when the bobbin commits, or the lines shown would be the ones for the previous
  // sheets. A machine-type or reduction toggle re-fetches for the same reason (the reduction happens
  // server-side, on the found lines) -- all three land here through committedFilterKey, on Search.
  useEffect(() => {
    if (!window.frank_mode || navSectionIndex === undefined) { return; }
    // A LINKED SECTION HAS NO LISTING AND ASKS FOR NONE -- its manufacturing line comes from the section that
    // leads it. It is given an EMPTY listing rather than left at null, because null means "not fetched yet" and
    // everything downstream waits on it; this section is not waiting, it is finished. Its own pricing still
    // renders below, which is the point: only the line and the solution are shared, not the prices.
    if (isLinked) { setListing(NO_LISTING_OF_ITS_OWN); return; }

    const query = new URLSearchParams();
    if (bobbinEnabled !== undefined) { query.set('bobbin_enabled', bobbinEnabled); }
    // The owner's chosen material row. It is ON the listing query because the finder and scorer both use it: the
    // material feeds the section's sheets (which lines can be found) and prices every solution the score is the
    // minimum of (Frank::Coordinator#offered_solution_cards -> MaterialScore), so BRISTOL and KROMO score the same
    // section differently. Absent (no choice) lets the server auto-pick the lowest-id row. Left off the listing
    // query, every material scored as that auto-pick -- the Skor never moved when the Malzeme changed.
    if (selectedMaterialUserPriceId) { query.set('material_user_price_id', selectedMaterialUserPriceId); }
    disabledFilters.forEach((key) => query.append('disabled_filters[]', key));
    Object.entries(selectedTypes).forEach(([key, types]) => {
      // A query string cannot carry an empty array, and "absent" already means "use the default" -- so an
      // empty selection is sent as one blank entry, which the server reads as "nothing ticked".
      if (types.length === 0) {
        query.append(`${key}[]`, '');
        return;
      }
      types.forEach((type) => query.append(`${key}[]`, type));
    });
    // The owner's chosen carton GSM (a carton_lamination section only) -- it sets the printing material's
    // thickness, so it IS on the listing query (it changes which printing machines are found), unlike the
    // material choice above. Absent when none is picked, so the server applies its default (300).
    if (selectedCartonGsm !== null) { query.set('carton_gsm', selectedCartonGsm); }    // The admin's area-ratio cap, so the listing is reduced against the same value the solve and scoring use.
    query.set('area_ratio_percent', areaRatioPercent);
    // The listing itself is count-independent (it lists which plans CAN make the section, not their cost), but
    // count is sent so a quantity change re-fetches it and so re-mints the `listing` object the best-score effect
    // keys off -- which is what re-triggers the (count-dependent) scoring. It also keeps the cache key uniform:
    // count rides in the URL on all three fetches. The server ignores it here.
    setCountParam(query, count);
    // Which account this listing is found and scored against -- keyed into the request so a Standard listing is
    // never reused for an Own one (and the server answers the source asked for).
    appendManufacturingLinesSource(query);
    const queryString = query.toString();

    // superseded guards against out-of-order responses: a slow earlier fetch must not land its (stale) listing
    // after a later one -- which would then drive the best-score batch over sheets it was not listed with. It
    // also guards the lock: only the LATEST fetch clears it, so a superseded early answer cannot unlock the
    // filters while the real re-list is still running.
    let superseded = false;
    let roundWaitTimer = null;
    setIsRefetchingListing(true);
    // A committed change starts a NEW search, so whatever was cancelled before is over -- the block locks again.
    setSearchCancelled(false);
    // ...and so is whatever the last one failed with. Held until here rather than cleared when the failure is
    // drawn, so the message stays on screen until something is actually being done about it.
    setListingUnavailable(null);

    const waitBetweenRounds = (milliseconds) => new Promise((resolve) => {
      roundWaitTimer = setTimeout(resolve, milliseconds);
    });
    const cancelRoundWait = () => { if (roundWaitTimer) { clearTimeout(roundWaitTimer); roundWaitTimer = null; } };

    // What Cancel does to this pass: the answer still arrives (fetch cannot be unsent here), but it is dropped
    // like a superseded one, so a cancelled search cannot land its listing minutes later over whatever the
    // viewer moved on to. The pause between rounds goes too -- that timer is the section's own, not the
    // request's, so nothing else would stop it waking up to ask for a listing nobody is waiting for.
    cancelListingRef.current = () => { superseded = true; cancelRoundWait(); setIsRefetchingListing(false); };

    const listingUrl = `/mcc/${window.user_quote_id}/manufacturing-line-lists/${navSectionIndex}${queryString ? `?${queryString}` : ''}`;
    // How long this search may go on asking, and from when. Read off the page so a test can reach the reported
    // state at once; see LISTING_PATIENCE_MILLISECONDS for what the length is for.
    const patience = window.frank_listing_patience_milliseconds ?? LISTING_PATIENCE_MILLISECONDS;
    const startedAt = Date.now();
    // A REFUSAL is not worth asking about again: a 4xx is the request being turned down -- an expired session, an
    // ask the endpoint does not take -- and the next one is refused identically. Everything else says nothing
    // about whether the listing is coming, so it is asked for again until the patience above is spent.
    const worthAskingAgain = (failure) => !failure.permanent && Date.now() - startedAt < patience;

    // NOTHING MORE IS COMING, so the section says so: the message, the server's own reason for whoever can act on
    // one, and a Try again that spends another round of patience. The last resort, not the first answer.
    const reportFailure = (failure) => {
      setIsRefetchingListing(false);
      setListingUnavailable(failure.message);
      // Nothing landed, so this search has no answer to blink for. Left armed, the blink would fire over the
      // NEXT listing to arrive -- a quantity change, a section switch -- claiming a search nobody pressed.
      awaitingSearchResultsRef.current = null;
    };

    // Whether the viewer pressed "Ara" / "Try again" for THIS re-list. Consumed here -- this effect runs on every
    // re-list, even a cache-hit one where the fetch short-circuits -- so the flag never survives to leak into a
    // later, unpressed search (a quantity or tab-return re-list). The pressed intent reaches the scoring pass by
    // stamping the listing this search produces (freshlyFetchedListingRef, below), not by leaving the flag set.
    const freshSearch = freshSearchRef.current;
    freshSearchRef.current = false;

    // ONE ROUND OF ASKING FOR THE LISTING. Through the response cache, keyed by this URL (count is in it): an
    // identical listing the wizard (or an earlier visit) just fetched is reused instead of re-fetched. See
    // ManufacturingLineResponseCache.
    const askForTheListing = (betweenRounds, refresh) => {
      if (superseded) { return Promise.resolve(); }

      // WHY this round could not be answered, kept for the reader -- see #listingUnavailable. Taken from the
      // failing ask rather than inferred from the null below, because only it carries the server's own reason,
      // and in development that reason is the one thing that says what to do about it. Held here rather than put
      // on screen, because a round that is going to be asked again is not news the reader has to receive.
      //
      // `refresh` drops a stuck claim so a retry (or an "Ara" press) queues the listing afresh -- it flows through
      // fetchJsonCached to fetchSectionAnswer, on its first poll only. A listing already in the response cache is
      // returned before it is looked at, which is what keeps an unedited Search silent.
      let unavailable = null;
      return fetchJsonCached(listingUrl, {
        onUnavailable: (error) => { unavailable = error; },
        refresh: refresh,
      })
        .then((data) => {
          if (superseded) { return undefined; }
          // No listing and no rejection: the server answered that it could not work this out. The reason came
          // through onUnavailable above -- and if even that is missing, the ask still has no answer, so it is
          // asked again rather than read as a section with no manufacturing lines.
          if (!data) {
            return askAgainOrReport(unavailable || new Error('the section answered with no listing'), betweenRounds);
          }

          // A pressed search stamps the listing it produced, so the scoring pass that keys off this exact object
          // (and no later, unpressed one) asks its first round afresh too.
          if (freshSearch) { freshlyFetchedListingRef.current = data; }
          setListing(data);
          setIsRefetchingListing(false);
          // A LISTING LANDED, whatever it turned out to be. Counted rather than inferred from the listing itself,
          // because a search that answers with the very listing already on screen changes NOTHING a watcher could
          // key on: the response cache returns the same object, so setListing bails out, the scoring pass skips
          // (it has scored this exact listing), and not one request is sent. That is the whole shape of an
          // unedited Search -- correct, instant, and completely silent, which is what read as "Search does
          // nothing". This counter is what the blink hangs off, so an answer arriving is observable even when the
          // answer is the same one.
          setListingLandedCount((previous) => previous + 1);
          return undefined;
        })
        .catch((error) => {
          // A request that never arrived at all (a network or parse error, which rejects rather than resolving to
          // no listing). Logged every round, because each is a real failure even when the next one heals it.
          console.error('Error fetching manufacturing line lists:', error);
          if (superseded) { return undefined; }

          return askAgainOrReport(error, betweenRounds);
        });
    };

    // A FAILED ASK IS NOT A FINDING ABOUT THE SECTION -- it is one request that did not come back, and the next
    // one usually does. So the section asks again itself, and NOTHING ON SCREEN CHANGES WHILE IT DOES: the
    // progress bar it was already showing stays up, because the section really is still working on the answer.
    // That is how the scores have always recovered (askAboutLines); the listing used to report the first failure
    // and hand the reader a button to do what the page could do itself.
    const askAgainOrReport = (failure, betweenRounds) => {
      if (!worthAskingAgain(failure)) { reportFailure(failure); return undefined; }

      // NEVER PAUSE PAST THE PATIENCE. The pause says how long to leave a struggling server alone, not how much
      // time to add on top of the minute -- and uncapped it does add it, because the round that will overrun is
      // started before anything measures it. Measured in the browser with every ask failing: rounds at 0s, 2s,
      // 8s and 26s, then a 54s pause, so the section was still sweeping at 75 seconds with the answer long
      // since decided. Capped, the last round starts at the edge of the minute and the report follows it.
      const remainingPatience = patience - (Date.now() - startedAt);
      // A RETRY ASKS AFRESH: the last round could not get the listing in time, so its claim is treated as stuck and
      // dropped rather than joined again.
      return waitBetweenRounds(Math.min(betweenRounds, remainingPatience)).then(() => askForTheListing(
        Math.min(betweenRounds * BETWEEN_ROUNDS_GROWTH, MAXIMUM_BETWEEN_ROUNDS_MILLISECONDS), true
      ));
    };

    // The FIRST round asks afresh only when the viewer pressed "Ara" / "Try again" (freshSearch, consumed above).
    askForTheListing(FIRST_BETWEEN_ROUNDS_MILLISECONDS, freshSearch);

    return () => { superseded = true; cancelRoundWait(); cancelListingRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the body reads the LATEST filter values, which by
    // the time committedFilterKey changes ARE the committed ones (Search commits them together); so
    // committedFilterKey is the trigger, not bobbinEnabled/disabledFilters/selectedTypes directly.
    // searchRunNumber is a trigger of its own, for a Search that commits the SAME filters (re-running after a
    // cancel). areaRatioPercent is another (committed on blur / Enter). count is a trigger too: a quantity
    // change re-fetches the listing, which re-mints the `listing` object the scoring effect keys off, so the
    // (count-dependent) best scores are recomputed for the new quantity.
  }, [navSectionIndex, committedFilterKey, searchRunNumber, areaRatioPercent, count]);

  // The picked plan's SOLUTIONS: how this plan would actually make the section. Fetched only once a plan is
  // picked, because the solve is the expensive half -- the listing above answers without it, which is why the
  // cards can appear long before any solution does.
  //
  // The same inputs the listing was fetched with are sent, so the plan is solved with the very sheets and
  // filters it was listed with: a plan identifier only means anything against a given listing.
  useEffect(() => {
    // The pricing view does not show the solutions grid, so there is nothing to solve there -- and re-solving
    // when a selection lands would be wasted work. Keep whatever groups were already fetched.
    // SKIPPED, NOT CANCELLED: `stageView` is a dependency of this effect (see the list below), so leaving the
    // pricing view runs it again and the solve this stage declined to make is made on arrival at the Solution
    // tab. Without that, a section whose FIRST render is the pricing view -- which is where closing the guided
    // setup wizard leaves the user (FrankQuoteBuilderPage#closeWizard opens the priced section's Pricing stage)
    // -- never solved at all, and its Solution tab fell through to the priced fallback below and listed the
    // chosen solution ALONE, as the only way this plan makes the section. Measured on quote 2706
    // (work/reproduce_mcc_single_solution_after_wizard.mjs): "Solutions (1)", zero /solutions/ requests, for a
    // plan the server answers with 8.
    if (stageView === 'pricing') { return; }
    // No plan picked, or a solution already CHOSEN: either way there is nothing to solve. Reopening a priced
    // section still had its plan in the address bar, so it fired a full solve whose answer was then never
    // rendered -- frank.contract calls the solve the expensive half of the page.
    //   In a 'line'/'solution' view, selection runs through markedSolution, not selectedSolutions, so a
    //   `selectedSolutions` restored from an already-priced section must NOT block the solve -- otherwise a
    //   plan the user just picked shows "no solutions" because the solve never ran.
    const chosenBlocksSolve = hasSelection(selectedSolutions) && !stageView;
    // A linked section solves nothing of its own -- it inherits.
    if (!window.frank_mode || navSectionIndex === undefined || !pickedList || chosenBlocksSolve || isLinked) {
      setSolveData(null);
      return;
    }

    const solveUrl = solutionsUrlFor(pickedList, {
      userQuoteId: window.user_quote_id, navSectionIndex, bobbinEnabled, selectedMaterialUserPriceId,
      selectedCartonGsm, disabledFilters, selectedTypes, areaRatioPercent, count
    });

    let superseded = false;
    // ONLY WHEN THERE IS SOMETHING TO WAIT FOR. An answer already in the response cache still resolves through a
    // promise, so flipping this on unconditionally puts the skeleton over a grid whose cards are ready to draw --
    // one blank frame every time the user arrives at the Solution tab from another stage, now that the stage
    // re-runs this effect. It is not only a flicker: the chosen solution is read as "on screen" only while
    // nothing is loading (see pricedSolutionOnScreen), so that frame also drops the card's outline and, in the
    // wizard, disables its Done.
    if (!hasCachedResponse(solveUrl)) { setSolutionsLoading(true); }
    // Through the response cache (keyed by this URL, count included), so the plan the wizard just solved is not
    // solved again when the tab mounts, and a stage change mid-solve joins the request in flight rather than
    // making a second one. The raw solve response is cached; the solution groups are still built from it each time
    // (a cheap local transform, not the expensive solve).
    fetchJsonCached(solveUrl)
      .then((data) => {
        if (superseded) { return; }
        primeSolutionScores(data);
        setSolveData(data);
        setSolutionsLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching solutions:', error);
        if (superseded) { return; }
        setSolveData(NO_SOLUTIONS_SOLVED);
        setSolutionsLoading(false);
      });

    return () => { superseded = true; };
    // selectedSolutions is a dependency because the guard above READS it: going back from a priced section has
    // to solve the plan again, and choosing one has to stop solving. count is a dependency because it is sent in
    // the solve URL and changes the solve's scores, so a quantity change must re-solve. stageView is one for the
    // same reason it is read: this effect DECLINES to solve on the pricing view, so the stage moving off it is
    // what has to run the solve -- exactly as the stage decides the scoring scope through planScope. Re-running
    // it costs nothing when the groups are already fetched: fetchJsonCached answers an identical solve URL from
    // the response cache rather than the server.
  }, [pickedList, navSectionIndex, bobbinEnabled, disabledFilters, selectedTypes, areaRatioPercent, selectedSolutions,
    count, stageView]);

  // BEST SCORES -- so a card can sort best-first and show its score. Scoring is heavy (every solution of every
  // line, min per line, no pruning: the cheapest way is not guessable from the layout), so WHERE it runs matters:
  //   - LISTING on screen (no plan picked): score EVERY distinct line, ONCE per listing, so the cards can sort
  //     and star. One request per manufacturing line, each asking a worker until its answer lands (they no
  //     longer solve on the request), all dispatched at once through sectionAnswerQueue -- with
  //     ListingProgressOverlay's modal bar over completed/total.
  //   - ONE plan on screen -- picked, or the plan a chosen solution is priced from (a click, or a refresh /
  //     deep link straight into either view): score ONLY that plan's lines. That card needs just its OWN best
  //     score; scoring all ~42 lines there is what made a deep link slow and ran the full progress bar for
  //     nothing. The rest are scored if and when the user goes Back to the listing.
  // A line is fetched ONCE (it is reused across many lists); a list's best is the SUM of its lines' bests.
  //
  // `fullyScoredListingRef` remembers the listing object we have already scored in full, so neither picking a
  // plan from a scored listing nor going Back to it re-scores (the scores are kept) -- only a NEW listing (a
  // filter / section change) or a Back from a deep link (never fully scored) triggers the full pass.
  const fullyScoredListingRef = useRef(null);

  useEffect(() => {
    if (!window.frank_mode || navSectionIndex === undefined || !listing) { return undefined; }
    // A linked section scores nothing of its own -- it has no listing to rank.
    if (isLinked) { return undefined; }
    if (fullyScoredListingRef.current === listing) { return undefined; }

    // WHAT THIS PASS SCORES -- manufacturingLinesToScore, the ONE definition readiness (bestScoresReady) waits on
    // too. With no scoped manufacturing line list: EVERY manufacturing line the section offers, never the bounded
    // product -- the listing shows only the cheapest lists, and which ones is exactly what these scores decide.
    // planScope is the stage's own scope (see its definition): null on the Manufacturing Line stage, where the
    // user compares them all. Not only a picked list: it may equally be the list a chosen solution is priced from.
    // A scoped list the structure does not offer (dropped by the default filters, see pricedPlanData) falls
    // through to the whole listing. One list -> just its manufacturing lines, merged in; the listing -> a fresh
    // full score, which on COMPLETION marks this listing done (below) so picking / Back does not repeat it.
    const planOnScreenList = scopedManufacturingLineList;
    const scoresWholeListing = !planOnScreenList;
    const distinctLines = manufacturingLinesToScore(listing, planOnScreenList);
    if (distinctLines.length === 0) { return undefined; }

    // The SAME filter/bobbin params the listing used, so each line is scored over the very sheets it was listed
    // with.
    const filterParams = new URLSearchParams();
    if (bobbinEnabled !== undefined) { filterParams.set('bobbin_enabled', bobbinEnabled); }
    if (selectedMaterialUserPriceId) { filterParams.set('material_user_price_id', selectedMaterialUserPriceId); }
    if (selectedCartonGsm !== null) { filterParams.set('carton_gsm', selectedCartonGsm); }
    disabledFilters.forEach((key) => filterParams.append('disabled_filters[]', key));
    Object.entries(selectedTypes).forEach(([key, types]) => {
      if (types.length === 0) { filterParams.append(`${key}[]`, ''); return; }
      types.forEach((type) => filterParams.append(`${key}[]`, type));
    });
    filterParams.set('area_ratio_percent', areaRatioPercent);
    // The quantity the server scores against -- see setCountParam. Shared into every per-line URL below.
    setCountParam(filterParams, count);
    // The account every per-line and batch best-score is scored against -- shared into all of them below.
    appendManufacturingLinesSource(filterParams);

    const bestScoreUrl = (line) => {
      const query = new URLSearchParams(filterParams);
      query.set('manufacturing_line', line.identifier);
      query.set('sub_section_index', line.sub_section_index);
      return `/mcc/${window.user_quote_id}/manufacturing-line-best-score/${navSectionIndex}?${query.toString()}`;
    };

    // WHAT IS ALREADY KNOWN GOES ON SCREEN IN THIS RENDER, not a microtask later. Every cached line would
    // resolve almost immediately anyway, but "almost" is a frame with the scoring bar in it -- and after a
    // remount (leaving a section for Manage and coming back, or the guided setup wizard closing) EVERY line is
    // cached, so the user was shown "Scoring manufacturing lines…" for a listing that needed no scoring at all.
    // Seeding synchronously means a fully cached listing never shows the bar.
    const cachedScores = {};
    distinctLines.forEach((line) => {
      const url = bestScoreUrl(line);
      if (!hasCachedResponse(url)) { return; }

      const data = getCachedResponse(url);
      cachedScores[lineKey(line)] = data ? data.best_score : null;
    });

    let superseded = false;
    // Cancel a superseded batch's requests. An aborted signal makes an in-flight fetch reject AND makes a
    // still-QUEUED fetch reject immediately when the queue reaches it -- WITHOUT sending the request -- so a
    // rapid filter toggle does not pile N x 42 heavy solves on the server (each request runs a full solve).
    const controller = new AbortController();
    // How this pass gives the queue back the progress counters it is still holding -- see #releaseProgress,
    // which fills this in once they exist. Reached from both ways out (Cancel below, and the cleanup at the end
    // of this effect), so an abandoned pass releases them whichever one abandoned it.
    let releaseProgress = () => {};
    // Scoring a whole listing REPLACES what was on screen (it is a new listing), so the seed is all it keeps;
    // scoring one plan merges into the scores already there.
    if (scoresWholeListing) {
      setBestScoreByLine(cachedScores);
    } else if (Object.keys(cachedScores).length > 0) {
      setBestScoreByLine((previous) => ({ ...previous, ...cachedScores }));
    }
    // WHATEVER THE LAST PASS CONCLUDED ABOUT THESE LINES IS NO LONGER THE ANSWER: this pass is about to ask
    // again. Cleared per line rather than wholesale, so a pass scoped to one plan (a pick, the priced view)
    // leaves the rest of the listing's findings alone -- and so that the retry control, which re-runs this
    // effect, is what puts a card that could not be scored back into "being worked out".
    setUnscoredLines((previous) => {
      if (previous.size === 0) { return previous; }

      const remaining = new Map(previous);
      distinctLines.forEach((line) => remaining.delete(lineKey(line)));
      return remaining.size === previous.size ? previous : remaining;
    });
    sectionAnswerQueue.reset();

    // The pause between rounds, kept so both ways out of this pass -- Cancel below and the cleanup at the end --
    // can call it off: a pass abandoned inside it would otherwise wake up and ask about a listing nobody is
    // looking at any more.
    let roundWaitTimer = null;
    const waitBetweenRounds = (milliseconds) => new Promise((resolve) => {
      roundWaitTimer = setTimeout(resolve, milliseconds);
    });
    const cancelRoundWait = () => { if (roundWaitTimer) { clearTimeout(roundWaitTimer); roundWaitTimer = null; } };

    // What Cancel does to this pass: abort the in-flight scores AND empty the queue, so the lines not yet
    // reached are never asked for -- each is a full solve, and the whole point of cancelling is to stop paying
    // for an answer nobody is waiting for. The scores already in hand stay on their cards.
    cancelScoringRef.current = () => {
      superseded = true;
      controller.abort();
      releaseProgress();
      // AND THE PAUSE BETWEEN ROUNDS, which the abort does not reach: that timer is the section's own, not the
      // request's. Today Cancel also empties the listing, which re-runs this effect and so cancels the pause
      // through the cleanup below -- but that is Cancel's business, not this pass's, and a caller that stopped
      // the scoring WITHOUT re-listing would leave a timer to wake up and ask about a listing nobody is watching.
      cancelRoundWait();
      sectionAnswerQueue.clear();
    };

    // The manufacturing lines this pass ends without a score for -- what decides whether the listing may be
    // remembered as fully scored (below). Kept alongside the state because the state is written asynchronously
    // and this is read the moment the pass finishes.
    const unscoredKeys = new Set();

    // WRITTEN THE MOMENT ONE MANUFACTURING LINE ANSWERS, not once the whole batch has: that is what lets each
    // card fill in its own score as it arrives, instead of the listing sitting blank behind a full-screen bar
    // and then appearing all at once. A superseded batch writes nothing.
    //
    // The key is recorded even when the score is null (an unmanufacturable manufacturing line), because the
    // card has to tell "answered, cannot be made" from "not answered yet" -- they look different: an em dash
    // against a still-loading placeholder.
    const recordScore = (key, best) => {
      if (superseded) { return; }

      setBestScoreByLine((previous) => ({ ...previous, [key]: best }));
      unscoredKeys.delete(key);
      setUnscoredLines((previous) => {
        if (!previous.has(key)) { return previous; }

        const remaining = new Map(previous);
        remaining.delete(key);
        return remaining;
      });
    };

    // A MANUFACTURING LINE WITH NO SCORE, and whether anything is still trying to get one.
    //
    // The provisional null keeps the REST of the listing moving -- a plan whose own lines have answered can show
    // its score without waiting on this one -- while the entry here is what stops that null being read as an
    // answer: the card draws a placeholder rather than the em dash that means "cannot be manufactured", and the
    // section goes on reporting itself as working.
    const recordUnscored = (key, reason, asking) => {
      if (superseded) { return; }

      unscoredKeys.add(key);
      setBestScoreByLine((previous) => (
        Object.prototype.hasOwnProperty.call(previous, key) ? previous : { ...previous, [key]: null }
      ));
      setUnscoredLines((previous) => new Map(previous).set(key, { reason: reason || null, asking: asking }));
    };

    // Every manufacturing line whose score is not already in hand. One already computed for these exact inputs
    // (the wizard just scored it at this quantity -- the quantity is in the url) is taken straight from the
    // response cache: no request, no server-side solve, and it is settled below without ever being asked about.
    const outstanding = distinctLines.filter((line) => !hasCachedResponse(bestScoreUrl(line)));
    const lineByKey = new Map(distinctLines.map((line) => [lineKey(line), line]));

    // ONE unit of progress per manufacturing line, resolved as its own answer lands -- so the bar still counts
    // manufacturing lines, not requests, now that the whole section is asked for in one.
    const settle = new Map();
    // WHICH MANUFACTURING LINES ARE HOLDING A UNIT. Kept apart from `settle`, which only gains a key when the
    // queued thunk RUNS: sectionAnswerQueue runs one straight away only while it is under its cap
    // (SECTION_ANSWER_CONCURRENCY, 64), so on a section bigger than that -- and a listing can be, the catalogue
    // reaches ~101 per sub_section with the filters off -- the lines still queued would look as if they held
    // nothing. A later round would then open a SECOND unit for each, whose resolver overwrites the first, and
    // that first promise -- the one in `settled` -- would never settle: the pass would never read as finished
    // and its queue slot would never come back.
    const unitsOpened = new Set();
    // The manufacturing lines whose answer has already landed. Needed because a unit only learns how to resolve
    // itself when the queue STARTS it, so on a section past the cap an answer can arrive for a line whose unit is
    // still waiting its turn: `finish` then had nothing to call, and when the queue got round to that unit it
    // registered a resolver nobody was ever going to use. Those units stayed "in flight" for the life of the
    // page -- one of the queue's slots each, gone -- and the pass they belonged to never read as finished.
    const finished = new Set();
    const settled = distinctLines.map((line) => {
      unitsOpened.add(lineKey(line));
      return sectionAnswerQueue.enqueue(() => new Promise((resolve) => {
        if (finished.has(lineKey(line))) { resolve(); return; }

        settle.set(lineKey(line), resolve);
      }));
    });
    const finish = (key) => {
      finished.add(key);
      settle.get(key)?.();
      settle.delete(key);
      unitsOpened.delete(key);
    };

    // GIVE BACK EVERY COUNTER THIS PASS IS STILL HOLDING, when the pass is abandoned.
    //
    // Those counters are promises that settle only when their own manufacturing line answers, and an abandoned
    // pass has answers that are never coming: the poll stops the moment its signal aborts, so nothing is left
    // to call finish for the rest. A promise that never settles is, to the queue, a request still in flight --
    // RequestQueueManager decrements only when one settles, and neither reset() nor clear() releases them.
    //
    // Left behind they accumulate, and they never come back: a handful of abandoned passes (a filter change, a
    // new quantity, a section switch, Cancel -- each abandons whatever had not answered yet) is enough to hold
    // every one of SECTION_ANSWER_CONCURRENCY's slots for the life of the page. After that no counter ever
    // starts, so none is ever resolved, and the section fills its cards in behind a scoring bar stuck at
    // nothing and a pass that never reads as finished.
    releaseProgress = () => {
      settle.forEach((resolve) => resolve());
      settle.clear();
      unitsOpened.clear();
    };

    // A PROGRESS UNIT FOR EVERY MANUFACTURING LINE ABOUT TO BE ASKED ABOUT that is not already holding one. The
    // pass above opens one per line; this is for the lines a LATER round asks about again after their unit was
    // given back -- the retry control's round, which would otherwise count towards a bar that has already
    // finished and leave the section saying it is idle while it works.
    const openProgressUnits = (keys) => keys.forEach((key) => {
      if (unitsOpened.has(key)) { return; }

      unitsOpened.add(key);
      // This round has NOT been answered yet, whatever the last one concluded -- otherwise the unit opened here
      // would resolve itself the moment the queue started it (see `finished`) and the section would report itself
      // idle while this round runs.
      finished.delete(key);
      sectionAnswerQueue.enqueue(() => new Promise((resolve) => {
        if (finished.has(key)) { resolve(); return; }

        settle.set(key, resolve);
      }));
    });

    // HOW LONG THIS PASS KEEPS ASKING, and from when. Read off the page so a test (and a developer who does not
    // want to wait ten minutes) can shorten it; see SCORING_PATIENCE_MILLISECONDS for what the length is for.
    const patience = window.frank_scoring_patience_milliseconds ?? SCORING_PATIENCE_MILLISECONDS;
    const scoringStartedAt = Date.now();
    // Whether a manufacturing line this round could not get an answer for is worth asking about again.
    //
    // A REFUSAL is not: a 4xx is the request being turned down -- an expired session, an ask the endpoint does
    // not take -- and the next ask is refused identically. Everything else is silence, which says nothing about
    // whether the answer is coming, so it is asked again until the patience above is spent.
    const worthAskingAgain = (loss) => !loss.permanent && Date.now() - scoringStartedAt < patience;

    distinctLines.forEach((line) => {
      if (outstanding.includes(line)) { return; }

      const data = getCachedResponse(bestScoreUrl(line));
      recordScore(lineKey(line), data ? data.best_score : null);
      finish(lineKey(line));
    });

    const bestScoresUrl = `/mcc/${window.user_quote_id}/manufacturing-line-best-scores/${navSectionIndex}?${filterParams.toString()}`;

    // ONE ROUND OF ASKING, and what happens to the manufacturing lines it does not get an answer for.
    //
    // A poll loop that has given up has established NOTHING about the lines it was still waiting on -- it ran out
    // of patience, which is not a finding about a machine. So a lost line is asked about again, AND NOTHING ON
    // SCREEN CHANGES WHEN THAT HAPPENS: the card keeps the placeholder it already had, the progress unit it is
    // holding is not released, and the section goes on showing the progress it shows for any scoring pass. A
    // worker restarting is not news the reader has to receive; the score arriving is.
    //
    // Rounds are also how a lost job is recovered. When a worker is killed mid-job every ask inside its claim
    // (Frank::SectionAnswer::PROCESSING_TTL, five minutes) is answered 'processing' by a claim nobody is working
    // on. A round gives up on that silence at POLL_DEADLINE_MILLISECONDS -- five minutes, a claim's whole life, so
    // it never gives up on one still being computed -- and the NEXT round asks afresh (`refresh` below), which DROPS
    // the by-then-dead claim and queues the work again, so the section heals itself a round later without the viewer
    // touching anything. Only a RETRY asks afresh; the first round joins whatever is already being computed (a warm
    // start, a wizard's scores) unless the viewer pressed "Ara", which asks afresh from the first round
    // (`freshSearch` at the initial call below).
    const askAboutLines = (keys, betweenRounds, refresh) => {
      // A pass abandoned between rounds asks for nothing more. Checked BEFORE the progress units are opened,
      // because a unit opened for a pass nobody is waiting on can never be settled -- the poll that would settle
      // it is refused by the aborted signal -- and an unsettled unit holds one of the request queue's slots for
      // the life of the page. Both ways out of a pass already cancel the pause, so this is the backstop for a
      // third one.
      if (superseded) { return Promise.resolve(); }

      const lost = new Map();
      openProgressUnits(keys);

      return pollSectionBestScores(bestScoresUrl, keys, {
        signal: controller.signal,
        refresh: refresh,
        onAnswer: (key, data) => {
          // Filed under the manufacturing line's OWN url, exactly where a single ask would have put it, so the
          // response cache and its IndexedDB half go on working as they did -- a remount, or coming back to
          // this section, finds these without asking again.
          const line = lineByKey.get(key);
          if (line) { setCachedResponse(bestScoreUrl(line), data); }

          recordScore(key, data ? data.best_score : null);
          finish(key);
        },
        onLost: (key, loss) => {
          lost.set(key, loss);
          recordUnscored(key, loss.reason, worthAskingAgain(loss));
        },
      }).then(() => {
        if (superseded || lost.size === 0) { return undefined; }

        const asking = [...lost].filter(([, loss]) => worthAskingAgain(loss)).map(([key]) => key);
        // WHAT IS NOT WORTH ASKING ABOUT AGAIN is said out loud rather than left spinning: the card states that
        // it could not be scored -- never the em dash, which is a finding about the plan -- and offers to ask
        // again. Its progress unit goes back, so the section stops reporting itself as working on something
        // nothing is working on.
        lost.forEach((loss, key) => {
          if (asking.includes(key)) { return; }

          recordUnscored(key, loss.reason, false);
          finish(key);
        });
        if (asking.length === 0) { return undefined; }

        // A RETRY ASKS AFRESH: these lines have already been polled for a whole round without answering, so their
        // claims are treated as stuck and dropped rather than joined again.
        return waitBetweenRounds(betweenRounds).then(() => askAboutLines(
          asking, Math.min(betweenRounds * BETWEEN_ROUNDS_GROWTH, MAXIMUM_BETWEEN_ROUNDS_MILLISECONDS), true
        ));
      });
    };

    // The FIRST round asks afresh only when THIS listing is the one a pressed "Ara" / "Try again" just produced
    // (stamped in the listing effect). Keyed by object identity and cleared on use, so a later pick, retry, or
    // unpressed re-list of a different listing does not ask afresh.
    const freshSearch = freshlyFetchedListingRef.current === listing;
    if (freshSearch) { freshlyFetchedListingRef.current = null; }
    askAboutLines(outstanding.map(lineKey), FIRST_BETWEEN_ROUNDS_MILLISECONDS, freshSearch)
      .catch(() => { /* aborted: the cleanup below has already taken over */ });

    Promise.all(settled).then(() => {
      if (superseded) { return; }

      // This pass has finished -- what the blink waits for. Counted whether or not the numbers moved.
      setScoringCompletedCount((previous) => previous + 1);
      // Mark the listing fully scored only NOW, once the batch actually landed. Recording it at the START would
      // strand the listing scoreless if a pick interrupted the batch: the aborted run leaves bestScoreByLine {},
      // and the next run would skip on `ref === listing` and never score anything.
      //
      // And only if every manufacturing line actually GOT a score. A listing still missing one is not fully
      // scored, whatever the pass did about it -- remembering it as such would mean picking a plan and coming
      // back never asked again, and the retry control (which re-runs this effect) could not either.
      if (scoresWholeListing && unscoredKeys.size === 0) { fullyScoredListingRef.current = listing; }
    });

    return () => {
      superseded = true;
      controller.abort();
      releaseProgress();
      cancelRoundWait();
      cancelScoringRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `listing` is the join point (a filter / bobbin /
    // area-ratio / section / COUNT change mints a new listing before this should re-run, and navSectionIndex is
    // stable per mounted instance), so only `listing` and `planScope` trigger this -- not the filter values
    // (the area-ratio cap among them) or the count the body reads: a count change re-lists above, and the new
    // listing object re-triggers this pass, which then scores against that same count via the cache key.
    //
    // `planScope` rather than `planOnScreen`: what this pass has to follow is the SCOPE -- which plans are on
    // screen and so expected to answer -- and that is the stage's, not the pick's. See its definition.
    //
    // `scoringRetryNumber` is the retry control asking for this pass again (#retryUnscoredScores): a re-run asks
    // only about the manufacturing lines with no answer, since every other one is seeded from the response cache
    // without a request.
    //
    // selectedMaterialUserPriceId is NOT a trigger: the material rides the LISTING query (like carton GSM), so a
    // material change re-lists and this pass re-runs off the NEW `listing` object -- with its correct per-material
    // embedded scores. Triggering here as well would run this pass against the OLD listing (old-material scores)
    // and cache them under the NEW material's best-score URL, which the new listing's pass would then read back
    // stale.
  }, [listing, planScope, scoringRetryNumber]);

  // A filter change re-lists, and the picked plan may no longer exist in the new listing -- so drop the
  // selection rather than show solutions for a plan that is no longer on screen. Clear the best scores in the
  // SAME pass, so the cards never show the previous listing's scores against the new listing for a render (the
  // best-score effect clears them too, but only after the render that first sees the new listing).
  //
  // Skip the very FIRST run: the picked plan is restored from the URL on mount (above), and this clear must not
  // wipe that restore -- only a later filter / section change should drop the selection.
  // Keyed off committedFilterKey (not the edited values), so the current listing's scores and stars stay put
  // while the user is still ticking -- they clear in the SAME pass the re-list fires, on Search. The material
  // choice is IN committedFilterKey (see pendingFilterKey), so a Malzeme change drops the pick and clears the
  // scores through this same path, exactly as a carton-GSM change does -- no separate material dependency needed.
  const selectionResetSkipRef = useRef(true);
  useEffect(() => {
    if (selectionResetSkipRef.current) { selectionResetSkipRef.current = false; return; }
    setPickedList(null);
    setBestScoreByLine({});
    setUnscoredLines(new Map());
    // The chosen solution goes too: it was chosen out of a listing these filters have just replaced, so the
    // plan it was found on may no longer be offered at all.
    //
    // A SECTION change is different from a filter change, though: the new section may already be priced, and
    // its own selection has to come back. When FrankQuoteBuilderPage rendered one section at a time, under a
    // LayoutNavSectionList that keys its child by the map POSITION (always 0 for a one-element array), switching
    // section did NOT remount this component, so restoring only in the useState initializer left an
    // already-priced second section (a shirt box's Kapak) showing the solution listing instead.
    //
    // TODAY EVERY SECTION HAS ITS OWN INSTANCE: FrankQuoteBuilderPage keys each section body by its section and
    // mounts every priceable section (staggered, see visitedSections), hidden while another is open, and
    // FrankSetupWizard keys by its section too. So a document-wide query for cards also finds the hidden
    // sections' cards -- scope it to the section body (data-frank-section-body) before reading one section's
    // listing (#6674).
    //
    // This line still runs on EVERY filter change (Search, area ratio), not only a section change: there it sets
    // the selection to whatever window.user_quote_nav_sections holds for this section.
    setSelectedSolutions(restoreSelectedSolutions(navSectionIndex));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- committedFilterKey is Search's trigger and
    // areaRatioPercent its own (blur-committed) one; a section switch resets at once and changes both, so the
    // reset stays in step with the re-list the same change fires.
  }, [navSectionIndex, committedFilterKey, areaRatioPercent]);

  // ANSWERED, not "answered with a score": a line the server says is unmanufacturable is recorded as null, and
  // that is an answer -- the card draws it as an em dash. What is NOT an answer is a null this page wrote itself
  // because nothing came back (unscoredLines), and the two are the same value, so the presence of the key cannot
  // tell them apart on its own. Declared HERE, above the early return below, so the flash effect and the
  // rendering read the same one set of definitions.
  const lineAnswered = (line) => Object.prototype.hasOwnProperty.call(bestScoreByLine, lineKey(line))
    && !unscoredLines.has(lineKey(line));
  // Still being asked about: a round is running for it, or one is about to. The section is WORKING while this is
  // true of any line on screen -- which is what keeps the progress up instead of showing a finished-looking
  // listing with holes in it.
  const lineBeingAsked = (line) => unscoredLines.get(lineKey(line))?.asking === true;
  // Nothing more is coming for this one: the rounds are spent. It has no score and it is not going to get one
  // until somebody asks again.
  const lineGaveUp = (line) => unscoredLines.has(lineKey(line)) && !lineBeingAsked(line);
  // SETTLED: this line's story is over, either way. Answered, or given up on and saying so.
  const lineSettled = (line) => lineAnswered(line) || lineGaveUp(line);

  // A list SHOWS ITS SCORE only when every one of its lines answered -- its best is the sum of theirs, so one
  // missing line means there is no total to show.
  const listScored = (list) => list.manufacturing_lines.every(lineAnswered);
  const listBeingAsked = (list) => list.manufacturing_lines.some(lineBeingAsked);
  const listGaveUp = (list) => list.manufacturing_lines.some(lineGaveUp);
  // WHY a list has no score. The first reason among its lines: one sentence about one card, and a plan is
  // unusable on the first line that could not be scored.
  //
  // SHOWN TO ADMINISTRATORS ONLY, because the reason is the server's own message -- the exception a broken
  // computation raised, or the store's complaint about a body that did not land whole. That is diagnostic text
  // about our internals, and this is a paid product whose subscribers should be told that the score could not be
  // worked out, not which class raised where. The card and the section still SAY they could not be scored, and
  // still offer to ask again, for everybody.
  const unscoredReasonVisible = () => window.current_user_admin || window.quote_builder_v2_enabled;
  const firstUnscoredReasonOf = (manufacturingLines) => {
    if (!unscoredReasonVisible()) { return null; }

    return manufacturingLines
      .map((manufacturingLine) => unscoredLines.get(lineKey(manufacturingLine))?.reason)
      .find((reason) => Boolean(reason)) || null;
  };
  const listUnscoredReason = (list) => firstUnscoredReasonOf(list.manufacturing_lines);

  // THE FLASH: dim the results for half a second as an asked-for search lands. Two searches can answer with the
  // very same manufacturing lines -- bobbin on and bobbin off are found over different material sheets, but the
  // same presses and cutters run both -- and a screen that settles back identical to the one before the press
  // reads as "the filter does nothing". This says the answer is fresh without claiming it is different.
  //
  // It fires when the section STOPS working (the listing is in and every line on screen has answered), and only
  // for a search someone pressed Search for: not the section's own first listing, and not a cancelled one, where
  // nothing arrived to show (both clear the ref).
  useEffect(() => {
    const awaited = awaitingSearchResultsRef.current;
    if (!awaited || isRefetchingListing) { return; }
    if (listingLandedCount <= awaited.landed) { return; }

    // WHAT IS LEFT TO WAIT FOR. A listing with nothing in it has no scoring pass, and neither has one that came
    // back as the very object already on screen -- the store answering a repeat of a search already made, which
    // is instant by design and the reason the section does not re-solve what it has just solved. Anything else
    // is a new listing whose lines are being scored right now, and blinking before that finishes would say
    // "here is your answer" over the previous search's numbers.
    const nothingToScore = manufacturingLineLists.length === 0 || listing === awaited.listing;
    if (!nothingToScore && scoringCompletedCount <= awaited.scored) { return; }

    awaitingSearchResultsRef.current = null;
    setResultsJustRefreshed(true);
  }, [isRefetchingListing, listing, listingLandedCount, scoringCompletedCount]);

  // THE TIMER IS THE FLAG'S OWN, keyed on nothing else. It used to be armed by the effect above, and so was
  // cleared by that effect's cleanup whenever a score or the listing changed -- which happens routinely inside
  // the flash's half second (a line answering late, a retry landing). The re-run then found the ref already
  // spent, armed nothing, and the results stayed dimmed for good. Here the only dependency is the flag itself,
  // so a raised flag always has exactly one timer to lower it.
  useEffect(() => {
    if (!resultsJustRefreshed) { return undefined; }

    const timer = setTimeout(() => setResultsJustRefreshed(false), RESULTS_BLINK_MILLISECONDS);
    return () => clearTimeout(timer);
  }, [resultsJustRefreshed]);

  // Mirror the picked plan into the address bar so a refresh / shared link reproduces it, AND tell the parent
  // what it now is. The parent gates the Solution stage on there being a picked plan, so it has to hear about
  // the plan going away as well as being chosen -- and a plan goes away in ways no click reports: a filter
  // change re-lists and drops it, and a plan restored from the address bar is dropped when the listing that
  // lands does not offer it. Told only on clicks, the page went on offering a Solution stage with nothing to
  // solve, which is what showed "Please select a Manufacturing Line to Continue" on a tab that should have
  // been closed.
  useEffect(() => {
    if (navSectionIndex === undefined) { return; }
    if (onLinePicked) { onLinePicked(pickedList); }
    if (pickedList) {
      writeSelectedManufacturingLineList(navSectionIndex, pickedList);
    } else {
      clearSelectedManufacturingLineList(navSectionIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onLinePicked is the parent's callback, reported
    // when the PICK changes; adding it as a dependency would re-report on every parent render instead.
  }, [pickedList, navSectionIndex]);

  // Mirror the SECTION'S FILTERS the same way, so whatever remounts this section -- closing the guided setup
  // wizard, a refresh, a shared link -- brings back the filters the listing on screen was found with, instead of
  // dropping to the server's defaults and re-listing the section from scratch.
  //
  // The EDITED values, not the committed ones: the address bar mirrors what is on screen, and a wizard closed
  // before Search should still reopen on the boxes the user had just ticked. The area-ratio cap is written only
  // while it differs from the page's default, so an ordinary visitor never carries an admin tuning knob around.
  useEffect(() => {
    if (navSectionIndex === undefined) { return; }
    writeSectionFilters(navSectionIndex, {
      machineTypes: selectedTypes,
      disabledFilters: disabledFilters,
      areaRatioPercent: areaRatioPercent === DEFAULT_AREA_RATIO_PERCENT ? null : areaRatioPercent,
      materialUserPriceId: selectedMaterialUserPriceId,
      cartonGsm: selectedCartonGsm,
    });
  }, [navSectionIndex, selectedTypes, disabledFilters, areaRatioPercent, selectedMaterialUserPriceId, selectedCartonGsm]);

  // A plan restored from the URL may name one the current listing does not hold (a shared link after the
  // catalogue or filters changed). Once the listing lands, drop such a stale selection so the page shows the
  // listing rather than an empty picked view.
  useEffect(() => {
    if (!listing || !pickedList) { return; }
    // The section's OWN committed manufacturing line is never a stale URL leftover: it is the manufacturing line
    // this section is priced on -- valid by definition -- and its card is drawn from the unfiltered priced fetch
    // (pricedPlanData) even when the default machine-type filters leave it out of the listing. Dropping it here
    // cleared the pick while the solution stayed committed, so the Solution and Pricing stages read as open with
    // no manufacturing line selected. Keep it; only a genuinely different URL-restored manufacturing line is
    // judged below.
    if (pickedList === pricedListIdentifier) { return; }
    // Judged by whether the manufacturing line can be BUILT from the section (planOfferedByStructure), not by
    // whether it is in the capped listing right now: the cap re-shuffles as scores land, and a valid pick must
    // not be dropped for falling below it -- only a manufacturing line the new structure genuinely cannot make
    // is stale.
    if (!planOfferedByStructure(listing, pickedList)) { setPickedList(null); }
  }, [listing]);

  // Rank the solutions once their scores land. Each card fetches its OWN score (the legacy card does this
  // itself), so the scores arrive one at a time and the ranking can only settle after the last one -- which is
  // why the legacy re-runs sortDivs on a 200ms timer rather than once. Frank does the same, but STOPS: it polls
  // only while the solutions are on screen, and the interval is cleared when the picked plan goes away.
  useEffect(() => {
    solutionRevealForcedRef.current = false; // a (re-)solve starts unforced -- hold until it ranks or the floor lapses
    if (!pickedList) { return undefined; }

    // The 200ms sorter also drives the REVEAL: each card owns its score and they land one at a time WITHOUT
    // re-rendering this component, so the layout effect never sees the last one arrive -- this poll re-orders the
    // grid and then re-evaluates the reveal, so the grid appears the moment it is settled.
    const settle = () => {
      sortSolutionsByScoreAndDrawStars(solutionsGridRef.current);
      settleSolutionGridReveal();
    };
    settle();
    const timer = setInterval(settle, 200);
    // A score that never lands would otherwise hold the grid off screen forever, so force the reveal after the
    // floor. ONLY on the Solution stage that actually holds a grid: armed on the Pricing/Line tabs (where pickedList
    // is still set but no grid is held) it would fire against nothing and, worse, leave forced=true for the ~200ms
    // until the next tick clears it -- long enough for a Pricing->Solution switch in that window to flash the
    // score-0 grid the hold exists to prevent. The effect re-runs on stageView (a dep), so returning to Solution
    // arms a fresh floor.
    const revealFloor = stageView === 'solution'
      ? setTimeout(() => {
        solutionRevealForcedRef.current = true;
        setSolutionGridRanked(true);
      }, SOLUTION_GRID_REVEAL_FLOOR_MILLISECONDS)
      : null;
    return () => { clearInterval(timer); if (revealFloor) { clearTimeout(revealFloor); } };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the poll restarts on a new pick / re-solve / stage
    // change (settleSolutionGridReveal reads the LIVE stageView, so it must not keep a stale one); settle and
    // settleSolutionGridReveal otherwise only read refs and the live DOM, so listing them would needlessly tear
    // down and rebuild the interval every render.
  }, [pickedList, solutionGroups, stageView]);

  // THE PRICED PLAN, for the refresh path. When a solution is chosen but the (default-filtered) listing does not
  // hold its plan, fetch that plan by identifier from the UNFILTERED priced endpoint so the priced card can still
  // be drawn. This is the refresh case: the component remounts with no ticked machine-type filters, so the
  // listing comes back without a plan whose machines are off by default (a Flatbed UV press + a Digital cutter).
  // On a fresh in-session select the listing already holds the plan (the filters the viewer ticked to surface it
  // are still set), so nothing is fetched.
  //
  // WAITS FOR THE LISTING (does nothing while it is null): the listing loads async, so acting before it arrives
  // would read an empty listing, miss the plan, and fire a recovery fetch on every priced-view load -- even when
  // the listing is about to hold the plan. Once it has loaded, the plan-in-listing check is real.
  useEffect(() => {
    if (!window.frank_mode || navSectionIndex === undefined
        || !hasSelection(selectedSolutions) || !planOnScreen || !listing) {
      setPricedPlanData(null);
      setPricedPlanPending(false);
      setPricedPlanUnavailable(null);
      return undefined;
    }
    const inListing = manufacturingLineLists.some((list) => list.identifier === planOnScreen);
    if (inListing) {
      setPricedPlanData(null);
      setPricedPlanPending(false);
      setPricedPlanUnavailable(null);
      return undefined;
    }

    const query = new URLSearchParams();
    query.set('manufacturing_line_list', planOnScreen);
    if (bobbinEnabled !== undefined) { query.set('bobbin_enabled', bobbinEnabled); }
    if (selectedMaterialUserPriceId) { query.set('material_user_price_id', selectedMaterialUserPriceId); }
    if (selectedCartonGsm !== null) { query.set('carton_gsm', selectedCartonGsm); }    query.set('area_ratio_percent', areaRatioPercent);
    // The priced plan's card is drawn against the same account the quote is set to.
    appendManufacturingLinesSource(query);

    let superseded = false;
    setPricedPlanPending(true);
    setPricedPlanUnavailable(null);
    // ASKED UNTIL IT IS ANSWERED, like the section's other three answers -- through fetchJsonCached, which polls
    // (sectionAnswerPolling) and caches what lands. This endpoint computes on the manufacturing_cost_calculator
    // queue too (Frank::SectionAnswer), so a cold ask answers `{ status: 'processing' }` and the answer follows
    // seconds later. Read as the answer, that first response carries no manufacturing_line_list at all -- which
    // this took for "there is no such plan" and nothing ever asked again. On a LINKED section, whose whole
    // Manufacturing Line tab IS this card, that was an empty tab for as long as it was left open: measured on
    // /mcc/48245/…/2717/edit, one request, `{"status":"processing"}`, and nothing after it. The warm path hid it
    // -- an answer already computed lands on the first ask, so the tab drew the card and the bug looked like
    // flakiness. Never `data.manufacturing_line_list` off an unanswered ask again: fetchJsonCached resolves only
    // a real answer, or null when the server says it has none.
    //
    // `unavailable` is WHY it could not be answered, kept for whoever can act on one -- in development, that
    // nothing is listening on that queue. Held rather than drawn from inside the callback, because whether there
    // is anything to say is decided below: a joined ask is not given the reason (there is one request, and it
    // goes to whoever asked first), so the null is what has to be read, not the presence of a message.
    let unavailable = null;
    fetchJsonCached(
      `/mcc/${window.user_quote_id}/priced-manufacturing-line-list/${planSectionIndex}?${query.toString()}`,
      { onUnavailable: (error) => { unavailable = error; } }
    )
      .then((data) => {
        if (superseded) { return; }
        setPricedPlanPending(false);
        // NO ANSWER is not the same as an answer of NO PLAN. The server answers `{ manufacturing_line_list: null }`
        // when the identifier names a plan this section cannot make -- a real answer, and the card is simply left
        // out, exactly as before. A null here is the ask itself ending with nothing, which is worth saying.
        if (!data) {
          setPricedPlanData(null);
          setPricedPlanUnavailable(unavailable ? unavailable.message : 'the section answered with no manufacturing line');
          return;
        }
        setPricedPlanData(data.manufacturing_line_list);
      })
      .catch((error) => {
        // A request that never arrived (a network or parse error). One statement of what happened, drawn rather
        // than only logged, for the same reason the listing's is: on a linked section there is nothing else on
        // the tab to say it.
        console.error('Error fetching the priced manufacturing line list:', error);
        if (superseded) { return; }
        setPricedPlanData(null);
        setPricedPlanPending(false);
        setPricedPlanUnavailable(error.message);
      });

    return () => { superseded = true; };
  }, [selectedSolutions, planOnScreen, listing, navSectionIndex, planSectionIndex, bobbinEnabled, areaRatioPercent,
    selectedMaterialUserPriceId, pricedPlanAskNumber]);

  // The normal /mcc page keeps this admin/v2 gate while the listing UX is still being productized. The guided
  // setup wizard, however, is the customer-facing surface (Frank only), so when this component is hosted inside
  // it (stageView set) the gate is relaxed and every Frank user can complete the flow.
  // NOTHING TO FOLLOW YET. The section that leads has chosen no manufacturing line, so this one has nothing to
  // inherit and nothing to show. One quiet line, not a panel: WHY there is nothing to choose here is said by the
  // marker on the Manufacturing Line tab (FrankQuoteBuilderPage), which costs no vertical space above the
  // answer. This only says what to do next.
  const renderInheritedPending = () => {
    const parentLabel = linkedParent && linkedParent.label;

    // Pull the leading section's chosen manufacturing line and solution into this one, then open that
    // section so the choice can be seen or changed there. This resolves the case where this section mounted
    // before the parent was answered: its selected_lists were seeded empty and never re-read, so it stayed on
    // this pending screen even after the parent chose a solution. The parent's selected_lists are read FRESH
    // from window.user_quote_nav_sections (every select-solution response refreshes it), and setting them here
    // is what communicates the solution across -- SelectedSolutionSections then posts select-solution for this
    // section and prices it, exactly as it would on a fresh mount with the solution already inherited.
    const continueToParent = () => {
      const parent = linkedParentOf(window.user_quote_nav_sections, navSectionIndex);
      const parentSolutions = parent && parent.selected_lists;
      if (hasSelection(parentSolutions)) { setSelectedSolutions(parentSolutions); }
      if (onGoToLinkedParent) { onGoToLinkedParent(); }
    };

    return (
      <div
        data-frank-inherited-pending
        style={{ textAlign: 'center', fontSize: '13px', color: 'var(--ink-faint, #838d99)', padding: '24px 8px' }}
      >
        <div>
          {parentLabel
            ? t('quote_builder.frank_linked_section_pending', { section: parentLabel })
            : t('quote_builder.frank_linked_section_pending_unnamed')}
        </div>
        {/* Only offered when the leading section is resolvable (so there is a tab to open and a solution to
            read); a stale window payload with no parent falls back to the message alone. */}
        {linkedParent && onGoToLinkedParent && (
          <button
            type="button"
            data-frank-linked-continue
            onClick={continueToParent}
            className="dct-button dct-button--secondary dct-button--small"
            style={{ marginTop: '16px' }}
          >
            {t('quote_builder.frank_linked_section_continue')}
          </button>
        )}
      </div>
    );
  };

  const canSee = stageView != null || window.current_user_admin || window.quote_builder_v2_enabled;

  // THE ASK FAILED, AND THAT IS AN ANSWER TOO. Said in the reader's own words, with the SERVER'S reason under
  // it for whoever can act on one: an administrator, or anyone running this in development, where the reason is
  // that nothing is listening on the manufacturing_cost_calculator queue and how to start one. A customer is
  // shown the sentence and the button, not the exception.
  //
  // Try again is the ask repeated, and nothing else: it bumps the same counter Search does (searchRunNumber is
  // a trigger of the listing fetch in its own right), so the very same filters are asked for again. It does not
  // go through runSearch, which also discards the picked plan and the chosen solution -- there is neither here,
  // and a failure is not the viewer changing their mind.
  //
  // It asks AFRESH (freshSearchRef), because this failure was reported at the round's deadline -- five minutes, the
  // claim's own lifetime -- so any claim still held is one nobody is working on. Dropping it here recovers a worker
  // killed mid-job at once, rather than leaving the next ask to wait out the tail of that expiring claim.
  const renderListingUnavailable = () => (
    <div
      data-listing-unavailable
      style={{ textAlign: 'center', color: 'var(--ink-soft, #4e5865)', fontSize: '14px', padding: '48px 20px' }}
    >
      <div>{t('quote_builder.frank_manufacturing_lines_unavailable')}</div>
      {(window.current_user_admin || window.quote_builder_v2_enabled) && (
        <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--ink-faint, #8a94a3)' }}>
          {listingUnavailable}
        </div>
      )}
      <button
        type="button"
        data-retry-manufacturing-lines
        onClick={() => {
          freshSearchRef.current = true;
          setListingUnavailable(null);
          setSearchRunNumber((previous) => previous + 1);
        }}
        className="dct-button dct-button--secondary dct-button--small"
        style={{ marginTop: '16px' }}
      >
        {t('quote_builder.frank_try_again')}
      </button>
    </div>
  );

  if (!canSee) { return null; }

  // No listing is either "not yet" or "not coming", and the two must not draw the same: a failure says so (see
  // #listingUnavailable), waiting shows that it is waiting.
  //
  // WAITING USED TO DRAW NOTHING, on the reasoning that the section's own loading state covered it. That is true
  // of the legacy stacked view, which has the full-screen ListingProgressOverlay over it -- and false of a STAGE
  // TAB, which has no overlay of its own. So opening a section whose listing was not already cached left the
  // whole tab white for the length of the fetch, with the stage tabs above it and nothing underneath: reported
  // as "just a white page". A stage tab shows the same indeterminate bar the scoring half uses -- titled for
  // what is actually happening here, which is the FINDING: nothing has been scored, or could be.
  //
  // No cancel button on it: what that button stops is the scoring pass, and this is the request BEFORE there is
  // anything to score. Offering it here would be a button that cancels something that has not started.
  // The PRICING stage is exempt: it does not need the section-wide listing. Its prices come from the chosen
  // solution and its plan/solution cards live on the other two tabs (hidden here), so a listing that never
  // arrived -- or failed -- must not replace the priced view. Blocked, it drew the listing's own "manufacturing
  // lines could not be worked out" failure over a section that is already priced: the wrong question, on the
  // wrong tab. It falls through to renderPricing below, which owns its own pricing-worded empty/pending state.
  if (!listing && stageView !== 'pricing') {
    if (listingUnavailable) { return renderListingUnavailable(); }

    // Inside the same ProgressProvider the main render uses: this early return is ABOVE that wrapper, and
    // WizardSearchProgress reads useProgress().
    if (!stageView) { return null; }

    return (
      <ProgressProvider queue={sectionAnswerQueue}>
        <WizardSearchProgress title={t('setup_wizard.finding')} />
      </ProgressProvider>
    );
  }

  // The reduction-toggle and area-ratio blocks are debug tuning controls. They live in <DebugPanel>, which
  // portals into the debug popup -- but falls back to rendering INLINE when there is no popup (show_frank_debug_popup
  // off). On the normal page that never showed because canSee gated the whole component; in the wizard
  // (which relaxes that gate) they would render inline for customers, so gate them on show_frank_debug_popup here,
  // matching the popup's own gate so they never leak inline when it is off.
  const debugPopupVisible = window.show_frank_debug_popup;

  // THE PLANS THE PRICED VIEW REASONS ABOUT: the listing, plus the priced plan recovered for the refresh path
  // when the listing does not hold it (pricedPlanData). The priced card's lookup and the machine scale it is
  // drawn against read this, so a plan the default filters dropped is still drawn to scale. Null pricedPlanData
  // (nothing priced, or the listing already holds the plan) leaves this as just the listing.
  const effectiveManufacturingLineLists = pricedPlanData
    && !manufacturingLineLists.some((list) => list.identifier === pricedPlanData.identifier)
    ? [...manufacturingLineLists, pricedPlanData]
    : manufacturingLineLists;

  // The manufacturing line lists actually ON SCREEN: every one while the listing shows, just the one otherwise
  // (picked, or the one a chosen solution is priced from). The wizard's Manufacturing Line stage lists them ALL (it
  // scores them all), so a restored pick does not narrow this to itself.
  const listsOnScreen = (planOnScreen && stageView !== 'line')
    ? manufacturingLineLists.filter((list) => list.identifier === planOnScreen)
    : manufacturingLineLists;

  // Whether every plan on screen has SETTLED. Individual cards no longer wait for this -- each shows its own
  // score as it lands -- but the two things that are a property of the SET still do: the best-first sort, and
  // the star ratings, which are each score's ratio to the cheapest plan on screen and so cannot be known until
  // that cheapest is settled.
  //
  // Settled, not scored: a plan the page has given up on will never have a score, and the listing cannot wait
  // for one it is not asking for. It is excluded from the range and sorts last, exactly as an unmanufacturable
  // plan does -- but its own card says which of the two it is.
  //
  // It waits for EXACTLY the manufacturing lines the scoring pass asks about (manufacturingLinesToScore, shared with
  // the best-score effect) -- on the Manufacturing Line stage every one the section offers, not only those of the
  // cheapest lists shown: a manufacturing line still being scored counts as Infinity, so its lists sit outside
  // them, and the cheapest list of all can be the one whose manufacturing line answers last.
  const manufacturingLinesToSettle = manufacturingLinesToScore(listing, scopedManufacturingLineList);
  const bestScoresReady = manufacturingLinesToSettle.length > 0 && manufacturingLinesToSettle.every(lineSettled);

  // The manufacturing lines with no score and nothing left asking for one -- what the section reports above the
  // listing, and what its retry control asks about again. Counted over the manufacturing lines, not the lists on
  // screen: a list holding one of them has no score, sorts last, and is usually not among the cheapest the listing
  // shows, so counting lists would hide exactly the failure that may have removed the cheapest one.
  const manufacturingLinesThatGaveUp = manufacturingLinesToSettle.filter(lineGaveUp);
  const unscoredNoticeReason = firstUnscoredReasonOf(manufacturingLinesThatGaveUp);

  // WHETHER THE SECTION IS STILL WORKING OUT THE ANSWER THE FILTERS DESCRIBE: the lines are being scored (they
  // are listed but not every one has answered). Only while there ARE lines to score -- with none (every
  // printing machine unticked, say) bestScoresReady never turns true, and a lock waiting on it would never
  // lift. A cancelled search is not in progress: nothing is coming, and the cards keep the scores that did land.
  //
  // NEITHER IS A SEARCH THAT FAILED, and the difference is not academic: the scoring pass follows the LISTING
  // object, so a failed re-list leaves the previous listing with the scores that same Search dropped and nothing
  // running to replace them. Read as "in progress" that was permanent -- it locked the filter block, and the
  // block's own way out (Cancel) is hidden while scoring, so the failure was reported over a section the viewer
  // could not touch: filters greyed, Search greyed, one button on the page. It ended when the ask did.
  //
  // AND NEITHER IS A SECTION THAT IS STILL BEING LISTED. The manufacturing lines on hand then belong to the
  // search being REPLACED, and the Search that started this one has already dropped their scores -- which read
  // as "scoring", so the stage titled its bar "Scoring manufacturing lines…" over a listing nothing was scoring
  // and that was about to be thrown away. Measured on an ordinary tick-and-search: the finding title for four
  // milliseconds, then the scoring one for the whole of the re-list. While the listing is out the section is
  // FINDING, whatever it is still holding.
  const scoringInProgress = !searchCancelled && !listingUnavailable && !isRefetchingListing
    && manufacturingLineLists.length > 0 && !bestScoresReady;

  // THE FILTER BLOCK IS LOCKED WHILE THE ANSWER IT DESCRIBES IS BEING COMPUTED -- the re-list AND the scoring
  // that follows it, which is by far the longer half.
  //
  // Ticking during the scoring changed the checkbox and re-ran the material-sheet finder AT ONCE, while the
  // lines and scores on screen were still the previous value's: the block then said "Bobbin" over a listing
  // found without it, and the debug popup's "Chosen material dimensions" showed sheets from whichever of the
  // two fetches happened to land -- the same section describing itself two ways. Locking the block until the
  // answer settles keeps the controls and the numbers below them the same reading.
  //
  // The lock is never a trap: while it holds, Cancel (below the block) stops the search and hands the filters
  // back, which is the only reason a viewer would want them mid-search.
  const filtersLocked = !searchCancelled && (isRefetchingListing || scoringInProgress);

  // Stop both passes, unlock, AND CLEAR WHAT THE STOPPED SEARCH HAD PRODUCED: a listing whose lines are half
  // scored is not an answer to anything -- it is a leftover of a question the viewer withdrew, and reading it as
  // a result (sorting it, starring it, picking from it) would be reading a partial one. So the lines and their
  // scores go; the section shows its filters and waits to be asked again.
  //
  // The listing OBJECT is kept (emptied) rather than nulled: it carries the machine-type filter rows this
  // section draws its checkboxes from, and nulling it would take the filters away with the results.
  const cancelSearch = () => {
    if (cancelListingRef.current) { cancelListingRef.current(); }
    if (cancelScoringRef.current) { cancelScoringRef.current(); }
    // Nothing arrived, so nothing blinks when the block unlocks.
    awaitingSearchResultsRef.current = null;
    setSearchCancelled(true);
    // Empty the STRUCTURE (and the legacy key), so the built listing goes empty until the next search lands.
    setListing((previous) => ({ ...(previous || {}), sub_sections: [], exact_covers: [], manufacturing_line_lists: [] }));
    setBestScoreByLine({});
    setUnscoredLines(new Map());
  };

  // ASK AGAIN ABOUT THE MANUFACTURING LINES THAT COULD NOT BE SCORED.
  //
  // Done by re-running the scoring pass rather than by reaching into it: the pass already asks only about the
  // lines it has no answer for (everything else is seeded from the response cache without a request), so a
  // re-run IS a retry of exactly those -- and it works from anywhere, including after a pick and a Back, where
  // the previous pass's closure is long gone.
  //
  // The "fully scored" memo is dropped first, because that is what the pass checks before doing anything; a
  // listing with a line nobody could score should never have been remembered as scored, and this is the other
  // half of that.
  const retryUnscoredScores = () => {
    fullyScoredListingRef.current = null;
    setScoringRetryNumber((previous) => previous + 1);
  };

  // Ask for the edited filters. This is the ONLY thing that starts a find + score for a filter change -- see
  // the commit note on pendingFilterKey.
  const runSearch = () => {
    // A NEW START. The plan picked and the solution chosen came out of the listing this search is about to
    // replace -- and even a search that answers with the very same plans is the viewer asking the question
    // again, not confirming the answer they had. So the pick goes, and the chosen solution goes with it through
    // the same server-backed discard a changed pick uses (it un-prices the section, tells the linked sections,
    // and clears nothing locally until the server has actually done it).
    setPickedList(null);
    discardSelectionForChangedPlan();
    awaitingSearchResultsRef.current = {
      landed: listingLandedCount, scored: scoringCompletedCount, listing: listing
    };
    setSearchCancelled(false);
    // THE WAIT BEGINS AT THE PRESS, not when the request goes out. The fetch is started by an effect, and React
    // runs effects AFTER it has painted the press -- so for one painted frame the section still showed what it
    // held before, which after a Cancel is an EMPTY listing and so drew the "no manufacturing lines" finding.
    // Set here, the progress bar (and the lock the search takes on the filters) is up in the same frame as the
    // click. Every path out of the fetch effect below puts it back down: the answer, a failure, or a Cancel.
    setIsRefetchingListing(true);
    setCommittedFilterKey(pendingFilterKey);
    setSearchRunNumber((previous) => previous + 1);
    // The section owns the material sheets (the bobbin is ITS question), so it re-finds them on the same press:
    // the sheets shown and the lines listed are then always the same bobbin's. Guarded on the DESCRIPTOR, not
    // on the function: a page with no bobbin question passes none at all (the legacy stacked view), but one
    // that draws the row and forgot #onSearch is miswired -- and would go back to showing chosen material
    // dimensions found for a different bobbin than the listing, silently.
    if (bobbin) { bobbin.onSearch(); }
  };
  // The GSM select searches on change (the effect up by selectedCartonGsm) by firing runSearch through this ref,
  // so it reaches the latest closure from up there where runSearch could not yet be named.
  runSearchRef.current = runSearch;

  // WHETHER THE LIST IS FINISHED, for the scroll-to-the-selection above -- which cannot ask for these two
  // itself, being declared before them. The Solution tab draws a skeleton while the solve is out
  // (renderSolutions), and the listing SORTS once every score has landed, so a glide before either would land
  // where the marked card used to be.
  listSettledRef.current = stageView === 'solution' ? !solutionsLoading : bestScoresReady;

  // A LIST's best = the SUM of its lines' bests (parts are made independently on their own lines). null when any
  // of its lines is unmanufacturable (no solution) -- that list cannot make the section, so it sorts last.
  const listBestScore = (list) => {
    const perLine = list.manufacturing_lines.map((line) => bestScoreByLine[lineKey(line)]);
    if (perLine.some((score) => score == null)) { return null; }

    return perLine.reduce((sum, score) => sum + score, 0);
  };

  // The cheapest of the lists' best scores on screen -- the star ratings are each score's ratio to it (best =
  // 5, the rest proportional). Unmanufacturable plans (null) are excluded and get no stars.
  const listBestScores = bestScoresReady
    ? listsOnScreen.map(listBestScore).filter((score) => score != null)
    : [];
  const bestListScore = listBestScores.length ? minimumOf(listBestScores) : 0;
  // The filters are the SECTION's: one set, applied to every sub_section the lists are built from.
  // The pricing stage falls through the gate above with a listing that may still be null (it does not need one),
  // so the section-wide listing is read defensively from here down -- everything below used to be reached only
  // with a listing in hand. These filter rows drive the line stage's own controls, unused on the pricing tab.
  const machineTypeFilters = listing?.machine_type_filters || [];
  const filters = listing?.filters || [];

  // On the owner's OWN manufacturing lines the section can only be searched and priced once it has the owner's
  // material AND a machine of every row it really has (printing + die-cutting for a carton, die-cutting alone for
  // hard cardboard; a row the section cannot use -- allowed set null -- or the combined-machine row impose no
  // requirement, mirroring selectEmptyStateAdder). Read at component scope because a few things depend on it: in a
  // stage view the whole filter island is HIDDEN while a prerequisite is missing (the main render) and renderListing
  // leads to the add-material / add-machine adder instead of scoring a line that cannot be priced; in the legacy
  // stacked view the island stays but its ticks + Search are disabled (renderSectionFilters) and its search-lock
  // dimming is held off so the add/manage buttons stay live. Standard uses the example account, which always has
  // both, so it is never gated.
  const ownSource = manufacturingLinesSource() === OWN;
  const ownMaterialKey = (ownSource && listing) ? listing.material_section_key : null;
  // How many of the owner's own material rows FIT the section as it is produced RIGHT NOW. Only CARTON, whose rows
  // split on the bobbin flag: the bobbin state is read LIVE (bobbin.checked), so toggling Bobin re-counts at once,
  // exactly as the Malzeme dropdown re-filters. null (skip the compatible clause below, fall back to the total
  // count) for every other key AND while the carton list is UNKNOWN (still loading, or the fetch could not be
  // read). Deliberately NOT extended to hard cardboard's thickness split here: its fit depends on the dieline
  // board thickness, which the listing does not always know yet, and reading "no material" over an unknown
  // thickness would wrongly gate a section whose rows are fine. The dropdown still hides in that window (benign);
  // the gate must not promote it to a wrong empty-state, so hard cardboard keeps the plain total-count gate.
  const compatibleOwnMaterialCount = (ownMaterialKey === 'carton_material' && ownMaterials)
    ? ownMaterials.filter((material) => materialFitsSection(material, {
      materialKey: ownMaterialKey,
      bobbinEnabled: bobbin ? bobbin.checked : undefined,
    })).length
    : null;
  // NO MATERIAL THAT FITS. The synchronous total count (window.frank_<key>_count, seeded by the page) is the fast
  // path -- with none at all the gate closes immediately, no flash. Layered on top, FOR CARTON: once the list has
  // landed, a count of ZERO rows fitting the current bobbin state also closes it -- so switching to Bobin with only
  // non-bobbin carton rows leads to adding a bobbin material rather than searching into unpriceable cards. When
  // compatibleOwnMaterialCount is null (any non-carton key, or the carton list not yet known) the clause is
  // skipped, so the total-count behaviour is unchanged.
  const ownMaterialMissing = ownSource && Boolean(ownMaterialKey)
    && (machineCount(ownMaterialKey) === 0 || compatibleOwnMaterialCount === 0);
  const ownMachineMissing = ownSource && machineTypeFilters.some((filter) => {
    const row = SECTION_MACHINE_ROWS[filter.key];
    if (!row) { return false; }
    // Type-aware, mirroring selectEmptyStateAdder: the row is missing only when the owner has no machine of a
    // type it allows, so a wrong-group die-cutter (or wrong printing type) leaves the setup step showing.
    const allowedTypes = row.allowedTypesFor(filter, listing);
    return allowedTypes !== null && !hasMachineOfType(row.machineKey, allowedTypes);
  });
  const ownPrerequisitesMissing = ownMaterialMissing || ownMachineMissing;
  // A machine a FOUND line uses that the owner has not PRICED for this section's type -- computed above with the
  // other hooks (findOwnUnpricedMachine / ownUnpricedMachine), so its wizard-notification effect stays a top-level
  // hook. renderListing shows EmptyStateMachinePriceAdder for it, ahead of the found-but-unquotable listing.
  const ownMachinePriceMissing = Boolean(ownUnpricedMachine);
  // The add-a-machine picker renderListing would ACTUALLY draw in the empty state (this is that same decision,
  // reused below so the two can never disagree): the owner's own lines came up empty and there is a specific
  // missing machine to add, with a tab to add it on. selectEmptyStateAdder returns null when the ONLY missing row
  // is a COMBINED print-and-die-cut one (corrugated cardboard's "Printing & cutting machines"), because there is
  // no single-machine picker for a combined machine -- so nothing replaces the island there.
  const emptyStateMachineAdder = (ownSource && manufacturingLineLists.length === 0)
    ? selectEmptyStateAdder(machineTypeFilters, listing)
    : null;
  // Whether an own-setup ADDER is actually on screen (see renderListing) -- the only time the filter island is
  // wasted space and hides. The material step wins even when a line was found (it cannot be priced without the
  // owner's material). The machine PRICE step also shows over a found line -- that is exactly the unquotable-line
  // case ownUnpricedMachine catches (the record exists, only its price is missing). The machine ENTRY step is
  // reached only with NO line found: a line that WAS found proves the machine RECORDS exist, so a stale record
  // count (window.frank_<key>_count not yet refreshed after adding one in the wizard) does NOT keep the island
  // hidden over the real listing -- selectEmptyStateAdder is gated on manufacturingLineLists being empty.
  //
  // Keyed off the REAL adder, not the looser "a machine is missing" (ownMachineMissing): a combined-row section
  // whose separate printing/die-cutting row is missing a machine has ownMachineMissing true yet NO adder to draw,
  // so keying the hide on it would strip the "no lines match" finding of its filter controls (checkboxes, Manage
  // buttons, Search) with nothing in their place. Gating on the adder keeps the controls, so the owner can change
  // filters, Manage their machines and re-search out of the empty state.
  const ownSetupAdderShown = ownMaterialMissing || ownMachinePriceMissing
    || Boolean(emptyStateMachineAdder && machineTabConfig(emptyStateMachineAdder.machineKey));
  // Whether the always-on area-ratio reduction is weighed for THIS section -- the server's own answer (a flexo
  // section applies no reduction at all), never a part_type test repeated in JavaScript: the same reason the
  // bobbin checkbox reads its state off the payload. Only an explicit false takes the cap out of force; a
  // payload that does not speak of it leaves the input exactly as it was.
  const areaRatioApplies = listing ? listing.area_ratio_applies !== false : true;

  // Largest machine sheet dimension across every manufacturing line the page draws -- the common scale that
  // makes the per-machine rectangles comparable to each other (a bigger machine renders bigger), including
  // across the two lines stacked in one list. The same scale the legacy listing computes, shared with the
  // standard-lines fallback (maxMachineDimensionOf).
  const maxMachineDimensionMm = maxMachineDimensionOf(effectiveManufacturingLineLists);

  // The SECTION's filter rows, drawn as ONE aligned card so the bobbin question and the two machine-type rows
  // read as a single control:
  //     Malzeme:                    [x] Bobin
  //     Baskı Makineleri:      [x] Offset  [ ] Dijital ...
  //     Kalıplı kesim makineleri:  [ ] Dijital  [x] Otomatik ...
  // The ticks are instant and local, and nothing is found or scored until Search (the button below the rows,
  // in the same card). While THAT runs the block dims and its inputs disable (filtersLocked) -- see the wrapper
  // below -- so the viewer sets the boxes they want, asks once, and cannot change them again until the listing
  // AND its scoring have settled. The bobbin row is Frank's: the legacy page draws its own in
  // ChooseLayoutDimensions, and this component is /mcc only.
  const headingStyle = { fontSize: '13px', color: 'var(--ink, #10141a)', fontWeight: 600, whiteSpace: 'nowrap' };
  const optionsRowStyle = { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '16px' };
  const optionLabelStyle = {
    fontSize: '13px', color: 'var(--ink-soft, #4e5865)', fontWeight: 500,
    display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer'
  };
  const checkboxStyle = { accentColor: 'var(--brand, #eb5424)', width: '15px', height: '15px', margin: 0, cursor: 'pointer' };

  const renderSectionFilters = () => {
    const bobbinRowShown = bobbin && bobbin.shown;
    // The "Malzeme" row carries the material add/manage button whenever the owner has an OWN material for this
    // section (ownMaterialKey, at component scope) -- like the machine rows carry theirs -- even when the section
    // has no bobbin question.
    const malzemeRowShown = bobbinRowShown || Boolean(ownMaterialKey);

    // Drawn even for a section with no rows to tick -- a bobbin question it is not asked and no machine-type
    // filters -- because the card is not only the rows: Search is in it, and asking the section to be worked
    // out again is worth a button whether or not there is anything to narrow it by.
    return (
      <div
        style={{
          display: 'inline-grid', gridTemplateColumns: 'max-content 1fr max-content', gap: '10px 20px', alignItems: 'center',
          padding: '12px 18px', textAlign: 'left',
          background: 'var(--surface, #ffffff)', border: '1px solid var(--rule, #e3e7ec)',
          borderRadius: 'var(--dct-radius, 8px)', boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)'
        }}
      >
        {malzemeRowShown && (
          <React.Fragment>
            <span style={headingStyle}>{t('quote_builder.material')}:</span>
            {/* SECOND COLUMN, over the machine-type checkboxes: the bobbin tick and -- on the owner's own lines --
                the material SELECT (+ carton GSM), so the material sits right after the label like the machine
                rows' checkboxes instead of being pushed out to the far right. */}
            <div data-section-bobbin-filter style={optionsRowStyle}>
              {bobbinRowShown && (
                <label style={{ ...optionLabelStyle, cursor: bobbin.forced ? 'default' : 'pointer' }}>
                  {/* Bobin stays enabled even while the own-setup gate disables the rest: it drives which materials
                      fit (MaterialSelect re-filters on it), so the owner must be able to toggle it while choosing /
                      adding a material. Only forced (flexo) or a search in flight locks it. */}
                  <input
                    type="checkbox"
                    checked={bobbin.checked}
                    disabled={bobbin.forced || filtersLocked}
                    data-bobbin-checkbox
                    onChange={(event) => bobbin.onChange(event.target.checked)}
                    style={{ ...checkboxStyle, cursor: bobbin.forced ? 'default' : 'pointer' }}
                  />
                  {t('quote_builder.bobbin_label')}
                </label>
              )}
              {/* marginLeft: auto pushes the material group to the RIGHT of this column, up against the "Yönet"
                  column, so the select reads as part of the material control beside its manage button rather than
                  floating in the middle. Any bobbin tick keeps its place on the left. */}
              {ownMaterialKey && (
                <div style={{ marginLeft: 'auto', display: 'inline-flex', gap: '8px', alignItems: 'center' }}>
                  <MaterialSelect
                    materialKey={ownMaterialKey}
                    materials={ownMaterials}
                    bobbinEnabled={bobbin ? bobbin.checked : undefined}
                    boardThicknessMm={listing.board_thickness_mm}
                    corrugatedCardboardFlute={listing.corrugated_cardboard_flute}
                    selectedId={selectedMaterialUserPriceId}
                    onSelect={setSelectedMaterialUserPriceId}
                  />
                  {/* The carton GSM (a carton_lamination section only, so the server offers carton_gsm_options).
                      It sets the printing material's thickness, so a change RE-LISTS AT ONCE (a different GSM finds
                      different printing machines -- the effect by selectedCartonGsm), NOT waiting for "Ara" like
                      the machine-type ticks. The shown value is the pending pick, else the server's current GSM. A
                      cover carries none: its printing paper is the chosen coating material row and its grammage is
                      that row's own, shown in the Malzeme label and derived server-side by
                      Frank::CoatingMaterialUserPriceFinder. */}
                  {(listing.carton_gsm_options || []).length > 0 && (
                    <select
                      data-carton-gsm-select
                      value={(selectedCartonGsm != null && listing.carton_gsm_options.includes(selectedCartonGsm))
                        ? selectedCartonGsm : listing.carton_gsm}
                      onChange={(event) => setSelectedCartonGsm(Number(event.target.value))}
                      disabled={filtersLocked}
                      className="form-control"
                      style={{ maxWidth: '150px', height: '38px' }}
                      aria-label={t('quote_builder.gsm')}
                    >
                      {listing.carton_gsm_options.map((gsm) => (
                        <option key={gsm} value={gsm}>{`${gsm} GSM`}</option>
                      ))}
                    </select>
                  )}
                </div>
              )}
            </div>
            {/* THIRD COLUMN: the material add/manage button on its own, so its "Yönet" lines up with the machine
                rows' "Yönet". An empty span when the section has no own material, so the 3-column grid stays aligned. */}
            {ownMaterialKey
              ? (
                <ManageOwnMaterialsButton
                  materialKey={ownMaterialKey}
                  hasCompatibleMaterial={compatibleOwnMaterialCount == null ? null : compatibleOwnMaterialCount > 0}
                  defaultBobbinEnabled={bobbin ? bobbin.checked : undefined}
                  boardThicknessMm={listing.board_thickness_mm}
                  corrugatedCardboardFlute={listing.corrugated_cardboard_flute}
                />
              )
              : <span />}
          </React.Fragment>
        )}

        {machineTypeFilters.map((filter) => {
          const labels = MACHINE_TYPE_FILTER_LABELS[filter.key];
          if (!labels) { return null; }

          // The row's own-machine add/manage button (printing / die-cutting rows), and the picker constraint it
          // hands down -- the SAME set the empty-state adder uses, so both paths to add a machine agree. A null
          // set means the section cannot use this machine at all (a die-cutting row on a part_type with no
          // material group, e.g. pvc), so -- exactly as the empty-state gate declines the inline picker -- the
          // button is not offered either: adding a machine there could never form a line.
          // The button falls back to MANAGE_BUTTON_ONLY_MACHINE_ROWS so the combined row gets a "Manage" button
          // WITHOUT joining SECTION_MACHINE_ROWS (which would wrongly gate the section on a combined machine above).
          const machineRow = SECTION_MACHINE_ROWS[filter.key] || MANAGE_BUTTON_ONLY_MACHINE_ROWS[filter.key];
          const machineAllowedTypes = machineRow ? machineRow.allowedTypesFor(filter, listing) : undefined;

          const currentlySelected = filter.options.filter((option) => option.selected).map((option) => option.value);
          // The EDITED selection (the ticks), not the server echo -- so a tick shows at once before the search
          // window, before the re-list that will confirm it. Absent means "not spoken yet" -> the server's default.
          const editedSelected = selectedTypes[filter.key] || currentlySelected;

          return (
            <React.Fragment key={filter.key}>
              <span style={headingStyle}>{t(labels.headingKey)}:</span>
              <div data-machine-type-filter={filter.key} style={optionsRowStyle}>
                {filter.options.map((option) => (
                  <label key={option.value} style={optionLabelStyle}>
                    <input
                      type="checkbox"
                      checked={editedSelected.includes(option.value)}
                      disabled={filtersLocked || ownPrerequisitesMissing}
                      data-machine-type={option.value}
                      onChange={() => toggleMachineType(filter.key, option.value, currentlySelected)}
                      style={checkboxStyle}
                    />
                    {t(labels.optionKey(option.value))}
                  </label>
                ))}
              </div>
              {/* THIRD COLUMN. On the owner's OWN manufacturing lines, the add/manage button opens the owner's own
                  machines OF THIS ROW's type -- printing machines on the printing row, die-cutting machines on the
                  die-cutting row, combined machines on the combined row -- so it sits in its own column at the end of
                  that row. A row with no mapped button and every other cell stay empty so the grid stays aligned.
                  Hidden on the Standard example account, which the customer does not manage. */}
              {manufacturingLinesSource() === OWN && machineRow && machineAllowedTypes !== null
                ? (
                  <span>
                    <ManageOwnMachinesButton
                      machineKey={machineRow.machineKey}
                      addLabelKey={machineRow.addLabelKey}
                      manageLabelKey={machineRow.manageLabelKey}
                      allowedLibraryTypes={machineAllowedTypes}
                    />
                  </span>
                )
                : <span />}
            </React.Fragment>
          );
        })}

        {/* ASK. Nothing above is found or scored until this is pressed -- until then the ticks are an edit and
            nothing more. It spans both columns under the rows it submits, in the same card, so what is being
            asked for and the asking are one control. */}
        <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'center', marginTop: '2px' }}>
          <button
            type="button"
            data-search-manufacturing-lines
            onClick={() => { freshSearchRef.current = true; runSearch(); }}
            disabled={filtersLocked || ownPrerequisitesMissing}
            className="dct-button dct-button--primary dct-button--small"
          >
            {t('nav.search')}
          </button>
        </div>
      </div>
    );
  };

  // The SECTION's reduction toggles. Each reduces every sub_section's manufacturing lines; unticking one
  // re-fetches and the manufacturing lines it was hiding reappear.
  //
  // A reduction the SECTION DOES NOT APPLY (applies: false -- a flexo section applies none) is drawn UNTICKED
  // and DISABLED rather than left out: the administrator sees that the reduction exists and that it cannot be
  // switched on here, which a missing checkbox would not say. Unticked because it is genuinely not in force --
  // a ticked-but-disabled box would describe a listing that was never reduced.
  const renderFilterToggles = () => (
    <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '16px', marginTop: '8px' }}>
      {filters.map((filter) => {
        // Only the server's explicit false takes a reduction out of force; a payload that does not speak of it
        // toggles exactly as it did before.
        const applies = filter.applies !== false;

        return (
          <label
            key={filter.key}
            title={applies ? undefined : 'Not applied for this section'}
            style={{ ...optionLabelStyle, opacity: applies ? 1 : 0.55, cursor: applies ? 'pointer' : 'default' }}
          >
            <input
              type="checkbox"
              // The EDITED value (disabledFilters), not the server echo, so the tick shows before the search runs.
              checked={applies && !disabledFilters.includes(filter.key)}
              disabled={filtersLocked || !applies}
              data-filter-key={filter.key}
              onChange={() => toggleFilter(filter.key)}
              style={{ ...checkboxStyle, cursor: applies ? 'pointer' : 'default' }}
            />
            {t(`quote_builder.${FILTER_LABEL_KEYS[filter.key]}`)}
          </label>
        );
      })}
    </div>
  );

  // The always-on area-ratio reduction's cap, as an adjustable PERCENT -- the Debug knob for tuning how much
  // bigger than the sheet a printing bed may be before the manufacturing line is dropped. It reads as the rule
  // itself: "Drop a printing bed more than [110] % larger in area than the sheet." Committed on blur / Enter
  // (commitAreaRatio), which re-lists and re-scores against the new cap.
  //
  // Drawn DISABLED for a section that does not apply the reduction (a flexo one), like the toggles above and for
  // the same reason: the rule is worth seeing even where it is not in force, and a live input would offer a cap
  // that changes nothing.
  const renderAreaRatioControl = () => (
    <label
      title={areaRatioApplies ? undefined : 'Not applied for this section'}
      style={{
        fontSize: '12px', color: '#555', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap',
        opacity: areaRatioApplies ? 1 : 0.55
      }}
    >
      Drop a printing bed more than
      <input
        type="text"
        inputMode="numeric"
        data-area-ratio-percent
        value={areaRatioInput}
        disabled={!areaRatioApplies}
        onChange={(event) => setAreaRatioInput(event.target.value)}
        onBlur={commitAreaRatio}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commitAreaRatio();
          }
        }}
        style={{
          width: '56px', padding: '2px 6px', fontSize: '12px', textAlign: 'right',
          border: '1px solid #d1d5db', borderRadius: '4px'
        }}
      />
      % larger in area than the sheet
    </label>
  );

  // A machine is drawn ONCE per list card (columnVisibilityFor, now shared with the standard-lines fallback --
  // see components/frank/utils/manufacturingLineListRendering): the lines of a list routinely share a machine
  // (one press running both the Cover and the Tray), and drawing it per line reads as though the plan needs two
  // of them, so a machine already drawn above leaves its column EMPTY rather than repeating it.

  // One ManufacturingLineList as a single card: a complete way to manufacture the section. A list covering two
  // sub_sections stacks BOTH its manufacturing lines in the one card -- so a card is the familiar machine card,
  // with more machines in it. Each line is badged with the sub_section it makes, since a card holding two
  // identical-looking machine pairs is otherwise unreadable.
  //
  // The card decides which columns each manufacturing line draws, because it is the only place that knows the
  // list's OTHER manufacturing lines -- a ManufacturingLineCard renders the one it is handed.
  //
  // A manufacturing line whose machines were ALL drawn by ONE line above has no box left, so it is not given a
  // row of its own: an empty row under a lone badge reads as unfinished. Its part is badged onto THAT line --
  // the one whose machines are its machines -- so the card reads "Kapak and Kutu, on this pair".
  // alone: the plan is the only card on screen (the picked view), so it is not one of a 4-across grid and must
  // not be squeezed to a quarter width.
  // readOnly: the priced view, where the card names the plan the prices are about rather than offering a pick.
  // Choosing again there goes through the "choose a different solution" button, which de-selects server-side
  // too -- a card that silently un-picked the plan under a priced section would leave the two disagreeing.
  const renderManufacturingLineList = (list, { alone = false, readOnly = false } = {}) => {
    const columnVisibility = columnVisibilityFor(list.manufacturing_lines);
    const drawnManufacturingLines = list.manufacturing_lines
      .map((manufacturingLine, index) => ({ manufacturingLine, index }))
      .filter(({ index }) => !columnVisibility[index].drawsNothing);

    // The parts each machine of this list makes, so each machine box carries the part(s) it makes -- shared with
    // the standard-lines fallback (partLabelsByMachineId in manufacturingLineListRendering).
    const machinePartLabels = partLabelsByMachineId(list);

    const picked = pickedList === list.identifier;
    // On the Manufacturing Line tab, the SELECTED line is the pick, or -- on a reopened priced section that
    // never held a pick -- the plan its chosen solution is priced from. Either way that line is highlighted and
    // the rest are dimmed, so "this is the selected manufacturing line" reads at a glance.
    const isSelectedLine = list.identifier === (pickedList || pricedListIdentifier);
    // The priced view's card IS the plan in question, so it carries the picked highlight whether or not the
    // page still holds a pick -- a link straight into a priced section never had one.
    const highlighted = picked || readOnly || (stageView === 'line' && isSelectedLine);
    // The plan's best (cheapest) way to make the whole section -- the sum of its lines' bests. Shown once every
    // line's score has landed; '—' when the plan is unmanufacturable (a line with no solution). Its star rating
    // is its ratio to the cheapest plan on screen: best = 5 stars, the rest proportional (matching the solution
    // cards).
    // THE THREE THINGS A CARD'S SCORE SLOT CAN BE, and they must not be confused with one another:
    //   scored     -- every line answered. A number, or the em dash if the plan cannot be manufactured.
    //   beingAsked -- a line has no answer yet and something is still asking. A placeholder.
    //   gaveUp     -- a line has no answer and nothing is asking any more. Says so, and offers to ask again.
    // The provisional null recorded for an unanswered line is why `scored` cannot simply read the value: it is
    // the same null the server sends for a plan that cannot be made, and drawing that em dash here would say
    // "cannot be manufactured" about a plan nobody established anything of the kind for.
    const beingAsked = listBeingAsked(list);
    const gaveUp = !beingAsked && listGaveUp(list);
    const scored = listScored(list);
    const listBest = scored ? listBestScore(list) : null;
    const rating = bestScoresReady && scored ? starRatingForScore(listBest, bestListScore) : null;

    // Picking toggles the plan. On a USER pick (not a restore from the URL) tell the parent, so the page's
    // Manufacturing Line tab can auto-advance to the Solution tab.
    //
    // CHANGING THE MANUFACTURING LINE INVALIDATES WHAT WAS CHOSEN ON THE OLD ONE. A solution is one way of
    // making this section with ONE manufacturing line, and the price is what THAT solution costs -- so once
    // the line changes, both describe a plan the user is no longer making. Left in place they came back on the
    // next refresh and dragged the pick with them (the priced plan is restored over the address bar), which is
    // how a freshly picked line could turn back into the old one.
    const pickThisPlan = () => {
      // Clicking the SELECTED line unselects the section. Its highlight tracks the plan the chosen solution is
      // priced on (isSelectedLine, via pricedListIdentifier), not just the pick -- so clearing the pick alone
      // left the card still reading "Selected". Discarding that solution through the shared reset is what
      // actually clears it (a no-op when nothing is chosen, so unpicking a line before any solution was chosen
      // just returns to the listing). The parent hears the pick go to null through the effect above and closes
      // the Solution stage.
      if (isSelectedLine) {
        setPickedList(null);
        discardSelectionForChangedPlan();
        return;
      }
      // Picking a DIFFERENT plan invalidates a solution priced on the old one (see above) -- unless it is that
      // same priced plan being re-picked.
      if (list.identifier !== pricedListIdentifier) { discardSelectionForChangedPlan(); }
      setPickedList(list.identifier);
      if (typeof trackEvent === 'function') {
        trackEvent('quote_builder_manufacturing_line_selected', {
          manufacturing_line_identifier: list.identifier,
          nav_section_index: navSectionIndex,
        });
      }
    };
    // Fade the un-selected plans once one is selected, so the selected manufacturing line stands out on the
    // page's Manufacturing Line tab.
    const dimmed = stageView === 'line' && Boolean(pickedList || pricedListIdentifier) && !isSelectedLine;

    return (
      <div
        key={list.identifier}
        className={styles.combinations_grid_item_cont}
        data-manufacturing-line-list={list.identifier}
        // The card a Manufacturing Line tab scrolls to when it opens -- the same marker the solution cards
        // carry (LayoutNavSectionCombinationListList), so #scrollToSelectedCard has one selector for both.
        // Only on the LISTING: the priced view draws this card too, alone and read-only, and there is nothing
        // to scroll to when the page holds a single card.
        data-selected-card={stageView === 'line' && isSelectedLine && !alone ? true : undefined}
        // Picking a plan asks "how would THIS plan make the section?" -- the solve, which the listing
        // deliberately does not do. Clicking the picked one again goes back to the listing.
        role={readOnly ? undefined : 'button'}
        tabIndex={readOnly ? undefined : 0}
        onClick={readOnly ? undefined : pickThisPlan}
        onKeyDown={readOnly ? undefined : (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            pickThisPlan();
          }
        }}
        style={{
          display: 'flex', flexDirection: 'column', boxSizing: 'border-box',
          flex: alone ? '0 0 auto' : `0 0 ${inWizard ? WIZARD_CARD_BASIS : PAGE_CARD_BASIS}`,
          maxWidth: alone ? '360px' : (inWizard ? WIZARD_CARD_BASIS : PAGE_CARD_BASIS),
          margin: '8px', padding: '12px 8px', cursor: readOnly ? 'default' : 'pointer',
          border: highlighted ? '2px solid var(--brand, #eb5424)' : '1px solid #c4ccd6',
          borderRadius: '6px',
          opacity: dimmed ? 0.8 : 1, transition: 'opacity 120ms ease'
        }}
      >
        {/* THE SCORE SLOT, always the same height so a card never jumps when its number lands. Each row fills
            in as soon as what it needs is known: the VALUE the moment this plan's own lines have answered, the
            STARS only once every plan on screen has, because a rating is a ratio to the cheapest of them. A plan that
            answered "cannot be made" shows an em dash -- it is answered, not still loading.
            A plan nobody could get a score for shows NEITHER: not a number it does not have, and not the em dash,
            which is a finding about the plan rather than about the asking. It says so and offers to ask again.
            DROPPED on the read-only priced card when it has no score: after a refresh that card is drawn from the
            plan recovered by pricedPlanData, which the best-score pass (it runs over the listing, and the listing
            no longer holds the plan) never scores -- so the slot would sit as a permanent skeleton. The card is
            about WHICH plan was priced, and the machines below carry that, so it simply drops the score row. */}
        {(!readOnly || scored) && (
        <div
          data-best-score={listBest == null ? '' : listBest}
          // WHICH OF THE THREE the slot is showing, so a reader (and a test) can tell an absent score from a
          // plan that cannot be manufactured without inferring it from a dash.
          data-score-state={scored ? 'scored' : (gaveUp ? 'unscored' : 'asking')}
          style={{ textAlign: 'center', marginBottom: '6px' }}
        >
          <div style={{
            marginBottom: '2px', minHeight: '16px',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
          >
            {/* No skeleton once the asking is over: a placeholder for stars that are never coming is the same
                lie the em dash was. */}
            {!gaveUp && (bestScoresReady
              ? (rating != null ? <Stars rating={rating} /> : null)
              : <div className={`${skeletonStyles.skeleton} ${skeletonStyles.scoreSkeletonStars}`} />)}
          </div>
          {/* The em dash ("cannot be made") is a customer-facing answer and always shows. The raw numeric score
              is an internal diagnostic -- customers read the star rating above it -- so it is gated on the
              show_score_details UserParam (window.isAdminShowDebug), matching the per-solution-card score readout
              in LayoutNavSectionCombination. */}
          {scored && (listBest == null || window.isAdminShowDebug) && (
            <div style={{ fontSize: '13px', fontWeight: 'bold', color: listBest == null ? '#c00' : '#2e7d32' }}>
              {listBest == null ? '—' : `Skor: ${listBest.toLocaleString()}`}
            </div>
          )}
          {gaveUp && (
            <div data-score-unavailable style={{ fontSize: '12px', color: 'var(--danger, #c00)' }}>
              {/* The reason is on the title rather than in the card: it is a server message, as long as whatever
                  broke, and forty cards' worth of it would bury the listing. The section's own notice above
                  carries it in full. */}
              <div title={listUnscoredReason(list) || undefined}>{t('quote_builder.frank_score_unavailable')}</div>
              <button
                type="button"
                className="dct-button dct-button--ghost dct-button--small"
                style={{ marginTop: '4px' }}
                // The card itself is a button (picking the plan), and a click inside it would pick or unpick the
                // plan on the way past -- see the Select button, which needs this for the same reason.
                onClick={(event) => { event.stopPropagation(); retryUnscoredScores(); }}
              >
                {t('quote_builder.frank_try_again')}
              </button>
            </div>
          )}
          {/* The value skeleton stands in for the number that is loading, so show it only for the viewer who will
              actually see that number (window.isAdminShowDebug). For a customer the number never comes, and a
              placeholder for something that is never coming is the same lie the em dash comment warns about above.
              The em dash ("cannot be made") is a terminal answer, not a loading value, so it needs no skeleton. */}
          {!scored && !gaveUp && window.isAdminShowDebug && (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div className={`${skeletonStyles.skeleton} ${skeletonStyles.scoreSkeletonValue}`} />
            </div>
          )}
        </div>
        )}
        {drawnManufacturingLines.map(({ manufacturingLine, index }) => (
          <ManufacturingLineCard
            key={`${manufacturingLine.identifier}-${index}`}
            manufacturingLine={manufacturingLine}
            maxMachineDimensionMm={maxMachineDimensionMm}
            partLabelsByMachineId={machinePartLabels}
            bare
            hidePricedSolutionCount
            hidePrintingColumn={columnVisibility[index].hidePrintingColumn}
            hideDieCuttingColumn={columnVisibility[index].hideDieCuttingColumn}
            hideCombinedColumn={columnVisibility[index].hideCombinedColumn}
            widthStyle={{ width: '100%', maxWidth: 'none', margin: 0 }}
          />
        ))}
        {/* An explicit "Select" button, so it is obvious the whole card is a choice -- the card is clickable, but
            that affordance is easy to miss. It does the same pickThisPlan as clicking the card; stopPropagation
            keeps the card's own onClick from ALSO firing (which would toggle the pick right back). Frank surfaces
            only (they set stageView, so t() resolves); not on the read-only priced card or the picked-plan
            header (both `alone`). */}
        {stageView && !readOnly && !alone && (
          <button
            type="button"
            data-frank-select-manufacturing-line={list.identifier}
            className={`dct-button dct-button--small dct-button--block ${isSelectedLine ? 'dct-button--primary' : 'dct-button--secondary'}`}
            // marginTop:auto, not a fixed gap: the cards in a row are stretched to a common height (see the
            // listing row), so the button is pushed to the bottom of whatever height this card ended up with and
            // every Select in the row lines up.
            style={{ marginTop: 'auto', paddingTop: '10px' }}
            onClick={(event) => { event.stopPropagation(); pickThisPlan(); }}
          >
            {isSelectedLine ? t('quote_builder.frank_selected') : t('quote_builder.frank_select')}
          </button>
        )}
        {/* Once THIS manufacturing line is the selected one, an explicit "Next" button walks the page's section
            tabs on to the Solution stage -- so the user is not left hunting for the numbered stage tab above.
            onGoToNextStage is the page's advance-one-stage callback; the wizard does not pass it (it has its own
            Next in the modal footer), so this button never appears there. stopPropagation keeps the card's own
            onClick from toggling the pick back off. */}
        {stageView === 'line' && isSelectedLine && !readOnly && !alone && onGoToNextStage && (
          <button
            type="button"
            data-frank-next-stage
            className="dct-button dct-button--small dct-button--block dct-button--primary"
            style={{ marginTop: '8px' }}
            onClick={(event) => { event.stopPropagation(); onGoToNextStage(); }}
          >
            {t('quote_builder.frank_next')}
          </button>
        )}
      </div>
    );
  };

  // The control that goes back to the listing. The picked plan itself stays on screen as its own card above the
  // solutions, so this is only the way back.
  const renderGoBack = () => (
    <button
      type="button"
      data-frank-go-back
      onClick={() => setPickedList(null)}
      className="btn simple-btn"
      style={{ marginBottom: '12px' }}
    >
      Go Back
    </button>
  );

  // The picked plan's SOLUTION GROUPS -- one CARD per group, each holding one solution per part.
  //
  // A plan makes the WHOLE section, and may need two manufacturing lines to do it ("Kapak on the KBA + BOBST,
  // Kutu on the 70x100 Pedal"). Those are two halves of ONE answer, so one card carries both: the legacy's
  // LayoutNavSectionCombinationListList takes a `list` of solutions and draws them all inside a single grid
  // item, which is exactly what a group is (see #buildSolutionGroups for how they are combined).
  //
  // A plan with NO groups is a real answer, not a blank: its machines can be listed for the section's sheets
  // and still fold nothing both beds can run -- and if ONE part cannot be made, the plan cannot make the
  // section at all. So it says so, rather than showing an empty page that reads as broken.
  const renderSolutions = () => {
    // NOTHING TO SOLVE YET. The Solution tab can be reached (via its numbered stage tab) before a manufacturing
    // line has been picked -- there is no plan to solve, so instead of an empty or "no solutions" grid it prompts
    // the user back to the Manufacturing Line stage. A priced section always has its plan (selectedSolutions is
    // restored on mount), so this is only the truly-empty case. Page tabs only: the wizard does not pass
    // onGoToManufacturingLine (its Next is disabled until a line is picked, so it never lands here).
    if (onGoToManufacturingLine && !pickedList && !hasSelection(selectedSolutions)) {
      return (
        <div data-frank-no-line-selected style={{ textAlign: 'center', padding: '32px 12px' }}>
          <div style={{ fontSize: '14px', color: 'var(--ink-soft, #4e5865)', marginBottom: '16px' }}>
            {t('quote_builder.frank_select_manufacturing_line_prompt')}
          </div>
          <button
            type="button"
            data-frank-go-to-manufacturing-line
            className="dct-button dct-button--small dct-button--primary"
            onClick={onGoToManufacturingLine}
          >
            {t('quote_builder.frank_go_to_manufacturing_line')}
          </button>
        </div>
      );
    }

    // THE SOLVE, in place: the picked plan's card stays on screen above this and the grid area shows the work
    // instead of a full-screen card over the whole page. It draws a fixed few placeholders and an INDETERMINATE
    // bar -- the solve is one request, so neither how many cards are coming nor how far along it is can be
    // known (see SolutionCardsSkeleton).
    if (solutionsLoading) { return <SolutionCardsSkeleton />; }

    // In a stage view (the wizard's Solution stage or the page's Solution tab) a click just MARKS the card --
    // the marked one is outlined, and it is committed later (wizard Done / page Pricing tab). onSolutionPicked
    // lets the page tabs auto-advance (no-op in the wizard, which has its own Next). On the legacy stacked view
    // (no stageView) a click chooses the solution and prices the section straight away (setSelectedSolutions).
    const useMark = stageView === 'solution';
    // Clicking the marked card again UNMARKS it (see markSolution), so what is reported is the card that ends
    // up marked -- null when the click cleared it. The page's Pricing tab opens on the strength of this, and a
    // section with nothing marked has no price to show.
    // A solution names the manufacturing line(s) it is made on, so report both (a plan may make the section on
    // two lines -- see #buildSolutionGroups) alongside the solution's own uuids.
    const trackSolutionSelected = (group) => {
      if (typeof trackEvent !== 'function') { return; }
      trackEvent('quote_builder_solution_selected', {
        solution_uuids: groupUuids(group),
        manufacturing_line_identifiers: group.map((solution) => solution.diecutting_result.manufacturing_line_identifier),
        nav_section_index: navSectionIndex,
      });
    };
    const onCardClick = useMark
      ? (group) => {
        const unmarking = groupUuids(markedSolution) === groupUuids(group);
        markSolution(group);
        if (onSolutionPicked) { onSolutionPicked(unmarking ? null : group); }
        // Clicking the marked card again UNMARKS it, which is not a selection.
        if (!unmarking) { trackSolutionSelected(group); }
      }
      : (group) => {
        trackSolutionSelected(group);
        setSelectedSolutions(group);
      };
    // FALLBACK for a PRICED section with nothing solved to show. Rather than tell the user "no solutions" for a
    // section they clearly priced, show the priced solution itself. Only when nothing new is being marked, so an
    // in-session choice always wins.
    //
    // It stands in for more than one cause, which is why the heading below does not speak for the plan: the
    // plan's solve genuinely produced nothing (a part that cannot be made on its machine), or no plan is picked
    // though the section is priced -- a restored selection whose cards carry no manufacturing line identifier
    // leaves pricedListIdentifier null, so nothing syncs a pick from it (see the ALL-OR-NOTHING note on
    // pricedListIdentifier). Called "Solutions (1) -- how this plan makes the section", as it was, the latter
    // claimed the plan makes the section exactly one way, on the strength of a solve that never happened.
    const showPricedFallback = useMark && solvedGroups.length === 0
      && !hasSelection(markedSolution) && hasSelection(selectedSolutions);
    const groups = showPricedFallback ? [selectedSolutions] : solvedGroups;
    // WHAT THE GRID IS, and only what is actually known: the plan's answers when they were solved for, and
    // otherwise the one thing the fallback really has -- the solution this section is priced on. The count is
    // dropped with the claim; "(1)" was never a fact about the plan there.
    const heading = showPricedFallback
      ? t('quote_builder.frank_priced_solution_heading')
      : t('quote_builder.frank_solutions_heading', { count: groups.length });
    // The SELECTED solution to emphasise on the Solution tab -- `chosenSolution`, the SAME value reported to the
    // wizard, so what the card draws as chosen and what the wizard's Next acts on are one thing. Its card is
    // outlined and the rest are dimmed, so the chosen solution stands out (mirrors the Manufacturing Line tab).
    const markedUuids = useMark ? groupUuids(chosenSolution) : '';
    // i18n on the customer-facing stage views (wizard + page tabs); the legacy stacked view keeps its exact
    // English, which its jest suite asserts on (t() returns the key there).
    const translated = stageView != null;

    // Hold the grid hidden until it has settled (#solutionsSettled / #settleSolutionGridReveal), but ONLY on the
    // Frank SOLUTION STAGE (page tab / wizard step) and ONLY when there is something to rank: the legacy stacked
    // view (no stageView) is unchanged, and a single card -- or none, or the priced fallback -- cannot move, so it
    // shows at once. This mirrors the Manufacturing Line tab, which already holds its listing behind the scoring
    // progress until bestScoresReady (see renderListing).
    const revealSolutionGrid = stageView !== 'solution' || groups.length <= 1 || solutionGridRanked;

    // The grid's marker names the grid AND the plan it is for. It is written as an empty string rather than the
    // bare `pickedList`, because React omits an attribute set to null altogether -- which made the grid
    // unfindable in the one state most worth inspecting: no plan picked, which is when the fallback above is
    // what is on screen. That has already misled two measurements (a browser script and an example in
    // ManufacturingLineListsStageSolve), both reading "no solutions grid" off a grid that was plainly there.
    return (
      // position:relative so the grid, held out of flow while it ranks (below), is measured against this panel's
      // width rather than the page's -- the cards lay out in their real rows even while hidden.
      <div data-frank-solutions={pickedList || ''} style={{ position: 'relative' }}>
        <div style={{ fontSize: '12px', color: '#888', marginBottom: '8px', textAlign: 'center' }}>
          {translated
            ? heading
            : <>Solutions ({groups.length}) — the best solutions for this section</>}
        </div>

        {groups.length === 0 ? (
          <div style={{ textAlign: 'center', fontSize: '12px', color: '#c00', padding: '8px' }}>
            {translated
              ? t('quote_builder.frank_plan_no_solutions')
              : <>This plan produces no solutions: one of its parts cannot be made on the machine it is assigned to.</>}
          </div>
        ) : null}

        {/* THE LEGACY'S OWN SOLUTION VIEWS, reused verbatim -- the same reuse ManufacturingLineCard already
            gets, and the same distinction: legacy COMPONENTS are fine, legacy SERVICES are not
            (frank.contract, THE FRONT DOOR). A solution reads identically on both pages BY CONSTRUCTION rather
            than by a Frank copy that drifts from it.

            It works because Frank's Frank::DieCuttingSolution#to_hash is deliberately the legacy's shape, so
            the props map straight onto it: diecutting_result, nav_section_id, count, and
            material_layout_dimension as layout_dimension. That was the point of keeping the hash identical. */}
        {/* justifyContent overrides the shared .combinations_grid's left alignment for the same reason as the
            listing above -- a lone solution card in the corner reads as a broken page. Set here rather than in
            the shared stylesheet: that class is the legacy page's grid too, and this is a Frank decision. */}
        {/* HELD HIDDEN UNTIL RANKED (why: #solutionsSettled). The skeleton -- the same in-place one the solve
            shows above -- sits in flow meanwhile, and the grid is kept MOUNTED but out of flow and hidden, so its
            cards can still fetch and rank unseen and revealing them shifts nothing. It stays the panel's LAST child
            either way, so the ranking read and the tests find it there. */}
        {!revealSolutionGrid && <SolutionCardsSkeleton />}
        <div
          className={styles.combinations_grid}
          ref={solutionsGridRef}
          style={{
            justifyContent: groups.length < cardsPerRow ? 'center' : 'flex-start',
            ...(revealSolutionGrid
              ? {}
              : { position: 'absolute', top: 0, left: 0, right: 0, visibility: 'hidden' }),
          }}
        >
          {groups.map((group) => (
            <LayoutNavSectionCombinationListList
              key={groupUuids(group)}
              list={group}
              count={group[0].count}
              setSelectedList={onCardClick}
              selected={markedUuids !== '' && groupUuids(group) === markedUuids}
              dimmed={markedUuids !== '' && groupUuids(group) !== markedUuids}
              columnBasis={inWizard ? WIZARD_CARD_BASIS : null}
              // An explicit "Select" button under each solution card, on the Frank surfaces (the wizard's
              // Solution stage + the page's Solution tab), so the card's clickability is obvious.
              selectButton={translated}
              selectLabel={t('quote_builder.frank_select')}
              selectedLabel={t('quote_builder.frank_selected')}
              // Under the SELECTED solution card, a "Next" button walks the page's section tabs on to Pricing
              // (which commits and prices the marked solution). The wizard does not pass onGoToNextStage -- it
              // has its own Next -- so this stays off the wizard's cards.
              nextButton={translated && !inWizard && Boolean(onGoToNextStage)}
              nextLabel={t('quote_builder.frank_next')}
              onNext={onGoToNextStage}
            />
          ))}
        </div>
      </div>
    );
  };

  // THE LISTING: the best ways to manufacture the section. Shown only while nothing is picked. Sorted
  // best-first once every line's score has landed (listing order until then -- the progress overlay covers it).
  const renderListing = () => {
    // THE SEARCH FAILED, AND THAT IS THE ONLY ANSWER THIS SECTION HAS -- so it is what the section shows, with
    // the server's reason for whoever can act on one and a Try again. Said HERE as well as from the no-listing
    // branch above, because a section that already HAS a listing never reaches that one, and what it drew
    // instead was not an answer to anything:
    //   - the manufacturing lines of the search BEFORE the one that failed, which the failed search had already
    //     dropped the scores of (the reset that runs on Search) -- so on the stage surface an unfinished
    //     progress bar that nothing was working on, and on the stacked view a listing of blank-scored cards.
    //     Neither of them ever moves again: the scoring pass follows the LISTING object, and a failed search
    //     brings no new one. Measured over five seconds with no request left in flight, and no way out but
    //     Cancel.
    //   - or, after a Cancel, nothing at all but the empty-listing message below, which reports the filters
    //     matching no manufacturing lines when the truth is that nobody could look.
    // Cleared when the next search starts (the fetch effect), so it only ever speaks for the latest ask.
    if (listingUnavailable) { return renderListingUnavailable(); }

    // FIRST, a material price -- BEFORE the scoring bar below. A manufacturing line is FOUND on standard boards
    // even with no material (finding does not need one), so on the owner's own lines with none for this section
    // the section would otherwise score a line it cannot PRICE -- a slow bar over a dead end. So lead them to add
    // a material before anything else, ahead of the scoring/finding progress AND the found-but-unpriceable
    // listing. Only in a stage view (the wizard / a section tab); the legacy stacked view is unchanged.
    if (stageView && ownMaterialMissing) {
      return (
        <EmptyStateMaterialAdder
          materialKey={listing.material_section_key}
          defaultBobbinEnabled={bobbin ? bobbin.checked : undefined}
          boardThicknessMm={listing.board_thickness_mm}
          corrugatedCardboardFlute={listing.corrugated_cardboard_flute}
        />
      );
    }

    // NEXT, a machine PRICE -- a line WAS found but the machine it uses has no unit price for this section's type
    // (ownUnpricedMachine), so it can be found yet never quoted ("Makine birim fiyat bulunamadi"). Lead the owner to
    // price that exact machine, ahead of the found-but-unquotable listing below, exactly as a missing material does.
    // Only in a stage view (the wizard already prices machines in its own steps; the legacy stacked view is
    // unchanged).
    if (stageView && ownMachinePriceMissing) {
      return (
        <EmptyStateMachinePriceAdder
          machineKey={ownUnpricedMachine.machineKey}
          attributeId={ownUnpricedMachine.attributeId}
          // In the wizard the footer drives the save (one set of buttons); on the section tab the form keeps its own.
          hideButtons={inWizard}
          submitRef={inWizard ? wizardSetupSubmitRef : undefined}
          afterSave={inWizard ? onWizardSetupResolved : undefined}
          onSaveError={inWizard ? onWizardSetupSaveError : undefined}
        />
      );
    }

    // In the WIZARD / a section Manufacturing Line tab, hold the cards until every line has answered and show the
    // scoring progress inline instead: a page of blank-scored cards reads as broken, and the page's full-screen
    // ListingProgressOverlay would sit behind the modal. Only while there ARE lines to score -- with none
    // (e.g. every printing machine unticked) scoringInProgress is false, which would leave the progress bar
    // stuck; instead fall through to the empty listing below. The normal page is unchanged (stageView undefined).
    if (stageView === 'line' && scoringInProgress) {
      return <WizardSearchProgress title={t('setup_wizard.scoring')} onCancel={cancelSearch} />;
    }

    // A SEARCH THAT HAS BEEN ASKED FOR AND NOT YET ANSWERED shows the same bar, for the same reason the very
    // first listing does (see the `!listing` branch above): nothing on screen can speak for a question that is
    // still out. What was on screen before it belongs to the question this search REPLACED -- and after a Cancel
    // there is nothing at all, because cancelSearch empties the listing. So pressing Search over edited filters
    // drew the empty-listing FINDING below ("No manufacturing lines match these filters") for the whole length of
    // the re-list, and only then the progress bar. Nothing had been looked at yet: that is not a finding.
    //
    // No Cancel on this bar: the way out of this half of the search is the button above the listing, which is on
    // screen for exactly this gap (see it in the main render). Its own Cancel would be a second one.
    if (stageView === 'line' && isRefetchingListing) {
      return <WizardSearchProgress title={t('setup_wizard.finding')} />;
    }

    // A CANCELLED SEARCH HAS NO RESULTS TO SHOW, and it did not fail to find any either -- it was withdrawn. So
    // the section shows its filters and nothing else, and says nothing: the empty message below would report a
    // finding this search never made.
    if (searchCancelled) { return null; }

    // Nothing matches the current filters. On the owner's OWN manufacturing lines, that is often because they are
    // MISSING a machine the line needs (a printing AND a die-cutting machine), so fill the empty area with the
    // picker to add the first missing one (the filter island is hidden while they set up, see the main render),
    // leading them to the data instead of a dead-end finding -- see selectEmptyStateAdder for which. Any OTHER
    // empty listing (Standard lines, or own machines that simply do not match these filters) keeps the finding.
    // Guarded on the settings config being
    // seeded, so the picker never renders without its tab.
    if (stageView && manufacturingLineLists.length === 0) {
      // The SAME adder ownSetupAdderShown weighs above, so the island hides exactly when this picker replaces it.
      if (emptyStateMachineAdder && machineTabConfig(emptyStateMachineAdder.machineKey)) {
        return (
          <EmptyStateMachineAdder
            machineKey={emptyStateMachineAdder.machineKey}
            allowedLibraryTypes={emptyStateMachineAdder.allowedTypes}
          />
        );
      }

      // NOTHING OF THE OWNER'S OWN MATCHED -- but the STANDARD account almost certainly makes this section, so
      // rather than leave the owner at a dead end, show the standard manufacturing lines below the finding
      // (fully scored, the same cards the standard listing draws) and let them pick one. Picking flips the whole
      // quote to Standard and opens that line's solutions (onUseStandardLine), which is why the standard toggle
      // must end up selected -- the section is priced against the standard account it just borrowed the line
      // from. On the page tabs and in the guided setup wizard (both pass onUseStandardLine), not the legacy
      // stacked view; and only on the owner's OWN lines: a Standard listing that comes up empty keeps the finding.
      const offerStandardFallback = manufacturingLinesSource() === OWN && Boolean(onUseStandardLine);

      // On the owner's OWN lines, point them at their catalogue right at this dead end: a button for each of THIS
      // section's rows -- the material and each machine row -- mirroring the filter island's third column and opening
      // the SAME managers those filter-row buttons do. Same state-aware label logic too: "+ Add <thing>" where the
      // owner has none of that type yet, or the descriptive "Manage <things>" where they have some (the filter row's
      // terse "Manage" would read as nothing beside these headingless buttons). Re-shown here (not only up in the
      // filters) to give the empty result a direction. Derived from the section's rows, so a carton shows three and a
      // section with the combined print-and-die-cut row (flexo) shows four. Standard example account: none -- it is
      // not the customer's to manage, exactly as the filter-island buttons are hidden there.
      const directionButtonNodes = [];
      if (ownSource) {
        if (ownMaterialKey) {
          directionButtonNodes.push(
            <ManageOwnMaterialsButton
              key="material"
              materialKey={ownMaterialKey}
              hasCompatibleMaterial={compatibleOwnMaterialCount == null ? null : compatibleOwnMaterialCount > 0}
              defaultBobbinEnabled={bobbin ? bobbin.checked : undefined}
              boardThicknessMm={listing.board_thickness_mm}
              corrugatedCardboardFlute={listing.corrugated_cardboard_flute}
              fullManageLabelKey="quote_builder.manage_own_materials"
              primary
            />
          );
        }
        machineTypeFilters.forEach((filter) => {
          const machineRow = SECTION_MACHINE_ROWS[filter.key] || MANAGE_BUTTON_ONLY_MACHINE_ROWS[filter.key];
          if (!machineRow) { return; }
          const machineAllowedTypes = machineRow.allowedTypesFor(filter, listing);
          if (machineAllowedTypes === null) { return; }
          directionButtonNodes.push(
            <ManageOwnMachinesButton
              key={filter.key}
              machineKey={machineRow.machineKey}
              addLabelKey={machineRow.addLabelKey}
              manageLabelKey={machineRow.manageLabelKey}
              fullManageLabelKey={machineRow.fullLabelKey}
              primary
              allowedLibraryTypes={machineAllowedTypes}
            />
          );
        });
      }

      return (
        <div>
          {/* The headline answer of the search, so it reads as one -- a heading, not the muted finding it was. */}
          <div style={{ textAlign: 'center', color: 'var(--ink, #10141a)', fontSize: '20px', fontWeight: 600, lineHeight: 1.35, padding: '48px 20px 4px' }}>
            {t('quote_builder.frank_no_manufacturing_lines')}
          </div>
          {directionButtonNodes.length > 0 && (
            <div
              data-empty-state-direction-buttons
              style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'center', padding: '16px 20px 8px' }}
            >
              {directionButtonNodes}
            </div>
          )}
          {offerStandardFallback && (
            <>
              <hr />
              <StandardManufacturingLinesFallback
                navSectionIndex={navSectionIndex}
                bobbinEnabled={bobbinEnabled}
                disabledFilters={disabledFilters}
                selectedTypes={selectedTypes}
                selectedCartonGsm={selectedCartonGsm}
                areaRatioPercent={areaRatioPercent}
                count={count}
                cardsPerRow={cardsPerRow}
                onSelectStandardLine={onUseStandardLine}
              />
            </>
          )}
        </div>
      );
    }

    // effectiveManufacturingLineLists, not the raw listing: on a reopened priced section whose committed
    // manufacturing line's machines are off the default machine-type filters, that manufacturing line is not in
    // the filtered listing, but its card is recovered into effectiveManufacturingLineLists (via pricedPlanData).
    // Rendering it here is what makes the Manufacturing Line stage SHOW the committed manufacturing line,
    // highlighted (its identifier is the pickedList) -- otherwise a priced section opened this stage on a listing
    // that did not contain its own selected manufacturing line, which read as nothing selected while the Solution
    // and Pricing stages were open.
    const sortedLists = bestScoresReady
      ? [...effectiveManufacturingLineLists].sort((a, b) => {
        const scoreA = listBestScore(a);
        const scoreB = listBestScore(b);
        if (scoreA == null) { return scoreB == null ? 0 : 1; } // unmanufacturable plans sort last
        if (scoreB == null) { return -1; }
        return scoreA - scoreB; // lower score is better -- it is a cost
      })
      : effectiveManufacturingLineLists;

    return (
      // The results BLINK as an asked-for search lands (resultsJustRefreshed), so a search that answers the same
      // as the last one still shows that it answered. See the blink effects above and the animation itself in
      // frank/_style.module.scss.
      <div
        data-listing-results
        data-listing-results-blinking={resultsJustRefreshed ? 'true' : 'false'}
        style={{ opacity: resultsJustRefreshed ? RESULTS_BLINK_OPACITY : 1 }}
      >
        {/* i18n only on the customer-facing WIZARD surface (see the note in renderSolutions); the admin/normal
            page keeps its exact English, which its test suite asserts on. */}
        <div style={{ fontSize: '12px', color: '#888', marginBottom: '6px', textAlign: 'center' }}>
          {stageView
            ? t('quote_builder.frank_manufacturing_line_lists_heading', {
              count: sortedLists.length,
              sorted: bestScoresReady ? t('quote_builder.frank_sorted_by_best_score') : ''
            })
            : (
              <>
                Manufacturing Line Lists ({sortedLists.length}) — the best ways to manufacture this
                section{bestScoresReady ? ', sorted by best score' : ''}. Click one to see its solutions.
              </>
            )}
        </div>
        {/* The listing's progress while its manufacturing lines are scored: the modal, blocking popup over the
            whole page, sweeping until the first line answers and counting percentage after that. It clears
            itself once every line has answered. Gated on !stageView, so this is the LEGACY STACKED view only --
            both stage surfaces (the wizard and the section's page tabs) show WizardSearchProgress above. */}
        {!stageView && <ListingProgressOverlay onCancel={cancelSearch} />}
        {/* WHAT THE SECTION COULD NOT ANSWER, said once, above the listing. The cards each say it too, but a
            reader scanning a wide listing for the cards that are blank should not have to hunt for them: this
            counts them and, for an administrator, carries the server's own reason in full -- which is too long
            for a card and, being our internals, is not for subscribers (see #listUnscoredReason). */}
        {manufacturingLinesThatGaveUp.length > 0 && (
          <div
            data-scores-unavailable={manufacturingLinesThatGaveUp.length}
            style={{
              margin: '0 8px 8px', padding: '8px 12px', borderRadius: '4px',
              border: '1px solid var(--danger, #c00)', color: 'var(--ink, #1f2933)', fontSize: '12px',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', flexWrap: 'wrap'
            }}
          >
            <span data-scores-unavailable-message>
              {t('quote_builder.frank_scores_unavailable', { count: manufacturingLinesThatGaveUp.length })}
            </span>
            {unscoredNoticeReason && (
              <span style={{ color: 'var(--ink-soft, #4e5865)' }}>{unscoredNoticeReason}</span>
            )}
            <button
              type="button"
              className="dct-button dct-button--secondary dct-button--small"
              onClick={retryUnscoredScores}
            >
              {t('quote_builder.frank_try_again')}
            </button>
          </div>
        )}
        {/* EQUAL HEIGHTS ACROSS A ROW. The machine beds inside a card are drawn to scale, so a plan with a taller
            die-cutting bed makes a taller card -- and the Select buttons ended up at three different heights,
            which reads as three unrelated blocks rather than one row of choices. alignItems stretch gives every
            card the row's height and the button (marginTop:auto) drops to the bottom of it.

            CENTRED WHILE THE ROW IS NOT FULL. One or two plans left-aligned sit in a corner with a wide empty
            space beside them, reading as "something failed to load". Only up to the row's capacity: past that
            the cards fill the row anyway, and centring would leave a stray last row floating mid-width. */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: sortedLists.length < cardsPerRow ? 'center' : 'flex-start',
          alignItems: 'stretch'
        }}>
          {sortedLists.map((list) => renderManufacturingLineList(list))}
        </div>
      </div>
    );
  };

  // THE PICKED VIEW: the plan on its own, centred, and its solutions under it -- the same shape the legacy page
  // uses (LayoutNavSectionCombinationList: Go Back, the selected card centred, an <hr />, then the solutions).
  //
  // It REPLACES the listing rather than appending to it. Appending is what the first cut did, and with 237 plan
  // cards above them the solutions landed several screens down: clicking a card looked like nothing happened,
  // even though the solve had run and the cards had rendered. A DOM assertion could not see that -- only opening
  // the page could.
  const renderPickedPlan = () => {
    const picked = manufacturingLineLists.find((list) => list.identifier === pickedList);

    return (
      <>
        {renderGoBack()}
        {picked ? (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            {renderManufacturingLineList(picked, { alone: true })}
          </div>
        ) : null}
        <hr />
        {renderSolutions()}
      </>
    );
  };

  // THE CHOSEN SOLUTION, and the section priced from it. It REPLACES the listing and the solutions, the way the
  // legacy page does: once a solution is chosen the question is no longer which one, and leaving the whole
  // listing above the prices buries them (the same reason the picked plan replaces the listing).
  // THE PAGE'S PRICING TAB. Once a solution is chosen it is the priced view (renderSelectedSolution). Arriving
  // here with a solution only MARKED (from the Solution tab) is what commits it -- the pricing commit effect
  // turns the mark into a selection, which posts select-solution and prices the section -- so this shows a short
  // "pricing" note while that is in flight. With nothing marked or chosen it points the user back to the
  // Solution tab.
  const renderPricing = () => {
    if (hasSelection(selectedSolutions)) {
      // Dimmed while a machine change re-selects the manufacturing line and re-solves it, so the old prices are
      // not read as the new machine's while the re-price is in flight.
      return (
        <div style={{ position: 'relative' }}>
          {renderSelectedSolution()}
          {reselectingForMachineChange && (
            <div
              data-frank-reselecting-manufacturing-line
              style={{
                position: 'absolute', inset: 0, zIndex: 5, background: 'rgba(255, 255, 255, 0.66)',
                display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '48px'
              }}
            >
              <div style={{
                background: 'var(--surface, #ffffff)', border: '1px solid var(--rule, #e3e7ec)',
                borderRadius: 'var(--dct-radius, 8px)', padding: '12px 18px', fontSize: '14px',
                color: 'var(--ink-soft, #4e5865)', boxShadow: '0 6px 24px rgba(16, 24, 40, 0.16)'
              }}>
                {t('quote_builder.frank_reselecting_manufacturing_line')}
              </div>
            </div>
          )}
        </div>
      );
    }
    // A finalised quote is only viewed, so a section it never priced has no solution left to select.
    let message = t('quote_builder.frank_pricing_needs_solution');
    if (window.user_quote_finalised) {
      message = t('quote_builder.frank_pricing_not_priced');
    } else if (hasSelection(markedSolution)) {
      message = t('quote_builder.frank_pricing_in_progress');
    }
    return (
      <div style={{ textAlign: 'center', color: 'var(--ink-soft, #4e5865)', fontSize: '14px', padding: '48px 20px' }}>
        {message}
      </div>
    );
  };

  // THE PLAN CARD, or what is happening instead of it. Drawn from the plan when there is one, and otherwise from
  // the ask that is meant to produce it -- because on a LINKED section this card IS the Manufacturing Line tab,
  // so "no card yet" has to read as the wait or the failure it actually is rather than as an empty tab. A
  // section that never asked (no plan on screen, or a legacy selection that carries no identifier) draws nothing
  // here, exactly as before: pricedPlanPending is false and there is no failure to report.
  //
  // SAID THE SAME WAY FOR A SECTION THAT CHOSE ITS OWN, which reaches this through the refresh path (the
  // default-filtered listing no longer holds its plan -- see the fetch effect). The card is the plan the section
  // is PRICED ON either way, chosen here or inherited, which is what the two sentences say; wording it as the
  // inherited one would be wrong on half the surfaces this renders on.
  const renderPricedPlanCard = () => {
    // The plan the section is priced from -- the very card the viewer picked. Taken from
    // effectiveManufacturingLineLists so it is found both in-session (from the listing) and after a refresh (from
    // pricedPlanData, fetched unfiltered because the default-filtered listing no longer holds it). Still absent
    // for a selection made on the legacy page, which stamps no manufacturing line identifier on its cards; the
    // prices are then about the chosen solution regardless, so the plan card is simply left out.
    const pricedPlan = planOnScreen
      ? effectiveManufacturingLineLists.find((list) => list.identifier === planOnScreen)
      : null;

    if (pricedPlan) { return renderManufacturingLineList(pricedPlan, { alone: true, readOnly: true }); }
    if (pricedPlanPending) {
      return <WizardSearchProgress title={t('quote_builder.frank_priced_manufacturing_line_pending')} />;
    }
    if (!pricedPlanUnavailable) { return null; }

    // Try again is the ask repeated and nothing else -- the same shape the listing's failure has
    // (renderListingUnavailable): the reader's sentence, the server's own reason under it for whoever can act on
    // one, and a button that asks again with everything else untouched.
    return (
      <div
        data-priced-plan-unavailable
        style={{ textAlign: 'center', color: 'var(--ink-soft, #4e5865)', fontSize: '14px', padding: '48px 20px' }}
      >
        <div>{t('quote_builder.frank_priced_manufacturing_line_unavailable')}</div>
        {(window.current_user_admin || window.quote_builder_v2_enabled) && (
          <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--ink-faint, #8a94a3)' }}>
            {pricedPlanUnavailable}
          </div>
        )}
        <button
          type="button"
          data-priced-plan-try-again
          onClick={() => setPricedPlanAskNumber((previous) => previous + 1)}
          className="dct-button dct-button--secondary dct-button--small"
          style={{ marginTop: '16px' }}
        >
          {t('quote_builder.frank_try_again')}
        </button>
      </div>
    );
  };

  // withoutPrices - show WHAT was chosen without the cost sections. Used by a linked section's Manufacturing
  //                 Line and Solution tabs: those tabs answer "what is this made on", and its prices live on
  //                 the Pricing tab, exactly as they do for a section that chose for itself.
  // showOnly       - 'plan' or 'solution', to draw just that card. A linked section's Manufacturing Line tab
  //                 must show the manufacturing LINE, and its Solution tab the SOLUTION -- the same question
  //                 each tab answers everywhere else. Absent draws both, which is what the stacked view and the
  //                 Pricing tab want (the Pricing tab keeps the solution card mounted-but-hidden, because it
  //                 writes the num_total_* totals the cost forms read).
  const renderSelectedSolution = ({ withoutPrices = false, showOnly = null } = {}) => {
    // On the page's PRICING tab the plan and the chosen solution have their own tabs (Manufacturing Line /
    // Solution), so this view shows ONLY the prices: the "choose a different solution" button and the plan +
    // solution cards below are hidden (the solution card stays mounted but display:none, because it writes the
    // num_total_* totals the cost forms read -- removing it would leave those columns at 0). The legacy stacked
    // view (no stageView) keeps them: it is the whole flow in one column.
    const pricingTab = stageView === 'pricing';

    return (
      <>
        {/* Not on a LINKED section: its solution is not its own to swap, and de-selecting here would clear the
            section that leads it. The note above says where to change it. */}
        {!pricingTab && !isLinked && (
        <button
          type="button"
          data-frank-choose-another-solution
          // Through the SHARED reset handler, so the server forgets the selection too. Clearing only the page's
          // state left user_quote_nav_sections.selected_lists and the quote's builder_payload still holding the
          // rejected solution: a refresh reopened the section priced from it, and the Manage tab's totals went on
          // counting it. It also resets the linked sections and reloads when the server says to, which the legacy
          // section's own Back button has always done -- this page had a second button that did none of it.
          onClick={() => resetSelectedListHandler({
            index: navSectionIndex,
            setSelectedList: setSelectedSolutions
          })}
          style={{
            marginBottom: '12px', padding: '6px 14px', fontSize: '13px', cursor: 'pointer',
            border: '1px solid var(--rule, #e3e7ec)', borderRadius: 'var(--dct-radius, 8px)',
            background: 'var(--surface, #ffffff)', color: 'var(--ink-soft, #4e5865)'
          }}
        >
          ← {t('quote_builder.choose_a_different_solution')}
        </button>
        )}

        {/* WHAT THE PRICES ARE ABOUT, kept above them: the plan the section is manufactured on, and the solution
            card that was chosen off it -- the two cards the viewer clicked through to get here. Without them the
            priced view is a page of forms with no subject, and the only way to see which plan and which solution
            it was pricing was to go back, which throws the selection away.

            Both are the cards the rest of the page already draws, not a summary written again here: the plan
            through renderManufacturingLineList (the listing's own card), the solution through the legacy
            LayoutNavSectionCombinationListList (the same card the solutions grid shows). So they read identically
            to the ones that were clicked.

            READ-ONLY, both of them: choosing again is the button above, which de-selects on the server too. */}
        <div
          data-frank-priced-from
          style={{
            // Hidden on the Pricing tab (its plan + solution live on the other two tabs) but kept mounted, so
            // the solution card below still writes the num_total_* totals the cost forms read.
            display: pricingTab ? 'none' : 'flex', flexWrap: 'wrap', justifyContent: 'center',
            alignItems: 'flex-start', marginBottom: '12px'
          }}
        >
          {showOnly !== 'solution' && renderPricedPlanCard()}
          {showOnly === 'plan' ? null : (
          <LayoutNavSectionCombinationListList
            // THE SELECTION ITSELF, deliberately not a copy. The shared card writes num_total_diecutting /
            // num_total_printing / num_of_drawings_in_set back into whatever `list` it is handed once its score
            // lands, and the selection is what carries those onward -- a copy would leave the page holding a
            // solution the card had already corrected. (The copy count they feed is no longer DRAWN under the
            // pictures: FrankSolutionDimensions dropped it, since the copies are visible in the picture itself.)
            //
            // What made that mutation dangerous was never the mutation -- it was that a stale selection could
            // be visible under the WRONG section index for one render, which turned the drift into a post
            // against another section. The selection is tagged with its section now, so it cannot be.
            list={selectedSolutions}
            count={selectedSolutions[0].count}
            // A no-op: the card is here to say WHICH solution is priced, not to choose one. The shared card
            // always wires its container to setSelectedList, so this is where the click is dropped.
            setSelectedList={() => {}}
          />
          )}
        </div>

        {withoutPrices ? null : (
          <SelectedSolutionSections
            config={config}
            navSectionIndex={navSectionIndex}
            selectedSolutions={selectedSolutions}
            setSelectedSolutions={setSelectedSolutions}
            hideBackButton={pricingTab}
            // The Pricing tab hides the cards above, so the board each form prices is named under that form's
            // heading instead. The stacked view keeps the cards, which name it already.
            showMaterialSheetLabel={pricingTab}
          />
        )}
      </>
    );
  };

  return (
    <ProgressProvider queue={sectionAnswerQueue}>
      <div ref={sectionRootRef} style={{ marginTop: '10px', marginBottom: '10px', position: 'relative' }}>

      {/* A machine the finder never paired with the other current one has no valid manufacturing line to switch to,
          so the swap is refused with this notice (an error is a popup, never left inline). The picker keeps showing
          the machine until a reload re-seeds the line's own; changing the machine on the Manufacturing Line tab is
          the way through. */}
      {machineChangeUnavailable && (
        <div
          data-frank-machine-change-unavailable
          role="dialog"
          aria-modal="true"
          onClick={() => setMachineChangeUnavailable(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 100000, background: 'rgba(16, 24, 40, 0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4vh 2vw'
          }}
        >
          <div
            onClick={(clickEvent) => clickEvent.stopPropagation()}
            style={{
              background: 'var(--surface, #ffffff)', borderRadius: '10px', maxWidth: '440px',
              boxShadow: '0 12px 48px rgba(16, 24, 40, 0.28)', padding: '20px 24px'
            }}
          >
            <div style={{ fontSize: '16px', fontWeight: 600, color: 'var(--ink, #10141a)', marginBottom: '8px' }}>
              {t('quote_builder.frank_machine_not_available_title')}
            </div>
            <div style={{ fontSize: '14px', color: 'var(--ink-soft, #4e5865)', marginBottom: '16px' }}>
              {t('quote_builder.frank_machine_not_available_body')}
            </div>
            <button
              type="button"
              onClick={() => setMachineChangeUnavailable(false)}
              className="dct-button dct-button--secondary dct-button--small"
            >
              {t('quote_builder.frank_machine_not_available_dismiss')}
            </button>
          </div>
        </div>
      )}

      {/* Asked ONCE for the whole section, above the sub_sections they all narrow. Hidden once a plan is picked
          or a solution chosen: the filters narrow the LISTING, and the listing is not what is on screen then.
          While a change re-lists, the whole block is dimmed and its clicks blocked (pointerEvents), on top of
          each input being disabled -- so no filter can be touched until the results are back. */}
      {/* The machine filters are not drawn for a linked section: they narrow a listing it does not have, and
          offering them is what made its old empty state read as "you have filtered everything out". Why there
          is nothing to choose is said by the marker on the Manufacturing Line tab. */}
      {/* NOR while the owner is still SETTING UP their own lines (in a stage view -- the wizard or a section tab):
          a material or a machine still missing means renderListing shows an empty-state adder (add the material,
          then the printing / die-cutting machine, price and all), and over THAT the island is only wasted space --
          its Search is gated off, its machine rows do not apply to picking a material, and its add buttons repeat
          the adder. It ALSO hides when a line was found but the machine it uses is unpriced (ownMachinePriceMissing):
          renderListing shows the price adder over the found-but-unquotable listing, so the island is wasted there
          too. It comes back the moment setup is complete. A post-search "no lines match" with everything priced is
          NOT this -- ownSetupAdderShown is false there (a quotable line was found), so the island stays for changing
          filters and re-searching. The legacy stacked view (no stageView) shows no adder, so it is unaffected. */}
      {!isLinked && !(stageView && ownSetupAdderShown)
        && (stageView === 'line' || (!stageView && !pickedList && !hasSelection(selectedSolutions))) ? (
        <div
          data-section-machine-filters
          aria-busy={filtersLocked}
          style={{
            // While a prerequisite is missing the island is NOT dimmed/locked, even if a (futile) score is still
            // in flight: the checkboxes + Search are already disabled on their own, and the add/manage buttons
            // must stay live so the owner can add the material/machine that is missing. In a stage view the island
            // is not even rendered then (hidden above), so this ownPrerequisitesMissing case is reached only by
            // the legacy stacked view -- where the island IS still shown while a prerequisite is missing.
            marginBottom: '12px', opacity: (filtersLocked && !ownPrerequisitesMissing) ? 0.55 : 1,
            pointerEvents: (filtersLocked && !ownPrerequisitesMissing) ? 'none' : 'auto', transition: 'opacity 120ms ease'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            {renderSectionFilters()}
          </div>
          {debugPopupVisible && <DebugPanel title="Filters">{renderFilterToggles()}</DebugPanel>}
          {debugPopupVisible && <DebugPanel title="Area ratio">{renderAreaRatioControl()}</DebugPanel>}
        </div>
      ) : null}

      {/* THE WAY OUT WHILE THE LISTING HALF IS IN FLIGHT. A progress surface carries a Cancel of its own only
          once the SCORING starts -- the bar drawn over the listing half has none -- so a find that takes its time
          (a cold section, a slow server) left the filters locked with nothing to press, which is the trap the
          lock was designed not to be. Shown only in that gap: once the scoring bar appears, its own Cancel takes
          over and there is never a second one. Outside the block above, whose pointer events are off while it is
          locked. */}
      {/* Not on the Pricing stage, which now renders the priced view while its (unused) listing loads: a Cancel of
          a search that stage does not show would be a button that stops nothing. */}
      {filtersLocked && !scoringInProgress && stageView !== 'pricing' && (
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
          <button
            type="button"
            data-cancel-search
            onClick={cancelSearch}
            className="dct-button dct-button--secondary dct-button--small"
          >
            {t('quote_builder.cancel')}
          </button>
        </div>
      )}

      {(() => {
        // A LINKED SECTION NEVER LISTS AND NEVER SOLVES -- those two are the section that leads it. Everything
        // else is unchanged: its Pricing tab is its own, and its priced view (the inherited plan, the inherited
        // solution, and THIS section's costs under them) renders exactly as any other section's.
        if (isLinked) {
          if (!hasSelection(selectedSolutions)) { return renderInheritedPending(); }
          // EACH TAB ANSWERS ITS OWN QUESTION, as it does for every other section: the Manufacturing Line tab
          // shows the manufacturing LINE it inherited, the Solution tab the SOLUTION, and Pricing the costs.
          // Showing the solution card under "Manufacturing Line" answers the wrong question with the wrong
          // card; showing the cost sections under all three leaves the tabs meaning nothing here.
          if (stageView === 'pricing') { return renderPricing(); }
          if (stageView === 'line') { return renderSelectedSolution({ withoutPrices: true, showOnly: 'plan' }); }
          if (stageView === 'solution') {
            return renderSelectedSolution({ withoutPrices: true, showOnly: 'solution' });
          }
          // The stacked view (no tabs) is the whole flow in one column, for a linked section as for any other.
          return renderSelectedSolution();
        }

        // A stage view (wizard stage or a page section tab) renders ONE sub-view; the wizard / the page tabs
        // drive the progression between them.
        if (stageView === 'line') { return renderListing(); }
        if (stageView === 'solution') { return renderSolutions(); }
        if (stageView === 'pricing') { return renderPricing(); }

        // The legacy stacked view: the whole flow in one column.
        if (hasSelection(selectedSolutions)) { return renderSelectedSolution(); }

        return pickedList ? renderPickedPlan() : renderListing();
      })()}

      {/* The sub_sections themselves are NOT listed: each is the working behind the lists, not an answer --
          "Kapak" on its own makes no shirt box, it makes a cover. Every one of its manufacturing lines already
          appears above, inside the lists that use it. */}
      </div>
    </ProgressProvider>
  );
};

export default ManufacturingLineLists;
